import { useEffect, useState, useRef, Fragment } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { styles, colors, StatusPill, fmtMoney, fmtDate, fmtDateTime, yearOptions, currentYear } from '../components/UI';
import ErrorBoundary from '../components/ErrorBoundary';
import UploadStatusBadge from '../components/UploadStatusBadge';
import DropZone from '../components/DropZone';
import { useTableControls, SortableFilterTh, ActiveFiltersBar } from '../components/TableControls';
import { useRollup, RollupH2 } from '../components/Rollup';
import EquipmentGantt from '../components/EquipmentGantt';
import JobHub from '../components/JobHub';
import { JobNumberCell } from '../components/InlineTextCell';
import RmInvoicesModal, { RmActualCell, rmFlagsOf } from '../components/RmInvoicesModal';
import DataFreshness from '../components/DataFreshness';
import { EmployeeDocsModal } from '../components/BadgingFiller';
import { EmployeeBadgesModal, BadgeBulkImport } from '../components/EmployeeBadges';
import CrewEditorCard, { buildCrewSlots } from '../components/CrewEditor';
import QuickCrewAdd from '../components/QuickCrewAdd';
import OpsSalesView from '../components/OpsSalesView';
import ApplicantsTab from '../components/ApplicantsTab';
import DisaRandomsTab from '../components/DisaRandomsTab';
import DisaAnnualTab from '../components/DisaAnnualTab';
import DisaRosterChangesTab from '../components/DisaRosterChangesTab';
import { EmployeeRecordModal } from '../components/EmployeeRecordModal';
import { useJobCompliance, ComplianceChip, ComplianceModal } from '../components/CrewCompliance';
import { HascCrewRegister } from '../components/HascRegistration';
import { fileDragProps, dlUrl } from '../components/fileDrag';
import { computeContextualVerifications } from '../utils/allocationDefaults';
import {
  statusLabel, openItems, isIsnGoverned, rowActions, actionsText, blockerCounts, hasBlocker, isOneItemAway,
  statusSortKey, fmtIsoDate, jobOptionLabel, readSiteAccessParams, writeSiteAccessParams, saParamsKey,
  tabSwitchParams, siteAccessCsvRows, toCsv, quickMarkLabel, quickMarkCheck, quickMarkable, liveQuickMarks,
} from '../utils/siteAccessView';
import { isoToUs, addDaysIso, crewPlanTotals, activeJobsColumns, ACTIVE_JOBS_COL_LABELS } from '../utils/activeJobsView';
import BlurDateInput from '../components/BlurDateInput';
import { useAuth, isAdminLike } from '../context/AuthContext';

// Operations page restructured 2026-05-15 from a single feed into a
// tabbed workforce management module. Replaces the SSS Project Startup
// Master spreadsheet. Sub-tabs:
//   Active Jobs    -- existing Won + Active project feed
//   Roster         -- employee master (separate from CRM users)
//   Allocations    -- one row per employee per job (replaces INPUT sheet)
//   DISA           -- per-employee DISA standing (drug + background expiry)
//   Training       -- per-employee certifications with expiry tracking
//   Crafts         -- 14-craft lookup, admin-editable

export default function Operations() {
  // Sub-tab lives in the URL (?tab=...) so a hard refresh, bookmark, or
  // shared link lands on the same tab the user was on. Falls back to the
  // first tab when the param is missing or unrecognized.
  const [searchParams, setSearchParams] = useSearchParams();
  // Requests & Estimates viewer is ops + admin/leadership only — the server
  // gate (/api/ops-sales) deliberately excludes supervisor, whose sales
  // visibility is scoped to their own reports. Hide the tab to match.
  const { user } = useAuth();
  const canSales = user && (user.role === 'ops' || isAdminLike(user));
  // Applicants (2026-10-31) is admin/leadership only, server-enforced by
  // /api/applicants: applicant contact details and resumes are need-to-know.
  // DISA Randoms (2026-11-01) uses the same wall (/api/disa-randoms): who was
  // picked for a random test is confidential until the supervisor sends them.
  const canApplicants = user && isAdminLike(user);
  const VALID = ['active', 'jobnums', ...(canSales ? ['sales'] : []), 'gantt', 'roster', 'alloc', 'disa', ...(canApplicants ? ['randoms', 'disaannual', 'disaroster'] : []), 'isn', 'train', 'siteaccess', 'crafts', ...(canApplicants ? ['applicants'] : [])];
  const tab = VALID.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'active';
  // Site Access keeps customer / site / job / through in the URL; a switch
  // into or out of it drops them (Allocations reads ?job= as its own).
  const setTab = (v) => setSearchParams(prev => tabSwitchParams(prev, tab, v));
  return (
    <>
      <h1 style={styles.h1}>Operations</h1>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { v: 'active', label: '🛠 Active Jobs' },
          { v: 'jobnums', label: '🔢 Job #s' },
          ...(canSales ? [{ v: 'sales', label: '🧾 Requests & Estimates' }] : []),
          { v: 'gantt',  label: '📊 Equipment Gantt' },
          { v: 'roster', label: '👥 Roster' },
          { v: 'alloc',  label: '📋 Allocations' },
          { v: 'disa',   label: '🛡 DISA' },
          ...(canApplicants ? [
            { v: 'randoms', label: '🎲 DISA Randoms' },
            { v: 'disaannual', label: '📅 DISA Annual' },
            { v: 'disaroster', label: '🔁 DISA Roster Changes' },
          ] : []),
          { v: 'isn',    label: '🌐 ISNetworld' },
          { v: 'train',  label: '🎓 Training' },
          { v: 'siteaccess', label: '🔑 Site Access' },
          { v: 'crafts', label: '🔧 Crafts' },
          ...(canApplicants ? [{ v: 'applicants', label: '🧑‍💼 Applicants' }] : [])
        ].map(t => (
          <button
            key={t.v}
            style={tab === t.v ? styles.tabBtnActive : styles.tabBtn}
            onClick={() => setTab(t.v)}
          >{t.label}</button>
        ))}
      </div>
      {/* Page-level, above every tab: each tab reads one or more of these
          reports, and a total that disagrees with another app is usually a
          different pull of the same report (George 2026-09-14). */}
      <ErrorBoundary>
        <DataFreshness
          load={api.opsDataFeeds}
          rollupId="ops-data-freshness"
          subtitle="every report Operations pulls: when it last came in, and when it runs next"
          footer={<>
            sss-inspection and Field Services read the same RentalMan, ISN and DISA reports on their own schedules, so a total there can differ from this page until both have loaded the same pull.
            Badges, quick-marked training and badging documents are entered by hand and carry no schedule.
          </>}
        />
      </ErrorBoundary>
      <ErrorBoundary key={tab}>
        {tab === 'active' && <ActiveJobsTab />}
        {tab === 'jobnums' && <JobNumbersTab />}
        {tab === 'sales' && canSales && <OpsSalesView />}
        {tab === 'gantt'  && <EquipmentGantt />}
        {tab === 'roster' && <RosterTab />}
        {tab === 'alloc'  && <AllocationsTab />}
        {tab === 'disa'   && <DisaTab />}
        {tab === 'randoms' && canApplicants && <DisaRandomsTab />}
        {tab === 'disaannual' && canApplicants && <DisaAnnualTab />}
        {tab === 'disaroster' && canApplicants && <DisaRosterChangesTab />}
        {tab === 'isn'    && <IsnTab />}
        {tab === 'train'  && <TrainingTab />}
        {tab === 'siteaccess' && <SiteAccessTab />}
        {tab === 'crafts' && <CraftsTab />}
        {tab === 'applicants' && canApplicants && <ApplicantsTab />}
      </ErrorBoundary>
    </>
  );
}

// ===== Active Jobs (existing operations feed) ============================

// In-the-Hunt win-% bands (EXCLUSIVE ranges, NOT cumulative). Selecting the
// 50-74 band shows only 50-74, never 75+. Top band is 75+ so any >100 data
// anomaly still lands somewhere; bottom band catches 0-25 incl. unset (0).
const HUNT_BANDS = [
  { key: 'all',    label: 'All bids' },
  { key: '75-100', label: '75-100% win' },
  { key: '50-74',  label: '50-74% win' },
  { key: '26-49',  label: '26-49% win' },
  { key: '0-25',   label: '0-25% win' },
];
const HUNT_BAND_LABEL = Object.fromEntries(HUNT_BANDS.map(b => [b.key, b.label]));
const winInBand = (win, band) => {
  const w = win ?? 0;
  switch (band) {
    case '75-100': return w >= 75;
    case '50-74':  return w >= 50 && w <= 74;
    case '26-49':  return w >= 26 && w <= 49;
    case '0-25':   return w <= 25;
    default:       return true;  // 'all'
  }
};


// Mobilization Standby (George 2026-09-30): pre-award, but ops staffs it now
// so we can mobilize the moment it is won. It has no job # until the award.
const isStandby = (status) => status === 'Mobilization Standby';
// Allocations lists these even before Ops assigns a job # (George 2026-09-30:
// "show Won/Active without job # in Allocations"). Complete/Lost/Closed still
// need one, or the archive would fill with every lost bid.
const STAFFABLE_WITHOUT_JOB_NO = new Set(['Won', 'Active', 'Mobilization Standby']);
const listedInAllocations = (p) => !!p.job_number || STAFFABLE_WITHOUT_JOB_NO.has(p.status);

function ActiveJobsTab() {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [year, setYear] = useState(String(currentYear()));
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);
  // In-the-Hunt win-% band (client-side filter). See HUNT_BANDS above.
  const [huntBand, setHuntBand] = useState('75-100');

  const reload = () => {
    const params = {};
    if (branchId) params.branch_id = branchId;
    if (year) params.year = year;
    api.opsFeed(params).then(setRows).catch(e => setError(e.message));
  };
  useEffect(() => { reload(); }, [branchId, year]);
  useEffect(() => { api.listBranches().then(setBranches).catch(() => {}); }, []);

  const onStatusChange = async (id, status) => {
    setSavingId(id);
    setError('');
    try { await api.opsUpdateStatus(id, status); reload(); }
    catch (err) { setError(err.message); }
    finally { setSavingId(null); }
  };

  // Inline-edit the job number from the Active Jobs row. Sales leaves this
  // blank; Ops fills it in once a deal moves to Won/Active. Errors (most
  // likely a 409 from the unique partial index) bubble into the page error
  // banner; the row's own value is reverted by the reload that follows
  // success or by leaving it untouched on failure.
  const onJobNumberSave = async (id, jobNumber) => {
    setSavingId(id);
    setError('');
    try {
      const r = await api.opsUpdateJobNumber(id, jobNumber);
      reload();
      // One live job per leading 3 digits (George 2026-08-11). The save has
      // already landed -- the cell is free text on purpose -- so this is a
      // heads-up, not a rejection. The Job #s tab keeps flagging it.
      if (r && r.slot_warning) window.alert(`⚠ ${r.slot_warning}`);
    } catch (err) {
      setError(err.message);
      throw err; // let the cell know the save failed so it can revert
    } finally { setSavingId(null); }
  };

  // Mint the next TA pool number onto a blank Job # cell (George 2026-08-07:
  // "a next job number and it auto picks it"). Same write path as typing one
  // -- cascade + Field Services mirror. The full pool register, and the
  // renumber-a-collision path, live on the Job #s tab.
  const onGenerateJobNumber = async (id) => {
    setSavingId(id);
    setError('');
    try {
      const r = await api.opsGenerateJobNumber(id, false);
      reload();
      if (r.slot_warning) window.alert(`⚠ ${r.slot_warning}`);
      return r.job_number;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally { setSavingId(null); }
  };

  // Crew date sync (George 2026-07-29): the job's window and each employee's
  // own start/end are separate fields, so moving the job here has to ask who
  // follows it. Best-effort — a planning hiccup must never undo the date save
  // that already succeeded.
  const [crewSync, setCrewSync] = useState(null);
  const offerCrewSync = async (project, kind, deltaDays, changeLabel) => {
    try {
      const plan = await api.opsPlanCrewDates(project.id, {
        mode: 'shift', kind, delta_days: deltaDays, include_travel: false,
      });
      if (plan.rows?.length) {
        setCrewSync({
          projectId: project.id,
          jobLabel: project.job_number || project.site || `Project ${project.id}`,
          kind, deltaDays, changeLabel, plan,
        });
      }
    } catch (err) { console.warn('crew date plan failed:', err.message); }
  };

  // Inline date + duration edits on the feed (George 2026-06-12) — same
  // endpoints the Allocations picker uses; sales-entered dates can't gate ops.
  const onDateSave = async (id, iso) => {
    const before = rows.find(r => r.id === id);
    const oldIso = before?.date_planned ? String(before.date_planned).slice(0, 10) : '';
    setSavingId(id);
    setError('');
    try {
      await api.opsUpdatePlannedDate(id, iso);
      const delta = oldIso ? daysBetweenIso(oldIso, iso) : 0;
      offerCrewSync(before || { id }, 'start', delta, oldIso
        ? `job start moved ${fmtDate(oldIso)} → ${fmtDate(iso)} (${signedDays(delta)} days).`
        : `job start set to ${fmtDate(iso)}.`);
      reload();
    }
    catch (err) { setError(err.message); throw err; }
    finally { setSavingId(null); }
  };
  const onDurationSave = async (id, days) => {
    const before = rows.find(r => r.id === id);
    const startIso = before?.date_planned ? String(before.date_planned).slice(0, 10) : '';
    const oldDur = parseInt(before?.duration_days, 10) || 0;
    setSavingId(id);
    setError('');
    try {
      await api.opsUpdateDuration(id, days);
      // Only the END moved — a duration edit never touches the start date.
      const newDur = parseInt(days, 10) || 0;
      const oldEnd = startIso && oldDur ? addDaysIso(startIso, oldDur) : '';
      const newEnd = startIso && newDur ? addDaysIso(startIso, newDur) : '';
      const delta = (oldDur && newDur) ? newDur - oldDur : 0;
      offerCrewSync(before || { id }, 'end', delta, (oldEnd && newEnd)
        ? `job end moved ${fmtDate(oldEnd)} → ${fmtDate(newEnd)} (${signedDays(delta)} days).`
        : `job duration set to ${newDur || '—'} days.`);
      reload();
    }
    catch (err) { setError(err.message); throw err; }
    finally { setSavingId(null); }
  };
  // PO # often arrives after the win, on the ops side — editable here too.
  const onPoNumberSave = async (id, poNumber) => {
    setSavingId(id);
    setError('');
    try { await api.opsUpdatePoNumber(id, poNumber); reload(); }
    catch (err) { setError(err.message); throw err; }
    finally { setSavingId(null); }
  };

  // Job Hub drill-in (George 2026-07-02) — click the site name on any
  // Won/Active/Complete row for the one-stop job view.
  const [hubId, setHubId] = useState(null);

  // Hidden-from-ops rows (George 2026-07-09): some won work isn't trackable
  // by ops. Hidden rows drop out of every section unless "Show hidden" is on.
  const [showHidden, setShowHidden] = useState(false);
  const hiddenCount = rows.filter(r => r.ops_hidden).length;
  const onHideToggle = async (id, hidden) => {
    setSavingId(id);
    setError('');
    try { await api.opsSetHidden(id, hidden); reload(); }
    catch (err) { setError(err.message); }
    finally { setSavingId(null); }
  };

  // Push/re-push one job to Field Services (2026-08-05). Auto-mirror fires
  // on every ops edit; this button backfills pre-existing rows + retries
  // failures. Success shows in the row stamp after reload — no alert.
  const onPushFS = async (id) => {
    setSavingId(id);
    setError('');
    try {
      const r = await api.opsPushFS(id);
      if (r.action === 'created') {
        alert(`Created in Field Services${r.job_number ? ` as ${r.job_number}` : ''}${r.po_added?.length ? ` with PO ${r.po_added.join(', ')}` : ''}.`);
      }
      reload();
    } catch (err) { setError(err.message); }
    finally { setSavingId(null); }
  };

  // Branch/TA-Div toggle (2026-08-05): a job quoted as a TA but billed at
  // the branch counter when scope shrank. Flipping mirrors to FS, which
  // drops the job out of its revenue forecast + billing digest.
  const onBillingLevel = async (id, branchBilled) => {
    setSavingId(id);
    setError('');
    try { await api.opsSetBillingLevel(id, branchBilled); reload(); }
    catch (err) { setError(err.message); }
    finally { setSavingId(null); }
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const visible = showHidden ? rows : rows.filter(r => !r.ops_hidden);
  const active = visible.filter(r => r.status === 'Active');
  const hunt = visible.filter(r =>
    (r.status === 'In the Hunt' || r.status === 'Bidding') && winInBand(r.win_pct, huntBand)
  );
  // Mobilization Standby (George 2026-09-30): not awarded yet, but ops must be
  // ready to mobilize the moment it is. Shown whatever the win % band.
  const standby = visible.filter(r => r.status === 'Mobilization Standby');
  const huntTitle = `In the Hunt — ${HUNT_BAND_LABEL[huntBand]}`;
  const won = visible.filter(r => r.status === 'Won' && (!r.date_planned || r.date_planned.slice(0, 10) >= todayStr));
  const complete = visible.filter(r => r.status === 'Complete');

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ fontSize: 13, color: '#6B7280' }}>
          Today: <strong>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong>.
          {' '}Active jobs, Mobilization Standby and In-the-Hunt bids always show; year filter applies to Won + Complete (planning view). Past work auto-flips to Complete — find it under <strong>Recently Complete</strong> below to reactivate.
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {hiddenCount > 0 && (
            <label style={{ fontSize: 12, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}
              title="Jobs kicked out of the ops view (not trackable by ops). Check to show them so they can be unhidden.">
              <input type="checkbox" checked={showHidden} onChange={e => setShowHidden(e.target.checked)} />
              Show hidden ({hiddenCount})
            </label>
          )}
          <select style={{ ...styles.input, width: 140 }} value={year} onChange={e => setYear(e.target.value)}>
            <option value="">All years</option>
            {yearOptions().map(y => <option key={y} value={String(y)}>{y}</option>)}
            <option value="undated">No date</option>
          </select>
          <select style={{ ...styles.input, width: 220 }} value={branchId} onChange={e => setBranchId(e.target.value)}>
            <option value="">All branches</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      </div>
      {error && <div style={styles.errorBox}>{error}</div>}
      <ActiveSection title="Active — In Progress" rollupId="active-inprogress" rows={active} emptyMsg="No active projects." onStatusChange={onStatusChange} onJobNumberSave={onJobNumberSave} onGenerateJobNumber={onGenerateJobNumber} onDateSave={onDateSave} onDurationSave={onDurationSave} onPoNumberSave={onPoNumberSave} savingId={savingId} onOpenHub={setHubId} onHideToggle={onHideToggle} onPushFS={onPushFS} onBillingLevel={onBillingLevel} />
      <ActiveSection title="Won — Upcoming" rollupId="active-won" rows={won} emptyMsg="No upcoming work." onStatusChange={onStatusChange} onJobNumberSave={onJobNumberSave} onGenerateJobNumber={onGenerateJobNumber} onDateSave={onDateSave} onDurationSave={onDurationSave} onPoNumberSave={onPoNumberSave} savingId={savingId} onOpenHub={setHubId} onHideToggle={onHideToggle} onPushFS={onPushFS} onBillingLevel={onBillingLevel} />
      <ActiveSection
        title="Recently Complete"
        rollupId="active-complete"
        rows={complete}
        emptyMsg="No completed jobs in this view."
        note="Jobs auto-complete when their end date passes. If one EXTENDED: push the End date out to the new finish first, then set it back to Active — otherwise it auto-completes again on the next load."
        onStatusChange={onStatusChange} onJobNumberSave={onJobNumberSave} onGenerateJobNumber={onGenerateJobNumber} onDateSave={onDateSave} onDurationSave={onDurationSave} onPoNumberSave={onPoNumberSave} savingId={savingId} onOpenHub={setHubId} onHideToggle={onHideToggle} onPushFS={onPushFS} onBillingLevel={onBillingLevel}
      />
      {/* Read-only like the Hunt section: sales owns a pre-award status, and no
          pool job number is burned before the award. */}
      <ActiveSection
        title="Mobilization Standby — Be Ready to Mobilize"
        rollupId="active-standby"
        rows={standby}
        emptyMsg="No jobs on Mobilization Standby."
        note="Not awarded yet, but the award could come at the last minute. Have crew, equipment and logistics ready so we can mobilize immediately if it is won."
        onStatusChange={onStatusChange} onJobNumberSave={onJobNumberSave} onDateSave={onDateSave} onDurationSave={onDurationSave}
        savingId={savingId} onOpenHub={setHubId} readOnly showWinPct
      />
      {hubId && <JobHub projectId={hubId} onClose={() => setHubId(null)} FilesPanel={JobFilesPanel} />}
      {crewSync && (
        <CrewDateSyncModal
          projectId={crewSync.projectId}
          jobLabel={crewSync.jobLabel}
          changeLabel={crewSync.changeLabel}
          kind={crewSync.kind}
          deltaDays={crewSync.deltaDays}
          initialPlan={crewSync.plan}
          onClose={() => setCrewSync(null)}
          onApplied={(n) => { setCrewSync(null); reload(); if (n) setError(''); }}
        />
      )}
      {/* No onGenerateJobNumber here on purpose: In-the-Hunt / Bidding rows
          must not burn a pool number on a deal that may never land. The
          number is assigned when the job goes Won/Active. */}
      <ActiveSection
        title={huntTitle}
        rollupId="active-hunt"
        rows={hunt}
        emptyMsg="No bids at this win % — lower the filter to see more."
        onStatusChange={onStatusChange} onJobNumberSave={onJobNumberSave} onDateSave={onDateSave} onDurationSave={onDurationSave}
        savingId={savingId} readOnly showWinPct
        filterControl={
          <select
            style={{ ...styles.input, width: 150, padding: '6px 8px', fontSize: 12 }}
            value={huntBand}
            onChange={e => setHuntBand(e.target.value)}
            title="Show only bids whose win % falls in this band"
          >
            {HUNT_BANDS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
        }
      />
    </>
  );
}

// ⛑ RESCUE — same pill Estimating puts on a rescue-scoped request, so the two
// screens read identically. The DECISION is server-side (`is_rescue`, from
// utils/rescue.js: request scope flag OR a 900-999 job number OR a 900-999
// branch code) — never re-derive the branch range in the client.
function RescuePill() {
  return (
    <span
      title="Rescue Division work — scope flag on the request, or a 900-999 job number / branch"
      // padding/letter-spacing are MEASURED, not cosmetic: at '1px 6px' plus
      // 0.5 letter-spacing this nowrap pill raised the Site column's minimum
      // by 3px. Tightened, it costs the layout nothing. See the table note.
      style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0, padding: '1px 4px', borderRadius: 10,
        background: '#FEE2E2', color: '#B91C1C', border: '1px solid #FCA5A5',
        whiteSpace: 'nowrap', display: 'inline-block', marginTop: 2 }}>⛑ RESCUE</span>
  );
}

// The crew plan this job needs, rendered INSIDE the Equipment cell rather than
// in a column of its own (George 2026-08-20). That is deliberate: this table's
// rigid width was measured at 1461px across 12 columns on 2026-08-07 and only
// Site and Equipment can shrink, so an extra column re-breaks the layout that
// fix bought unless it pays for its own width. (The 13th column, the
// 2026-09-14 Est. $ / Actual $ split, paid for itself; see the table note.)
// Equipment is one of the two flexible columns and absorbs this for free.
//
// Source is job_craft_requirements vs job_allocations (server: labor_plan),
// the SAME numbers as the Job Hub's staffing plan and the PM email package.
// Totals lead because the question is "what does this job need"; per-craft
// detail wraps under it, clamped, with the full breakdown on hover.
function CrewPlanBlock({ plan, status }) {
  // Totals come from utils/activeJobsView.js, which the Equipment / Labor
  // search reads too, so "need 2" is findable exactly as printed.
  const { rows, needed, assigned, short } = crewPlanTotals(plan);
  if (!rows.length) {
    // Only nag where a plan is actually expected. A Bidding/Hunt row has no
    // committed crew yet and must not read as a gap.
    if (status !== 'Won' && status !== 'Active') return null;
    return (
      <div style={{ marginTop: 6, paddingTop: 5, borderTop: '1px dashed #E5E7EB', fontSize: 11, color: '#92400E', maxWidth: 280 }}
        title="No staffing plan on this job yet. It seeds from the estimate's crew (Estimating → Resync Crew Plan) or can be entered on Operations → Allocations.">
        👷 No crew plan
      </div>
    );
  }
  // short is per craft, not the total — 7 assigned against 5 PM + 2 Tech can
  // still be 2 techs short. Same rule StaffingChip uses on Allocations.
  const tooltip = rows
    .map(r => `${r.craft}: ${r.assigned} of ${r.needed}` + (r.assigned >= r.needed ? '  (✓)' : `  (need ${r.needed - r.assigned})`))
    .join('\n');
  // maxWidth below is LOAD-BEARING (measured 2026-08-20). This table is
  // auto-layout: a cell's max-content width decides how much of the free space
  // its column claims. Uncapped, the crew plan made Equipment demand more and
  // collapsed Site from 115px to 86px at 1600 -- the exact "site names wrap onto
  // 3-4 lines" complaint the 2026-08-07 width fix was for. Capped, Site and
  // Equipment both sit at their baseline widths at every width 1300-1920.
  return (
    <div style={{ marginTop: 6, paddingTop: 5, borderTop: '1px dashed #E5E7EB', maxWidth: 280 }} title={tooltip}>
      {/* Bold coloured TEXT, not a pill, and it wraps. MEASURED, not guessed
          (standalone harness sweep 2026-08-20): as a nowrap pill this one span
          raised the Equipment column's minimum from 84px to 101px and the whole
          table's rigid width by 17px — straight back into the overflow the
          2026-08-07 fix removed. Plain wrapping text costs +6px total, which is
          noise. Do NOT put a background or white-space:nowrap back on this. */}
      <span style={{
        fontSize: 10, fontWeight: 800,
        color: short > 0 ? '#991B1B' : '#065F46',
      }}>
        👷 {assigned}/{needed}{short > 0 ? ` · need ${short}` : ' ✓'}
      </span>
      {/* overflowWrap:anywhere is LOAD-BEARING, not styling. Craft names run long
          ('Turnaround Superintendent (Night)') and an unbreakable one becomes the
          Equipment column's minimum width, which is one of only two columns that
          can shrink. Measured 2026-08-20: with it, this whole feature adds 0px to
          the table's rigid width; without it, 7px. */}
      <div style={{
        marginTop: 3, fontSize: 11, color: '#6B7280', lineHeight: 1.3, overflowWrap: 'anywhere',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
      }}>
        {rows.map((r, i) => (
          <span key={r.craft} style={{ color: (Number(r.assigned) || 0) < (Number(r.needed) || 0) ? '#B91C1C' : '#6B7280' }}>
            {i > 0 && <span style={{ color: '#D1D5DB' }}> · </span>}
            {r.needed}× {r.craft}
          </span>
        ))}
      </div>
    </div>
  );
}

function ActiveSection({ title, rollupId, rows, emptyMsg, onStatusChange, onJobNumberSave, onGenerateJobNumber, onDateSave, onDurationSave, onPoNumberSave, savingId, readOnly, showWinPct, filterControl, note, onOpenHub, onHideToggle, onPushFS, onBillingLevel }) {
  // Rollup: id must be STABLE (huntTitle changes with the win-% band), so the
  // call sites pass an explicit rollupId.
  const [rolled, toggleRolled] = useRollup(rollupId || title);
  // Per-row expandable Customer Files panel. Files come from the linked
  // request / opportunity (project_id) and are drag-out enabled.
  const [openFiles, setOpenFiles] = useState(() => new Set());
  const toggleFiles = (id) => setOpenFiles(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  // Est. $ and Actual $ are two columns since 2026-09-14 (George: "matching
  // columns for Estimated and Actual"), so 13 columns, 12 read-only. Every
  // colSpan below reads this.
  const colCount = readOnly ? 12 : 13;
  // Table-LOCAL compact cells. styles.td/th are shared app-wide and carry
  // 10px 12px; at 12 columns that is ~288px of pure padding, which is a big
  // slice of the rigid width squeezing Site and Equipment. 8px bought ~96px
  // back for the two text columns without touching any other table.
  // 6px (2026-09-14) pays for the 13th column: 13 x 12px = 156px of padding
  // against the old 12 x 16px = 192px. See the width note above the table.
  // The header cells pad 6px too: SortableFilterTh's compact variant.
  const tdC = { ...styles.td, padding: '8px 6px' };
  // The Files cell holds four controls. They inherit btnSecondary's
  // minWidth:70, which pinned this column at 224px no matter how narrow the
  // card got. Freed + allowed to wrap, it flexed 148-211px (8px padding, cell
  // minWidth 132); 132-207px since 2026-09-14 (6px padding, minWidth 120).
  const filesBtn = { minWidth: 0, margin: '1px 2px 1px 0' };
  // Row whose RentalMan invoice list is open. Rendered OUTSIDE the table: a
  // fixed overlay inside a hidden job's row would inherit its 0.55 opacity.
  const [rmFor, setRmFor] = useState(null);
  // A search box under every header (George 2026-09-28: "need to be able to
  // search all headers"), plus click-to-sort. Each section searches its own
  // table. What each column matches, and how it sorts, is
  // utils/activeJobsView.js: the text the cell shows, read through the SAME
  // fmtMoney / rmFlagsOf the cells print with. Files is searchable only where
  // it carries the FS / TA-BR / hide controls; In the Hunt's is just the
  // expander, so there is nothing in it to find.
  const rowActions = !!(onHideToggle || onPushFS || onBillingLevel);
  const cols = activeJobsColumns({ money: fmtMoney, rmFlags: rmFlagsOf, showWinPct, rowActions });
  const { displayRows, headerProps, filters, clearFilter, clearAll } = useTableControls(rows, cols.search, cols.sort);
  const searching = Object.values(filters).some(f => f.value);
  return (
  <div style={styles.card}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: rolled ? 0 : 12 }}>
      <RollupH2 collapsed={rolled} onToggle={toggleRolled} style={{ marginBottom: 0 }}>{title} <span style={{ color: '#6B7280', fontWeight: 400 }}>· {searching ? `${displayRows.length} of ${rows.length}` : rows.length}</span></RollupH2>
      {filterControl || null}
    </div>
    {!rolled && <>
    {note && <div style={{ fontSize: 12, color: '#6B7280', marginTop: -4, marginBottom: 12 }}>{note}</div>}
    <ActiveFiltersBar filters={filters} clearFilter={clearFilter} clearAll={clearAll} colLabels={ACTIVE_JOBS_COL_LABELS} />
    {/* table-layout:fixed + an explicit colgroup (2026-08-07, George). With
        12 auto-sized columns the browser starved Site / Job # / Equipment —
        job numbers rendered truncated ("810-2-26-VAL-SRT]"), site names wrapped
        onto 3-4 lines, and a wide dead gap opened before Change. Fixed layout
        means these widths are honored instead of negotiated. Adjust the numbers
        here, not with per-cell widths. */}
    {/* Cramped columns here are a RIGID-WIDTH problem, measured 2026-08-07 in
        a standalone harness of this exact markup. Only Site and Equipment can
        shrink; every other column is pinned by a fixed-width control (the two
        date inputs, the job-number and PO inputs, the status pill's
        minWidth:70, the status select) plus 24px of cell padding x 12 columns.
        That rigid floor was 1461px, so on a ~1600 card the two text columns
        were left ~102 and ~183 and the table overflowed on anything narrower.

        The fix is to REDUCE the rigid width, not to override it: 8px cell
        padding here (table-local -- styles.td/th are shared app-wide), and the
        Files controls freed of the inherited btnSecondary minWidth:70 so they
        wrap instead of pinning the column at 224px. Measured result across
        1300-1900px: nothing overflows at any width, row height is constant,
        and at 1600 Site/Equipment go 102/183 -> 114/242.

        DO NOT reach for table-layout:fixed + a colgroup. Tried twice the same
        day and reverted both times: a colgroup does not govern the widths of
        the controls inside the cells, so any column narrower than its control
        renders that control ON TOP of the next column, and widths summing past
        the card push the FS / TA-BR chips and the whole Change column off the
        edge entirely.

        2026-09-14: "$ (est/actual)" split into Est. $ and Actual $ (George:
        the Operations tab needs matching Estimated and Actual columns, and
        actual || estimate showed a $1.48M job as its first invoice). The split
        was paid for in the same rigid-width currency, by arithmetic, not yet
        by a harness measurement:
          + the old header's unbreakable "(EST/ACTUAL)" (~82px) is replaced by
            two figure-wide columns (~64px for a $1,480,000 estimate, ~54px
            for a six-figure Actual): about +36px, +46px worst case
          + one more column of padding
          - cell padding 8px -> 6px: 13 x 12 = 156px vs 12 x 16 = 192px, -36px
          - Files cell minWidth 132 -> 120, -12px. The first line of that cell
            (Files button + hide toggle) is ~100-104px, so it still wraps to
            the same two lines.
        Net about -12px, -2px worst case. The Actual notes are capped at 90px
        so they do not raise that column's share of the free space.

        2026-09-28: a search box under every header (George: "search all
        headers"). MEASURED, A/B against the plain-label header in one harness
        of the real page at 1300/1440/1600/1760/1920: every column within 2px,
        Site and Equipment never narrower, same overflow, nothing clipped; the
        header row is 30px taller. That needed SortableFilterTh's compact
        variant (wrapping label, 6px padding, no type dropdown), size={1} on
        the box (its ~20-character natural width took 35px from In the Hunt's
        Site at 1600) and the sort arrow only on the sorted column (an
        always-on one took 3-4px). */}
    <table style={styles.table}>
      <thead>
        <tr>
          <SortableFilterTh compact label="Date" {...headerProps('date')} />
          <SortableFilterTh compact label="Site" {...headerProps('site')} />
          <SortableFilterTh compact label="Job #" {...headerProps('job')} />
          <SortableFilterTh compact label="Duration" {...headerProps('duration')} />
          <SortableFilterTh compact label="Equipment / Labor" {...headerProps('equipment')} />
          <SortableFilterTh compact label="PO #" {...headerProps('po')} />
          <SortableFilterTh compact label="Est. $" {...headerProps('est')} />
          <SortableFilterTh compact label="Actual $" {...headerProps('actual')} />
          <SortableFilterTh compact label="Branch" {...headerProps('branch')} />
          <SortableFilterTh compact label="Acct Mgr" {...headerProps('am')} />
          <SortableFilterTh compact label="Status" {...headerProps('status')} />
          {rowActions
            ? <SortableFilterTh compact label="Files" {...headerProps('files')} />
            : <SortableFilterTh compact label="Files" noSort noFilter />}
          {/* Change is the Status column's value as a dropdown: search Status. */}
          {!readOnly && <SortableFilterTh compact label="Change" noSort noFilter />}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={colCount} style={{ ...tdC, textAlign: 'center', color: '#6B7280' }}>{emptyMsg}</td></tr>
        )}
        {rows.length > 0 && displayRows.length === 0 && (
          <tr><td colSpan={colCount} style={{ ...tdC, textAlign: 'center', color: '#6B7280' }}>No jobs in this section match the search. Clear it above the table to see all {rows.length}.</td></tr>
        )}
        {displayRows.map(p => (
          <Fragment key={p.id}>
          <tr style={p.ops_hidden ? { opacity: 0.55 } : undefined}>
            <td style={tdC}>
              <DateRangeCell
                startValue={p.date_planned}
                durationDays={p.duration_days}
                disabled={savingId === p.id}
                onStartSave={(iso) => onDateSave(p.id, iso)}
                onDurationSave={(days) => onDurationSave(p.id, days)}
              />
            </td>
            <td style={tdC}>
              {onOpenHub ? (
                <button
                  onClick={() => onOpenHub(p.id)}
                  title="Open the Job Hub — request Q&A, site requirements, staffing, equipment, files"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    color: '#E36F2C', fontWeight: 600, fontSize: 'inherit', textAlign: 'left',
                    textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}>
                  {p.site}
                </button>
              ) : (
                <Link to={`/accounts/${encodeURIComponent(p.site)}`} style={{ color: '#E36F2C', fontWeight: 600, textDecoration: 'none' }}>
                  {p.site}
                </Link>
              )}
              {/* Rescue rides the Site cell, not a column of its own — a pill
                  costs no rigid width (see the layout note above the table). */}
              {p.is_rescue && <div><RescuePill /></div>}
            </td>
            <td style={tdC}>
              <JobNumberCell
                value={p.job_number}
                disabled={savingId === p.id}
                onSave={(v) => onJobNumberSave(p.id, v)}
                onGenerate={onGenerateJobNumber ? () => onGenerateJobNumber(p.id) : null}
              />
            </td>
            <td style={tdC}>
              {/* Read-only: auto-calculated from Start -> End (set the End date to change it). */}
              {p.duration_days
                ? <span style={{ fontSize: 12, color: '#374151' }}>{p.duration_days} <span style={{ fontSize: 11, color: '#6B7280' }}>days</span></span>
                : <span style={{ color: '#9CA3AF' }} title="Set Start + End to calculate">—</span>}
            </td>
            {/* Clamped to 3 lines — one long scope note ("Issue trailer, LP
                system, smaller equipment, possible rescue, possbile CSVM")
                was stretching its whole row to ~110px and pushing every other
                job off the screen. Full text on hover and in the Job Hub. */}
            <td style={{ ...tdC, fontSize: 12 }}>
              <div
                title={p.equipment_type || ''}
                style={{
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden', whiteSpace: 'pre-wrap', lineHeight: 1.35,
                }}>
                {p.equipment_type}
              </div>
              <CrewPlanBlock plan={p.labor_plan} status={p.status} />
            </td>
            <td style={tdC}>
              {onPoNumberSave ? (
                <JobNumberCell
                  value={p.po_number}
                  disabled={savingId === p.id}
                  onSave={(v) => onPoNumberSave(p.id, v)}
                  maxLength={100}
                  width={100}
                  subtle
                />
              ) : p.po_number}
            </td>
            <td style={tdC}>
              {fmtMoney(p.estimated_value)}
              {p.has_estimate === false && (
                <div
                  title="No estimate on file in the CRM — this $ figure is the sales forecast on the opportunity, not a priced estimate."
                  style={{ fontSize: 10, color: '#B45309', marginTop: 2, lineHeight: 1.25, maxWidth: 110, overflowWrap: 'anywhere' }}
                >
                  No Estimate / Opportunity Only
                </div>
              )}
              {showWinPct && p.win_pct != null && (
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{p.win_pct}% win</div>
              )}
            </td>
            <td style={tdC}>
              {/* Same cell as the Project Tracker (components/RmInvoicesModal.jsx),
                  so the two screens cannot render an Actual differently. */}
              <RmActualCell project={p} onOpen={setRmFor} />
            </td>
            <td style={tdC}>{p.branch_name || ''}</td>
            <td style={tdC}>{p.account_owner_name || ''}</td>
            <td style={tdC}><StatusPill status={p.status} /></td>
            <td style={{ ...tdC, minWidth: 120 }}>
              <button
                style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 8px', ...filesBtn }}
                onClick={() => toggleFiles(p.id)}
                title="Customer files on this opportunity"
              >
                📎 Files {openFiles.has(p.id) ? '▾' : '▸'}
              </button>
              {onHideToggle && (
                <button
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
                    marginLeft: 4, fontSize: 13, lineHeight: 1, verticalAlign: 'middle',
                    opacity: p.ops_hidden ? 0.9 : 0.35 }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = 1; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = p.ops_hidden ? 0.9 : 0.35; }}
                  disabled={savingId === p.id}
                  onClick={() => onHideToggle(p.id, !p.ops_hidden)}
                  title={p.ops_hidden
                    ? 'Bring this job back into the ops view'
                    : 'Hide from the ops view — not trackable by ops. Recover it via the "Show hidden" checkbox above.'}
                >
                  {p.ops_hidden ? '↩' : '🚫'}
                </button>
              )}
              {onPushFS && (
                <button
                  style={{
                    ...styles.btnSecondary, fontSize: 11, padding: '4px 8px', ...filesBtn,
                    ...(p.fs_push_status === 'ok'
                      ? { color: '#10B981', borderColor: '#10B981' }
                      : p.fs_push_status
                        ? { color: '#EF4444', borderColor: '#EF4444' }
                        : {}),
                  }}
                  disabled={savingId === p.id}
                  onClick={() => {
                    if (p.fs_push_status === 'ok'
                      && !window.confirm(`Re-push ${p.job_number || p.site} to Field Services? Dates, status and new POs will mirror onto the FS job (FS keeps its own name and existing POs).`)) return;
                    onPushFS(p.id);
                  }}
                  title={p.fs_push_status === 'ok'
                    ? `Pushed to Field Services ${fmtDateTime(p.fs_pushed_at)} — click to re-push (mirrors dates/status/POs)`
                    : p.fs_push_status
                      ? `FS push FAILED${p.fs_push_error ? `: ${p.fs_push_error}` : ''} — click to retry`
                      : 'Push this job to Field Services (creates the FS project; auto-generates a job number if blank)'}
                >
                  {p.fs_push_status === 'ok' ? 'FS ✓' : p.fs_push_status ? 'FS ✗' : '→FS'}
                </button>
              )}
              {onBillingLevel && (
                <button
                  style={{
                    ...styles.btnSecondary, fontSize: 11, padding: '4px 8px', ...filesBtn,
                    ...(p.branch_billed
                      ? { color: '#92400E', borderColor: '#F59E0B', background: '#FFFBEB', fontWeight: 700 }
                      : { opacity: 0.65 }),
                  }}
                  disabled={savingId === p.id}
                  onClick={() => {
                    const toBranch = !p.branch_billed;
                    if (!window.confirm(toBranch
                      ? `Mark ${p.job_number || p.site} as BILLED AT THE BRANCH LEVEL? Field Services will exclude it from the revenue forecast and billing digest (mirrors automatically).`
                      : `Return ${p.job_number || p.site} to TA-Div billing? Field Services will count it in the revenue forecast again.`)) return;
                    onBillingLevel(p.id, toBranch);
                  }}
                  title={p.branch_billed
                    ? 'BILLED AT THE BRANCH LEVEL — excluded from the FS revenue forecast. Click to return it to TA-Div.'
                    : 'TA-Div billing (normal). Click if this job\'s scope shrank and it bills at the branch counter instead — FS will exclude it from the revenue forecast.'}
                >
                  {p.branch_billed ? 'BR' : 'TA'}
                </button>
              )}
            </td>
            {!readOnly && (
              <td style={tdC}>
                <select
                  style={{ ...styles.input, width: 92, padding: '4px 6px', fontSize: 11 }}
                  value={p.status}
                  disabled={savingId === p.id}
                  onChange={e => onStatusChange(p.id, e.target.value)}
                >
                  <option value="Won">Won</option>
                  <option value="Active">Active</option>
                  <option value="Complete">Complete</option>
                </select>
              </td>
            )}
          </tr>
          {openFiles.has(p.id) && (
            <tr>
              <td colSpan={colCount} style={{ ...styles.td, background: '#F9FAFB', padding: '10px 14px' }}>
                <JobFilesPanel projectId={p.id} />
              </td>
            </tr>
          )}
          </Fragment>
        ))}
      </tbody>
    </table>
    </>}
    {rmFor && (
      <RmInvoicesModal
        projectId={rmFor.id}
        source="ops"
        title={`RentalMan invoices · ${rmFor.site}${rmFor.job_number ? ` · ${rmFor.job_number}` : ''}`}
        onClose={() => setRmFor(null)}
      />
    )}
  </div>
  );
}

// List of the opportunity's customer files, surfaced inline under a job row
// on the Active Jobs feed. Same documents that show on the linked request and
// the Project Tracker. Each file is drag-out enabled: drag it onto an Outlook
// email or the desktop to attach. Ops can also drag new files IN via the drop
// zone below (upload, not just view). Lazy-loaded when the row expands.
function JobFilesPanel({ projectId }) {
  const { user } = useAuth();
  const inputRef = useRef(null);
  const [files, setFiles] = useState(null);  // null = loading
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(null);  // id being removed

  const reload = () => {
    api.opsListProjectFiles(projectId)
      .then(setFiles)
      .catch(e => { setError(e.message); setFiles([]); });
  };
  useEffect(() => { reload(); }, [projectId]);

  const uploadFiles = async (fileList) => {
    const arr = Array.from(fileList || []).filter(Boolean);
    if (!arr.length) return;
    setUploading(true); setError('');
    try {
      for (const f of arr) await api.opsUploadProjectFile(projectId, f);
      reload();
    } catch (e) { setError(e.message); }
    finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  // Uploader or admin/leadership only -- same rule the server enforces.
  const canRemove = (f) => isAdminLike(user) || (f.uploaded_by && user?.id === f.uploaded_by);

  const removeFile = async (f) => {
    if (!confirm(`Remove "${f.original_name}" from this job?`)) return;
    setRemoving(f.id); setError('');
    try {
      await api.opsDeleteProjectFile(projectId, f.id);
      reload();
    } catch (e) { setError(e.message); }
    finally { setRemoving(null); }
  };

  const dropZone = (
    <div
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => inputRef.current?.click()}
      style={{
        border: `2px dashed ${dragOver ? colors.orange : '#CBD5E1'}`,
        background: dragOver ? '#FFF7ED' : '#F8FAFC',
        borderRadius: 6, padding: '10px 12px', textAlign: 'center', cursor: 'pointer',
        fontSize: 12, color: '#6B7280', marginBottom: 8,
      }}
    >
      ⬆ {uploading ? 'Uploading…' : 'Drop new files here to attach, or click to browse.'}
      <input ref={inputRef} type="file" multiple style={{ display: 'none' }}
        onChange={e => { uploadFiles(e.target.files); e.target.value = ''; }} />
    </div>
  );

  if (files === null) return <span style={{ color: '#6B7280', fontSize: 12 }}>Loading files…</span>;

  return (
    <div>
      {dropZone}
      {error && <div style={{ color: '#991B1B', fontSize: 12, marginBottom: 6 }}>{error}</div>}
      {!files.length ? (
        <span style={{ color: '#9CA3AF', fontSize: 12 }}>No customer files on this opportunity.</span>
      ) : (
      <div style={{ color: '#6B7280', fontSize: 11, marginBottom: 6 }}>
        ↕ Drag a file onto an Outlook email or your desktop to attach it.
      </div>
      )}
      {files.map(f => (
        <div key={f.id} {...fileDragProps(f)} style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px',
          background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, marginBottom: 4, fontSize: 13,
          cursor: f.dl_token ? 'grab' : 'default',
        }}>
          <span style={{ flex: 1, fontWeight: 600 }}>📎 {f.original_name}</span>
          <span style={{ color: '#9CA3AF', fontSize: 11 }}>
            {f.uploaded_by_name || '—'} · {fmtDate(f.created_at)}
            {f.size_bytes ? ` · ${(f.size_bytes / 1024).toFixed(0)} KB` : ''}
          </span>
          <a
            href={dlUrl(f) || undefined}
            style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 10px', textDecoration: 'none' }}
          >⬇ Download</a>
          {canRemove(f) && (
            <button
              onClick={() => removeFile(f)}
              disabled={removing === f.id}
              title="Remove this file from the job"
              style={{ ...styles.btnDanger, fontSize: 11, padding: '3px 10px' }}
            >{removing === f.id ? '…' : '✕ Remove'}</button>
          )}
        </div>
      ))}
    </div>
  );
}

// Inline-editable date cell for the Allocations picker. Saves on change
// (date inputs only fire onChange when the user commits a value, so blur
// handling isn't needed). Reverts to the original on save failure.
// Helpers: ISO <-> US display. Stored format is YYYY-MM-DD; user-facing
// format is MM/DD/YYYY (isoToUs lives in utils/activeJobsView.js, which the
// Active Jobs Date search shares). Parsing tolerates either separator
// (/, -, .) and year-first or month-first input.
const parseToIso = (input) => {
  if (!input) return { iso: '', valid: true };  // empty clears
  const cleaned = input.trim().replace(/[.\s]/g, '/').replace(/-/g, '/');
  // Match MM/DD/YYYY or M/D/YYYY (also accept YYYY/MM/DD as fallback).
  let m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(cleaned);
  let mo, dy, yr;
  if (m) { mo = parseInt(m[1], 10); dy = parseInt(m[2], 10); yr = parseInt(m[3], 10); }
  else if ((m = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(cleaned))) {
    yr = parseInt(m[1], 10); mo = parseInt(m[2], 10); dy = parseInt(m[3], 10);
  } else {
    return { iso: '', valid: false, reason: 'Format must be MM/DD/YYYY (e.g. 05/11/2026).' };
  }
  if (yr < 2000 || yr > 2100) return { iso: '', valid: false, reason: `Year ${yr} looks wrong — must be between 2000 and 2100.` };
  if (mo < 1 || mo > 12)      return { iso: '', valid: false, reason: `Month ${mo} is out of range.` };
  if (dy < 1 || dy > 31)      return { iso: '', valid: false, reason: `Day ${dy} is out of range.` };
  // Verify the date actually exists (e.g. Feb 30 should fail).
  const d = new Date(Date.UTC(yr, mo - 1, dy));
  if (d.getUTCFullYear() !== yr || d.getUTCMonth() !== mo - 1 || d.getUTCDate() !== dy) {
    return { iso: '', valid: false, reason: 'That date doesn\'t exist on the calendar.' };
  }
  const iso = `${yr}-${String(mo).padStart(2, '0')}-${String(dy).padStart(2, '0')}`;
  return { iso, valid: true };
};

// Start/End <-> duration math. End follows the app's own convention:
// end = start + duration_days (matches projectStatuses.js auto-complete and
// CrewWindowCell), so duration = end - start. addDaysIso lives in
// utils/activeJobsView.js so the Date cell and the Date search share it.
const daysBetweenIso = (startIso, endIso) =>
  Math.round((new Date(endIso + 'T00:00:00Z') - new Date(startIso + 'T00:00:00Z')) / 86400000);

// Stacked Start (top) + End (bottom) native date pickers. Start writes
// date_planned; End writes duration_days (= End - Start) so the Duration
// column auto-calcs. The End picker carries min={start}, so it opens on the
// start month and can't be set before the start. End is disabled until a
// Start exists (no anchor to measure from). Native <input type="date"> uses
// ISO (YYYY-MM-DD) values, so no text parsing here.
//
// Commit on BLUR (or Enter), never on change. A native date input fires
// `change` on every segment the user completes, so committing on change
// saved a half-typed date (year "0002" while typing 2027) and then set
// busy -> disabled, which yanks focus out of the field mid-type. Keystrokes
// only move local state; the prop-sync effects are skipped while the field
// is focused so a save's response can't stomp what is being typed.
function DateRangeCell({ startValue, durationDays, disabled, onStartSave, onDurationSave }) {
  const startIso = startValue ? startValue.slice(0, 10) : '';
  const endIso = (startIso && durationDays) ? addDaysIso(startIso, parseInt(durationDays, 10)) : '';
  const [s, setS] = useState(startIso);
  const [e, setE] = useState(endIso);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const sRef = useRef(null);
  const eRef = useRef(null);
  useEffect(() => { if (document.activeElement !== sRef.current) setS(startIso); }, [startIso]);
  useEffect(() => { if (document.activeElement !== eRef.current) setE(endIso); }, [endIso]);

  const commitStart = async (val) => {
    if (busy || val === startIso) return;
    if (!val) { setS(startIso); return; }  // date pickers can clear; ignore
    setErr(''); setBusy(true);
    try { await onStartSave(val); }
    catch (e2) { setErr(e2.message); setS(startIso); }
    finally { setBusy(false); }
  };
  const commitEnd = async (val) => {
    if (busy || val === endIso) return;
    if (!startIso) { setErr('Set a start date first.'); setE(''); return; }
    if (!val) { setE(endIso); return; }  // clearing End leaves duration as-is
    const days = daysBetweenIso(startIso, val);
    if (days < 1) { setErr('End must be after the start date.'); setE(endIso); return; }
    setErr(''); setBusy(true);
    try { await onDurationSave(days); }
    catch (e2) { setErr(e2.message); setE(endIso); }
    finally { setBusy(false); }
  };

  const boxStyle = (val) => ({
    width: 142, padding: '4px 6px', fontSize: 12,
    border: `1px solid ${err ? '#EF4444' : '#D1D5DB'}`, borderRadius: 4,
    background: busy ? '#F3F4F6' : (val ? '#fff' : '#FEF3C7'),
    color: val ? '#374151' : '#92400E',
  });
  const labelStyle = { fontSize: 9, color: '#9CA3AF', width: 26, textTransform: 'uppercase', letterSpacing: 0.4 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={labelStyle}>Start</span>
        <input
          type="date" value={s}
          ref={sRef}
          disabled={disabled}
          onChange={ev => setS(ev.target.value)}
          onBlur={ev => commitStart(ev.target.value)}
          onKeyDown={ev => { if (ev.key === 'Enter') ev.currentTarget.blur(); }}
          style={boxStyle(s)}
          title="Job start (planned date)."
        />
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span style={labelStyle}>End</span>
        <input
          type="date" value={e}
          ref={eRef}
          min={startIso || undefined}
          disabled={disabled || !startIso}
          onChange={ev => setE(ev.target.value)}
          onBlur={ev => commitEnd(ev.target.value)}
          onKeyDown={ev => { if (ev.key === 'Enter') ev.currentTarget.blur(); }}
          style={boxStyle(e)}
          title={startIso ? 'Job end. Sets Duration = End − Start automatically.' : 'Set a start date first.'}
        />
      </label>
      {err && <div style={{ fontSize: 10, color: '#991B1B', marginTop: 1, maxWidth: 160 }}>{err}</div>}
    </div>
  );
}

function PlannedDateCell({ value, disabled, onSave }) {
  const originalIso = value ? value.slice(0, 10) : '';
  const originalDisplay = isoToUs(originalIso);
  const [v, setV] = useState(originalDisplay);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');
  useEffect(() => { setV(isoToUs(originalIso)); }, [originalIso]);
  const commit = async () => {
    if (busy) return;
    if (v.trim() === originalDisplay) { setLocalError(''); return; }
    const parsed = parseToIso(v);
    if (!parsed.valid) {
      setLocalError(parsed.reason);
      // Don't revert -- leave the user's text so they can correct it.
      return;
    }
    setLocalError('');
    setBusy(true);
    try { await onSave(parsed.iso); }
    catch (err) { setLocalError(err.message); setV(originalDisplay); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ display: 'inline-block' }}>
      <input
        type="text"
        inputMode="numeric"
        placeholder="MM/DD/YYYY"
        value={v}
        disabled={disabled || busy}
        onChange={e => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
        style={{
          width: 110, padding: '4px 6px', fontSize: 12, fontFamily: 'monospace',
          border: `1px solid ${localError ? '#EF4444' : '#D1D5DB'}`, borderRadius: 4,
          background: busy ? '#F3F4F6' : (v ? '#fff' : '#FEF3C7'),
          color: v ? '#374151' : '#92400E',
        }}
        title={localError || (v ? 'Type MM/DD/YYYY. Press Enter or click away to save.' : 'No date set — type MM/DD/YYYY. Affects "days until active" + auto-status transition.')}
      />
      {localError && (
        <div style={{ fontSize: 10, color: '#991B1B', marginTop: 2, maxWidth: 140 }}>
          {localError}
        </div>
      )}
    </div>
  );
}

// Inline-editable duration cell. Saves on blur or Enter (same uncontrolled-
// input pattern as JobNumberCell in components/InlineTextCell.jsx). Empty
// value clears to NULL on the project.
function DurationCell({ value, disabled, onSave }) {
  const original = value ?? '';
  const [v, setV] = useState(String(original));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(String(value ?? '')); }, [value]);
  const commit = async () => {
    if (busy) return;
    if (String(v) === String(original)) return;
    setBusy(true);
    try { await onSave(v); }
    catch { setV(String(original)); }
    finally { setBusy(false); }
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
    else if (e.key === 'Escape') { e.preventDefault(); setV(String(original)); e.target.blur(); }
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <input
        type="number"
        min="1"
        max="3650"
        value={v}
        placeholder="—"
        disabled={disabled || busy}
        onChange={e => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        style={{
          width: 60, padding: '4px 6px', fontSize: 12, textAlign: 'right',
          border: '1px solid #D1D5DB', borderRadius: 4,
          background: busy ? '#F3F4F6' : '#fff',
          color: v ? '#374151' : '#9CA3AF',
        }}
        title="Days the job runs. Allocations default to start = planned date, end = planned date + this many days."
      />
      {v && <span style={{ fontSize: 11, color: '#6B7280' }}>days</span>}
    </span>
  );
}

// Crew Window cell: shows the actual ramp-up to ramp-down footprint
// across all of this job's allocations (min start → max end). When the
// crew window extends beyond the job's planned window (PMs ramp early,
// demob lasts past job end), shows a small "+Nd early / +Nd late" hint
// so Ops can see ramp at a glance. Empty when no allocations yet.
function CrewWindowCell({ crewStart, crewEnd, jobStart, jobDuration }) {
  if (!crewStart && !crewEnd) {
    return <span style={{ color: '#9CA3AF' }}>—</span>;
  }
  const cs = crewStart ? crewStart.slice(0, 10) : null;
  const ce = crewEnd ? crewEnd.slice(0, 10) : null;
  // Compute ramp delta vs job window if we have job dates.
  let earlyDays = 0, lateDays = 0;
  if (jobStart) {
    const js = jobStart.slice(0, 10);
    if (cs && cs < js) {
      earlyDays = Math.round((new Date(js + 'T00:00:00') - new Date(cs + 'T00:00:00')) / 86400000);
    }
    if (jobDuration && ce) {
      const jobEnd = new Date(js + 'T00:00:00');
      jobEnd.setDate(jobEnd.getDate() + parseInt(jobDuration, 10));
      const jeStr = jobEnd.toISOString().slice(0, 10);
      if (ce > jeStr) {
        lateDays = Math.round((new Date(ce + 'T00:00:00') - jobEnd) / 86400000);
      }
    }
  }
  const hasRamp = earlyDays > 0 || lateDays > 0;
  return (
    <div>
      <div>{cs ? fmtDate(cs) : '—'} → {ce ? fmtDate(ce) : '—'}</div>
      {hasRamp && (
        <div style={{ fontSize: 10, color: '#7C3AED', marginTop: 2 }}>
          {earlyDays > 0 && <span title="Crew ramps up before job's planned start">▲ {earlyDays}d early</span>}
          {earlyDays > 0 && lateDays > 0 && ' · '}
          {lateDays > 0 && <span title="Crew ramps down after job's planned end">▼ {lateDays}d late</span>}
        </div>
      )}
    </div>
  );
}

// ===== Crew date sync ====================================================
// George 2026-07-29: "when I make changes in the ops view for job start dates,
// it needs to ask me which employees to change their start dates so there
// isn't conflicting views." The job's window (date_planned + duration_days)
// and each employee's own start/end on job_allocations are separate fields —
// moving the job silently left the crew on the old dates, and the Job Hub,
// Site Access's onsite horizon and the estimate's Personnel schedule then all
// disagreed about when the job runs.
//
// Opens right after a successful Start or End edit in EITHER ops surface
// (Active Jobs feed + Allocations picker). Nothing is written until Apply, and
// only the ticked rows move — skipping is always one click. The server owns
// the date math (utils/crewDates.js) so this preview is exactly what Apply
// writes; changing the mode re-plans rather than recomputing here.
const signedDays = (n) => `${n > 0 ? '+' : ''}${n}`;

function CrewDateSyncModal({ projectId, jobLabel, changeLabel, kind, deltaDays, initialPlan, onClose, onApplied }) {
  const [mode, setMode] = useState('shift');
  const [includeTravel, setIncludeTravel] = useState(false);
  const [plan, setPlan] = useState(initialPlan);
  const [picked, setPicked] = useState(
    () => new Set(initialPlan.rows.filter(r => !r.warn).map(r => r.id))
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // initialPlan is already the shift / no-travel plan the parent fetched —
  // don't re-fetch it on mount, only when the user changes an option.
  const firstRun = useRef(true);

  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    let cancelled = false;
    setError('');
    api.opsPlanCrewDates(projectId, {
      mode, kind, delta_days: deltaDays, include_travel: includeTravel,
    })
      .then(p => {
        if (cancelled) return;
        setPlan(p);
        // A row that would end before it starts stays unticked until ops looks
        // at it — an inverted window breaks the compliance horizon math.
        setPicked(new Set(p.rows.filter(r => !r.warn).map(r => r.id)));
      })
      .catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [projectId, mode, kind, deltaDays, includeTravel]);

  const rows = plan?.rows || [];
  const toggle = (id) => setPicked(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allPicked = rows.length > 0 && rows.every(r => picked.has(r.id));

  const apply = async () => {
    if (busy || !picked.size) return;
    setBusy(true); setError('');
    try {
      const res = await api.opsApplyCrewDates(projectId, {
        mode, kind, delta_days: deltaDays, include_travel: includeTravel,
        allocation_ids: [...picked],
      });
      onApplied(res.updated);
    } catch (e) { setError(e.message); setBusy(false); }
  };

  const win = (s, e) => `${s ? fmtDate(s) : '—'} → ${e ? fmtDate(e) : '—'}`;
  const snapLabel = kind === 'start'
    ? `Set every start to the job start (${plan?.project?.date_planned ? fmtDate(plan.project.date_planned) : '—'})`
    : `Set every end to the job end (${plan?.project?.job_end ? fmtDate(plan.project.job_end) : '—'})`;

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={{ ...modalCard, maxWidth: 900 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>📆 Move the crew too?</h2>
          <button style={modalClose} onClick={onClose}>×</button>
        </div>
        <div style={{ background: '#FEF3C7', border: '1px solid #FBBF24', color: '#92400E', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
          <strong>{jobLabel}</strong> — {changeLabel}<br />
          Employee dates are stored per person and did <strong>not</strong> move. Pick who follows the job so
          the Job Hub, Site Access readiness and the crew window all agree.
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12, fontSize: 13 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="radio" checked={mode === 'shift'} onChange={() => setMode('shift')} />
            Shift by {signedDays(deltaDays)} day{Math.abs(deltaDays) === 1 ? '' : 's'}
            <span style={{ color: '#6B7280', fontSize: 11 }}>(keeps each person's ramp offset)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="radio" checked={mode === 'snap'} onChange={() => setMode('snap')} />
            {snapLabel}
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#6B7280' }}
            title="Per-diem start and hotel in/out are anchored to the employee's own dates — move them by the same amount.">
            <input type="checkbox" checked={includeTravel} onChange={e => setIncludeTravel(e.target.checked)} />
            Also move per-diem + hotel dates
          </label>
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        {rows.length === 0 ? (
          <div style={{ padding: 16, color: '#6B7280', fontStyle: 'italic', fontSize: 13 }}>
            Nothing to change with these options — every assigned employee already sits on the new dates.
          </div>
        ) : (
          <table style={{ ...styles.table, fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: 34 }}>
                  <input
                    type="checkbox"
                    checked={allPicked}
                    onChange={() => setPicked(allPicked ? new Set() : new Set(rows.map(r => r.id)))}
                    title={allPicked ? 'Unselect everyone' : 'Select everyone'}
                  />
                </th>
                <th style={styles.th}>Employee</th>
                <th style={styles.th}>Craft / Shift</th>
                <th style={styles.th}>Current</th>
                <th style={styles.th}>New</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ opacity: picked.has(r.id) ? 1 : 0.5 }}>
                  <td style={styles.td}>
                    <input type="checkbox" checked={picked.has(r.id)} onChange={() => toggle(r.id)} />
                  </td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>
                    {r.employee_name}
                    {r.warn && <div style={{ fontSize: 11, color: '#991B1B' }}>⚠ {r.warn}</div>}
                    {r.filled_from_job && <div style={{ fontSize: 11, color: '#6B7280' }}>no dates on file — taking the job window</div>}
                  </td>
                  <td style={{ ...styles.td, fontSize: 12, color: '#6B7280' }}>
                    {r.craft}{r.shift ? ` · ${r.shift}` : ''}
                  </td>
                  <td style={{ ...styles.td, fontSize: 12, color: '#6B7280' }}>{win(r.start_date, r.end_date)}</td>
                  <td style={{ ...styles.td, fontSize: 12, fontWeight: 600, color: r.warn ? '#991B1B' : '#065F46' }}>
                    {win(r.new_start_date, r.new_end_date)}
                    {r.travel && (
                      <div style={{ fontSize: 11, fontWeight: 400, color: '#6B7280' }}>
                        + per-diem / hotel moved
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16, alignItems: 'center' }}>
          <span style={{ marginRight: 'auto', fontSize: 12, color: '#6B7280' }}>
            {picked.size} of {rows.length} selected · {plan?.crew_count || 0} on this job
          </span>
          <button style={styles.btnSecondary} onClick={onClose} disabled={busy}>
            Leave crew dates alone
          </button>
          <button
            style={{ ...styles.btn, opacity: (busy || !picked.size) ? 0.5 : 1, cursor: (busy || !picked.size) ? 'not-allowed' : 'pointer' }}
            disabled={busy || !picked.size}
            onClick={apply}
          >{busy ? 'Saving…' : `Move ${picked.size} employee${picked.size === 1 ? '' : 's'}`}</button>
        </div>
      </div>
    </div>
  );
}

// Days-until-start chip. For Active jobs: "▶ Day N" (running). For Won
// jobs: "X days" with color coding by urgency. For undated: "—".
function DaysUntilChip({ status, datePlanned }) {
  if (!datePlanned) return <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const start = new Date(datePlanned.slice(0, 10) + 'T00:00:00');
  const diff = Math.round((start - today) / (1000 * 60 * 60 * 24));
  if (status === 'Active') {
    const dayNum = Math.max(1, -diff + 1);
    return <span style={{
      background: '#DBEAFE', color: '#1E3A8A', padding: '2px 8px',
      borderRadius: 10, fontSize: 11, fontWeight: 700,
    }}>▶ Day {dayNum}</span>;
  }
  // Won (or anything else) -- count down to start.
  if (diff < 0) {
    return <span style={{
      background: '#FEE2E2', color: '#991B1B', padding: '2px 8px',
      borderRadius: 10, fontSize: 11, fontWeight: 700,
    }}>{Math.abs(diff)}d overdue</span>;
  }
  if (diff === 0) {
    return <span style={{
      background: '#FED7AA', color: '#9A3412', padding: '2px 8px',
      borderRadius: 10, fontSize: 11, fontWeight: 700,
    }}>Today</span>;
  }
  // Color by urgency: red < 7 days, amber 7-30, green 30+
  const bg = diff < 7 ? '#FEE2E2' : (diff < 30 ? '#FEF3C7' : '#D1FAE5');
  const fg = diff < 7 ? '#991B1B' : (diff < 30 ? '#92400E' : '#065F46');
  return <span style={{
    background: bg, color: fg, padding: '2px 8px',
    borderRadius: 10, fontSize: 11, fontWeight: 700,
  }}>{diff} day{diff === 1 ? '' : 's'}</span>;
}

// Staffing-progress chip combining filled vs required. Hovering the chip
// reveals a craft-by-craft breakdown so Ops can see at a glance who's
// assigned and which crafts are still short.
//
// Color discipline -- needs-attention states are AMBER or RED so the eye
// catches them; only fully-staffed-against-plan goes GREEN. Indigo "looks
// fine" was misleading and is gone.
//
// States:
//   - No plan + no allocations: amber "📋 Plan needed"
//   - Allocations + no plan:    amber "⚠ N assigned · no plan" (can't validate)
//   - Plan + any craft short:   red   "X / Y · need Z more"   (per-craft check)
//   - Plan + total met + mix wrong: red "Mix off · need Z more"
//   - Plan + every craft met:   green "X / Y ✓"
//   - Plan + over-staffed:      green "X / Y ✓ (+N)"
function StaffingChip({ filled, required, craftCounts, craftRequirements }) {
  const counts = craftCounts || new Map();
  const reqs = craftRequirements || new Map();

  // Build the hover tooltip: per-craft progress with a status flag.
  const allCrafts = new Set([...counts.keys(), ...reqs.keys()]);
  const lines = [...allCrafts].sort().map(craft => {
    const have = counts.get(craft) || 0;
    const need = reqs.get(craft) || 0;
    if (need > 0) {
      const status = have >= need ? '✓' : (have === 0 ? '✗ none' : `need ${need - have}`);
      return `${craft}: ${have} of ${need}  (${status})`;
    }
    return `${craft}: ${have}`;
  });
  const tooltip = lines.length ? lines.join('\n') : 'No allocations or staffing plan yet.';

  const wrap = (children, bg, fg) => (
    <span title={tooltip} style={{
      background: bg, color: fg, padding: '3px 8px',
      borderRadius: 10, fontSize: 11, fontWeight: 700,
      cursor: 'help',
    }}>{children}</span>
  );

  // No formal staffing plan defined yet.
  if (required === 0) {
    if (filled === 0) return wrap('📋 Plan needed', '#FEF3C7', '#92400E');
    // Allocations exist but no plan -- can't tell if it's complete.
    // Amber so it visibly needs attention rather than indigo "looks fine".
    return wrap(`⚠ ${filled} assigned · no plan`, '#FEF3C7', '#92400E');
  }

  // Plan exists -- count craft-level shortages (the bug case where total
  // matches but the mix is wrong, e.g. 7 PMs assigned when plan needs
  // 5 PMs + 2 Crew. Total = 7, Required = 7, but Crew shortage = 2).
  let shortage = 0;
  for (const [craft, qty] of reqs.entries()) {
    const have = counts.get(craft) || 0;
    if (have < qty) shortage += (qty - have);
  }

  if (shortage > 0) {
    // Differentiate "short on total" vs "total met but mix is wrong".
    if (filled >= required) {
      return wrap(`⚠ Mix off · need ${shortage} more`, '#FEE2E2', '#991B1B');
    }
    return wrap(`${filled} / ${required} · need ${shortage} more`, '#FEE2E2', '#991B1B');
  }
  if (filled === required) return wrap(`${filled} / ${required} ✓`, '#D1FAE5', '#065F46');
  return wrap(`${filled} / ${required} ✓ (+${filled - required})`, '#D1FAE5', '#065F46');
}

// JobNumberCell (the inline-editable job number / PO # cell) lives in
// components/InlineTextCell.jsx since 2026-09-14 so the Project Tracker's PO #
// column shares it.

// ===== Job #s (TA pool register) =========================================

// George 2026-08-07: "I need to fix these duplicates and find what job
// numbers I have available 801-820 ... a next job number and it auto picks
// it or at least tells me what the next job number branch is available ...
// maybe easier to do a tab with used job numbers by year".
//
// The pool is 20 slots (801-820) that RESET YEARLY and lap when exhausted
// (801-26 -> 801-2-26 -> 801-3-26). Nothing enforced that, so live jobs went
// out double-booked. This tab is the register: every slot x lap for a year,
// who holds it, what is open, and what is double-booked. Read-only except
// for the two mint buttons, which write through the same route a typed
// number does (cascade + Field Services mirror).
//
// A job files under the year IN ITS NUMBER, not its start date -- the number
// is what consumed the slot.
function JobNumbersTab() {
  const [data, setData] = useState(null);
  const [year, setYear] = useState('');
  const [err, setErr] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);
  // Rollups — before the early returns below (hooks must run every render).
  const [rolledPool, togglePool] = useRollup('jobnums-pool');
  const [rolledFix, toggleFix] = useRollup('jobnums-fix');
  const [rolledReg, toggleReg] = useRollup('jobnums-register');
  const [rolledOut, toggleOut] = useRollup('jobnums-outside');

  const load = async (y) => {
    setLoading(true);
    try {
      const d = await api.opsJobNumberPool(y || undefined);
      setData(d);
      setYear(d.year2);
      setErr('');
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mint onto one job. `replace` renumbers a job that already has a number
  // (the collision fix) -- confirmed first, because the number is the
  // cross-app contract with Inventory and Field Services.
  const mint = async (job, replace) => {
    const label = job.job_number || job.site || `Project ${job.id}`;
    if (replace && !window.confirm(
      `Renumber "${job.job_number}" (${job.site}) to the next open pool number?\n\n` +
      `This is the job-tracking number Inventory and Field Services key on. ` +
      `Field Services updates the existing job in place (no duplicate), but ` +
      `anything printed or emailed with the old number stays wrong.`)) return;
    setBusyId(job.id);
    try {
      const r = await api.opsGenerateJobNumber(job.id, replace);
      await load(year);
      window.alert(`${label} is now ${r.job_number}.` +
        (r.slot_warning ? `\n\n⚠ ${r.slot_warning}` : ''));
    } catch (e) { window.alert(`Could not assign a number: ${e.message}`); }
    finally { setBusyId(null); }
  };

  if (loading && !data) return <div style={styles.card}>Loading job numbers...</div>;
  if (err) return <div style={{ ...styles.card, color: colors.red }}>{err}</div>;
  if (!data) return null;

  const L = data.ledger;
  const lapCols = Array.from({ length: L.lap_columns }, (_, i) => i + 1);
  const concurrent = L.concurrent || [];
  const needsWork = L.collisions.length + concurrent.length + L.malformed.length + data.unnumbered.length;
  // One live job per slot (George 2026-08-11) — an opening on a slot that is
  // still running is not on offer, so it is shown apart from the real ones.
  const openFree = L.free.filter(f => !f.blocked);
  const heldFree = L.free.filter(f => f.blocked);

  const chip = (bg, fg, bd) => ({
    display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 12,
    fontFamily: 'monospace', fontWeight: 700, background: bg, color: fg,
    border: `1px solid ${bd}`, marginRight: 6, marginBottom: 6,
  });
  const miniBtn = (disabled) => ({
    ...styles.btnSecondary, padding: '3px 8px', fontSize: 11,
    opacity: disabled ? 0.5 : 1, cursor: disabled ? 'default' : 'pointer',
  });

  return (
    <>
      {/* --- Header: year picker + the headline answer --------------------- */}
      <div style={{ ...styles.card, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <RollupH2 collapsed={rolledPool} onToggle={togglePool} style={{ margin: 0 }}>TA job-number pool · {L.pool_start}-{L.pool_end}</RollupH2>
          <select
            value={year}
            onChange={e => { setYear(e.target.value); load(e.target.value); }}
            style={{ ...styles.input, width: 110, margin: 0 }}>
            {data.years.map(y => <option key={y} value={y}>20{y}</option>)}
          </select>
          <button style={{ ...styles.btnSecondary, padding: '6px 12px' }} onClick={() => load(year)}>↻ Refresh</button>
          <div style={{ flex: 1 }} />
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Next number up</div>
            <div style={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 700, color: colors.green }}>
              {L.next.prefix}-<span style={{ color: '#9CA3AF' }}>CUST-CITYST</span>
            </div>
          </div>
        </div>
        {!rolledPool && <>
        {L.next.conflict && (
          <div style={{
            marginTop: 10, padding: '8px 10px', borderRadius: 4, fontSize: 12,
            background: '#FEF2F2', border: `1px solid ${colors.red}`, color: colors.red, fontWeight: 600,
          }}>
            ⚠ Every slot {L.pool_start}-{L.pool_end} is running a live job. The next number doubles up
            on slot {L.next.branch} — issue it if you must, then close out or renumber whatever finished.
          </div>
        )}
        <div style={{ marginTop: 12, borderTop: '1px solid #E5E7EB', paddingTop: 12 }}>
          <div style={{ fontSize: 12, color: '#374151', fontWeight: 600, marginBottom: 6 }}>
            Open in 20{L.year2} — {openFree.length} number{openFree.length === 1 ? '' : 's'}
          </div>
          {openFree.length
            ? openFree.map(f => (
                <span key={f.number}
                  title={f.next ? 'The next one the CRM hands out' : 'Open'}
                  style={chip(f.next ? colors.greenSoft : '#F9FAFB', f.next ? colors.green : '#374151',
                    f.next ? colors.green : '#D1D5DB')}>
                  {f.number}{f.next ? ' ←' : ''}
                </span>
              ))
            : <span style={{ fontSize: 12, color: colors.amber }}>
                No slot is both free and idle — every remaining opening sits on a slot that is
                still running a job.
              </span>}
          {heldFree.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 600, marginBottom: 6 }}>
                Not on offer — the slot is still running · {heldFree.length}
              </div>
              {heldFree.map(f => (
                <span key={f.number}
                  title={`Slot ${f.branch} is running ${(f.blocked_by || []).join(', ')} — only one live job at a time answers to ${f.branch}.`}
                  style={{ ...chip('#F3F4F6', '#9CA3AF', '#E5E7EB'), textDecoration: 'line-through' }}>
                  {f.number}
                </span>
              ))}
            </div>
          )}
          <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
            Suffix is customer (first 3 letters) + city/state, filled from the request. The number stays editable.
            No two running jobs share the leading 3 digits; a slot frees up when its job goes
            Complete or its end date passes, whichever comes first.
          </div>
        </div>
        </>}
      </div>

      {/* --- Needs attention ---------------------------------------------- */}
      {needsWork > 0 && (
        <div style={{ ...styles.card, marginBottom: 16, borderLeft: `4px solid ${colors.red}` }}>
          <RollupH2 collapsed={rolledFix} onToggle={toggleFix} style={{ marginBottom: rolledFix ? 0 : undefined }}>⚠ Needs a fix · {needsWork}</RollupH2>

          {!rolledFix && <>
          {L.collisions.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: colors.red, marginBottom: 4 }}>
                Double-booked · {L.collisions.length}
              </div>
              <p style={{ fontSize: 12, color: '#6B7280', marginTop: 0 }}>
                Two jobs on the same slot and lap. Renumber the one that has moved the least — the
                button below takes the next open number and mirrors it to Field Services.
              </p>
              <table style={{ ...styles.table, marginBottom: 18 }}>
                <thead><tr>
                  <th style={styles.th}>Slot</th><th style={styles.th}>Job #</th>
                  <th style={styles.th}>Site</th><th style={styles.th}>Status</th><th style={styles.th}></th>
                </tr></thead>
                <tbody>
                  {L.collisions.map(c => c.holders.map((h, i) => (
                    <tr key={h.id} style={{ background: i === 0 ? '#FEF2F2' : '#fff' }}>
                      {i === 0 && (
                        <td style={{ ...styles.td, fontFamily: 'monospace', fontWeight: 700, verticalAlign: 'top' }}
                            rowSpan={c.holders.length}>{c.number}</td>
                      )}
                      <td style={{ ...styles.td, fontFamily: 'monospace' }}>{h.job_number}</td>
                      <td style={styles.td}>{h.site}</td>
                      <td style={styles.td}><StatusPill status={h.status} /></td>
                      <td style={styles.td}>
                        <button style={miniBtn(busyId === h.id)} disabled={busyId === h.id}
                          onClick={() => mint(h, true)}>
                          {busyId === h.id ? '...' : `Renumber → ${L.next.prefix}`}
                        </button>
                      </td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </>
          )}

          {concurrent.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: colors.red, marginBottom: 4 }}>
                Two live jobs on one slot · {concurrent.length}
              </div>
              <p style={{ fontSize: 12, color: '#6B7280', marginTop: 0 }}>
                Different laps, same leading 3 digits — and both windows are still open. The yard,
                the gate and the paperwork all say the slot number out loud, so these read as one
                job. Renumber the one that has moved the least, or close out whatever has actually
                finished. Won counts: it flips to Active on its own start date.
              </p>
              <table style={{ ...styles.table, marginBottom: 18 }}>
                <thead><tr>
                  <th style={styles.th}>Slot</th><th style={styles.th}>Job #</th>
                  <th style={styles.th}>Site</th><th style={styles.th}>Status</th>
                  <th style={styles.th}>Window</th><th style={styles.th}></th>
                </tr></thead>
                <tbody>
                  {concurrent.map(c => c.holders.map((h, i) => (
                    <tr key={h.id} style={{ background: i === 0 ? '#FEF2F2' : '#fff' }}>
                      {i === 0 && (
                        <td style={{ ...styles.td, fontFamily: 'monospace', fontWeight: 700, verticalAlign: 'top' }}
                            rowSpan={c.holders.length}>{c.branch}</td>
                      )}
                      <td style={{ ...styles.td, fontFamily: 'monospace' }}>{h.job_number}</td>
                      <td style={styles.td}>{h.site}</td>
                      <td style={styles.td}><StatusPill status={h.status} /></td>
                      <td style={{ ...styles.td, fontSize: 12, color: '#6B7280' }}>
                        {h.date_planned ? fmtDate(h.date_planned) : 'no start date'}
                        {h.duration_days ? ` · ${h.duration_days}d` : ''}
                      </td>
                      <td style={styles.td}>
                        <button style={miniBtn(busyId === h.id)} disabled={busyId === h.id}
                          onClick={() => mint(h, true)}>
                          {busyId === h.id ? '...' : `Renumber → ${L.next.prefix}`}
                        </button>
                      </td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </>
          )}

          {data.unnumbered.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: colors.amber, marginBottom: 4 }}>
                Live jobs with no number · {data.unnumbered.length}
              </div>
              <table style={{ ...styles.table, marginBottom: 18 }}>
                <thead><tr>
                  <th style={styles.th}>Site</th><th style={styles.th}>Status</th>
                  <th style={styles.th}>Start</th><th style={styles.th}>Est. $</th><th style={styles.th}></th>
                </tr></thead>
                <tbody>
                  {data.unnumbered.map(u => (
                    <tr key={u.id}>
                      <td style={styles.td}>{u.site}</td>
                      <td style={styles.td}><StatusPill status={u.status} /></td>
                      <td style={styles.td}>{fmtDate(u.date_planned)}</td>
                      <td style={styles.td}>{fmtMoney(u.estimated_value)}</td>
                      <td style={styles.td}>
                        <button style={miniBtn(busyId === u.id)} disabled={busyId === u.id}
                          onClick={() => mint(u, false)}>
                          {busyId === u.id ? '...' : `Assign ${L.next.prefix}`}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {L.malformed.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: colors.amber, marginBottom: 4 }}>
                Not a readable job number · {L.malformed.length}
              </div>
              <p style={{ fontSize: 12, color: '#6B7280', marginTop: 0 }}>
                No branch or no year segment, so these hold no slot in the register — and the slot
                they were meant to hold reads as open. Fix on the Active Jobs tab, or renumber here.
              </p>
              <table style={styles.table}>
                <thead><tr>
                  <th style={styles.th}>Job #</th><th style={styles.th}>Site</th>
                  <th style={styles.th}>Status</th><th style={styles.th}></th>
                </tr></thead>
                <tbody>
                  {L.malformed.map(m => (
                    <tr key={m.id}>
                      <td style={{ ...styles.td, fontFamily: 'monospace', color: colors.red }}>{m.job_number}</td>
                      <td style={styles.td}>{m.site}</td>
                      <td style={styles.td}><StatusPill status={m.status} /></td>
                      <td style={styles.td}>
                        <button style={miniBtn(busyId === m.id)} disabled={busyId === m.id}
                          onClick={() => mint(m, true)}>
                          {busyId === m.id ? '...' : `Renumber → ${L.next.prefix}`}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>}
        </div>
      )}

      {/* --- The register -------------------------------------------------- */}
      <div style={{ ...styles.card, marginBottom: 16 }}>
        <RollupH2 collapsed={rolledReg} onToggle={toggleReg} style={{ marginBottom: rolledReg ? 0 : undefined }}>Register · 20{L.year2}</RollupH2>
        {!rolledReg && <>
        <p style={{ fontSize: 12, color: '#6B7280', marginTop: 0 }}>
          One row per pool slot. Lap 1 is the plain number ({L.pool_start}-{L.year2}); each later lap
          inserts its number between branch and year ({L.pool_start}-2-{L.year2}). A slot with a
          running job is <strong>held</strong> at every lap — its empty cells are not on offer.
          A job releases its slot once it goes Complete <em>or</em> its end date passes
          (start + duration), whichever comes first, so the pool never waits on paperwork.
        </p>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: 70 }}>Slot</th>
              {lapCols.map(l => <th key={l} style={styles.th}>Lap {l}</th>)}
            </tr>
          </thead>
          <tbody>
            {L.rows.map(r => {
              const dual = (r.live_count || 0) > 1;
              return (
              <tr key={r.branch}>
                <td style={{
                  ...styles.td, fontFamily: 'monospace', fontWeight: 700, fontSize: 14,
                  background: dual ? '#FEF2F2' : undefined,
                  borderLeft: dual ? `3px solid ${colors.red}` : undefined,
                }}>
                  {r.branch}
                  {dual && (
                    <div title={`${r.live_count} live jobs answer to ${r.branch} at once`}
                      style={{ fontSize: 10, fontWeight: 700, color: colors.red, fontFamily: 'inherit' }}>
                      {r.live_count} LIVE
                    </div>
                  )}
                  {!dual && r.occupied && (
                    <div style={{ fontSize: 10, fontWeight: 600, color: '#9CA3AF', fontFamily: 'inherit' }}>held</div>
                  )}
                </td>
                {r.laps.map(l => {
                  const clash = l.holders.length > 1;
                  // An empty cell on a slot that is still running is not an
                  // opening -- showing it green was the whole bug.
                  const held = !l.holders.length && r.occupied;
                  return (
                    <td key={l.lap} style={{
                      ...styles.td, verticalAlign: 'top',
                      background: clash ? '#FEF2F2'
                        : (l.holders.length ? '#fff' : (held ? '#F9FAFB' : '#F0FDF4')),
                      borderLeft: clash ? `3px solid ${colors.red}` : undefined,
                    }}>
                      {!l.holders.length ? (
                        <span
                          title={held ? `Slot ${r.branch} is still running a live job — not on offer until it goes Complete.` : 'Open'}
                          style={{
                            fontFamily: 'monospace', fontSize: 12, fontWeight: 700,
                            color: held ? '#9CA3AF' : colors.green,
                            textDecoration: held ? 'line-through' : undefined,
                          }}>
                          {l.number}{held ? ' · held' : ' · open'}
                        </span>
                      ) : l.holders.map(h => (
                        <div key={h.id} style={{ marginBottom: l.holders.length > 1 ? 6 : 0 }}>
                          <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: clash ? colors.red : '#111827' }}>
                            {h.job_number}
                          </div>
                          <div style={{ fontSize: 12, color: '#374151' }}>
                            {h.site} · <StatusPill status={h.status} />
                          </div>
                          <div style={{ fontSize: 11, color: '#6B7280' }}>
                            {h.date_planned ? fmtDate(h.date_planned) : 'no start date'}
                            {h.branch_code ? ` · ${h.branch_code}` : ''}
                            {h.account_manager ? ` · ${h.account_manager}` : ''}
                          </div>
                          {/* Status still open, window already over -- says why
                              the slot below reads free (George 2026-08-11). */}
                          {h.past_end && (
                            <div title={`Ran through ${fmtDate(h.job_end)} — past its end date, so it no longer holds slot ${h.branch}. Status is still ${h.status}.`}
                              style={{ fontSize: 11, color: colors.amber, fontWeight: 600 }}>
                              ended {fmtDate(h.job_end)} · slot released
                            </div>
                          )}
                        </div>
                      ))}
                    </td>
                  );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
        </>}
      </div>

      {/* --- Outside the pool ---------------------------------------------- */}
      {L.outside.length > 0 && (
        <div style={styles.card}>
          <RollupH2 collapsed={rolledOut} onToggle={toggleOut} style={{ marginBottom: rolledOut ? 0 : undefined }}>Outside {L.pool_start}-{L.pool_end} · {L.outside.length}</RollupH2>
          {!rolledOut && <>
          <p style={{ fontSize: 12, color: '#6B7280', marginTop: 0 }}>
            8xx numbers that carry 20{L.year2} but sit outside the TA sub-branch pool — the 800
            division number itself, or numbers issued past 820. They consume no pool slot.
          </p>
          <table style={styles.table}>
            <thead><tr>
              <th style={styles.th}>Job #</th><th style={styles.th}>Site</th><th style={styles.th}>Status</th>
            </tr></thead>
            <tbody>
              {L.outside.map(o => (
                <tr key={o.id}>
                  <td style={{ ...styles.td, fontFamily: 'monospace' }}>{o.job_number}</td>
                  <td style={styles.td}>{o.site}</td>
                  <td style={styles.td}><StatusPill status={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          </>}
        </div>
      )}
    </>
  );
}

// ===== Roster (employees) ================================================

// One-line home address from whatever parts exist — mirrors the server's
// formatAddress in utils/integrationRoster.js (the shape Field Services shows
// on its crew roster). Missing parts drop out without leaving stray commas.
export function empAddress(e) {
  if (!e) return '';
  const t = (v) => String(v ?? '').trim();
  const street = [t(e.address_line1), t(e.address_line2)].filter(Boolean).join(', ');
  const cityState = [t(e.city), [t(e.state), t(e.zip)].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');
  return [street, cityState].filter(Boolean).join(', ');
}

// Bulk "read the address off every DL scan we already have" — so nobody opens
// 387 employees one at a time. Polls while running; the run continues
// server-side even if this page is closed.
function AddressBackfillButton({ onDone }) {
  const [st, setSt] = useState(null);
  const [err, setErr] = useState('');
  const timer = useRef(null);

  const poll = async () => {
    try {
      const s = await api.addressBackfillStatus();
      setSt(s);
      if (!s.running && timer.current) { clearInterval(timer.current); timer.current = null; onDone && onDone(); }
    } catch (e) { /* transient poll failure shouldn't clear the UI */ }
  };
  useEffect(() => {
    poll();
    return () => { if (timer.current) clearInterval(timer.current); };
  }, []);

  const start = async () => {
    setErr('');
    const n = st?.pending?.with_dl ?? 0;
    if (!n) return;
    if (!confirm(`Read the home address off ${n} employee${n === 1 ? "'s" : "s'"} driver's-license scan${n === 1 ? '' : 's'}?\n\nFills blank addresses only — a corrected address is never overwritten. Runs in the background; you can leave this page.`)) return;
    try {
      const r = await api.runAddressBackfill();
      if (r.started) {
        setSt(s => ({ ...(s || {}), ...r.state }));
        if (!timer.current) timer.current = setInterval(poll, 3000);
      } else if (r.reason === 'already_running') {
        if (!timer.current) timer.current = setInterval(poll, 3000);
      }
    } catch (e) { setErr(e.message); }
  };

  const running = st?.running;
  const pending = st?.pending?.with_dl ?? 0;
  const noDocs = st?.pending?.missing_no_docs ?? 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {err && <span style={{ fontSize: 11, color: colors.red }}>{err}</span>}
      {running ? (
        <span style={{ fontSize: 12, color: '#6B7280' }}>
          🏠 Reading DL scans… {st.done}/{st.total}
          {st.filled > 0 && <> · <b style={{ color: '#065F46' }}>{st.filled} filled</b></>}
          {st.current && <> · {st.current}</>}
        </span>
      ) : (
        <button
          style={{ ...styles.btnSecondary, opacity: pending ? 1 : 0.5, cursor: pending ? 'pointer' : 'not-allowed' }}
          disabled={!pending}
          onClick={start}
          title={pending
            ? `Reads the home address off the DL scan already on file for ${pending} employee(s) missing one. Fills blanks only.`
            : noDocs
              ? `No DL scans left to read. ${noDocs} employee(s) still have no address and no documents on file — those need the address entered another way.`
              : 'Every employee with a document on file already has an address.'}
        >
          🏠 Read addresses from DL scans{pending ? ` (${pending})` : ''}
        </button>
      )}
      {!running && st && st.done > 0 && (
        <span style={{ fontSize: 11, color: '#065F46' }}>
          ✓ {st.filled} filled{st.no_address ? ` · ${st.no_address} no address on the scan` : ''}{st.failed ? ` · ${st.failed} failed` : ''}
        </span>
      )}
    </div>
  );
}

function RosterTab() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // employee object being edited
  const [showInactive, setShowInactive] = useState(false);
  const [togglingId, setTogglingId] = useState(null); // row currently saving an inline toggle
  const [docsFor, setDocsFor] = useState(null); // employee whose documents modal is open
  const [badgesFor, setBadgesFor] = useState(null); // employee whose site-badges modal is open
  const [recordFor, setRecordFor] = useState(null); // employee whose record (DISA/Training/ISN) modal is open
  const [deactivating, setDeactivating] = useState(null); // employee pending a deactivate confirm + rehire-eligibility pick
  const [rolled, toggleRolled] = useRollup('roster');

  const reload = () => api.listEmployees().then(setRows).catch(e => setError(e.message));
  useEffect(() => { reload(); }, []);

  const visible = showInactive ? rows : rows.filter(r => r.is_active);

  // Employee search + sortable/filterable headers (same TableControls pattern
  // as the DISA tab). Seeded from ?emp= when arriving via a "jump to
  // roster" link (e.g. the DISA tab's clickable employee names) — read
  // once on mount, not kept in sync afterward, so typing in the box here
  // doesn't fight the URL.
  const [jumpParams] = useSearchParams();
  const [q, setQ] = useState(() => jumpParams.get('emp') || '');
  const s = q.trim().toLowerCase();
  const searched = s
    ? visible.filter(e =>
        (e.full_name || '').toLowerCase().includes(s) ||
        (e.type || '').toLowerCase().includes(s) ||
        (e.craft || '').toLowerCase().includes(s) ||
        (e.email || '').toLowerCase().includes(s) ||
        (e.phone || e.mobile || '').toLowerCase().includes(s) ||
        (e.employee_no || '').toLowerCase().includes(s))
    : visible;
  const rosterCols = {
    name: e => e.full_name,
    type: e => e.type || '',
    craft: e => e.craft || '',
    email: e => e.email || '',
    phone: e => e.phone || e.mobile || '',
    address: e => empAddress(e),
    pay_st: e => (e.pay_st_default ?? ''),
    pay_ot: e => (e.pay_ot_default ?? ''),
    paylocity: e => (e.paylocity_status === 'Active' ? 'active' : 'not set'),
    status: e => (e.is_active ? 'active' : 'inactive'),
  };
  const { displayRows, headerProps, filters, clearFilter, clearAll } = useTableControls(searched, rosterCols);
  const ROSTER_COL_LABELS = { name: 'Name', type: 'Type', craft: 'Craft', email: 'Email', phone: 'Phone', address: 'Address', pay_st: 'Pay ST', pay_ot: 'Pay OT', paylocity: 'Paylocity', status: 'Status' };

  // Scroll to top whenever the form opens so the user actually sees it open
  // (otherwise clicking Edit on row #40 looks like nothing happened).
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const startCreate = () => {
    setEditing({
      full_name: '', employee_no: '', type: 'Full-Time', craft: '', email: '', phone: '',
      address_line1: '', address_line2: '', city: '', state: '', zip: '',
      pay_st_default: '', pay_ot_default: '', paylocity_status: '', notes: '', is_active: true
    });
    scrollToTop();
  };
  const startEdit = (e) => {
    setEditing({ ...e, pay_st_default: e.pay_st_default ?? '', pay_ot_default: e.pay_ot_default ?? '' });
    scrollToTop();
  };
  const cancel = () => setEditing(null);

  const save = async () => {
    setError('');
    try {
      if (editing.id) await api.updateEmployee(editing.id, editing);
      else await api.createEmployee(editing);
      setEditing(null);
      reload();
    } catch (err) { setError(err.message); }
  };
  const remove = async (id, name) => {
    if (!confirm(`Delete ${name}?`)) return;
    try { await api.deleteEmployee(id); reload(); }
    catch (err) {
      // Allocation guard: offer a force-delete (removes their job
      // assignments too) instead of burying the 400 in the top error box.
      if (/allocation/i.test(err.message)) {
        if (confirm(`${name} is assigned to jobs — ${err.message}\n\nDelete anyway? Their job assignments will be removed with them.`)) {
          try { await api.deleteEmployee(id, { force: true }); reload(); return; }
          catch (err2) { alert(`Delete failed: ${err2.message}`); }
        }
        return;
      }
      alert(`Delete failed: ${err.message}`);
    }
  };

  // Inline toggles -- click the Paylocity / Status cell to flip the value
  // without opening the edit form. Optimistic UI: row updates immediately,
  // reverts on server error.
  const togglePaylocity = async (emp) => {
    setTogglingId(emp.id);
    setError('');
    const next = emp.paylocity_status === 'Active' ? '' : 'Active';
    try { await api.updateEmployee(emp.id, { ...emp, paylocity_status: next }); reload(); }
    catch (err) { setError(err.message); }
    finally { setTogglingId(null); }
  };
  const toggleActive = (emp) => {
    // Deactivating asks for rehire eligibility first (captured in the same
    // request, since the record is sealed read-only immediately after —
    // there's no later chance to set it). Reactivating stays instant.
    if (emp.is_active) { setDeactivating(emp); return; }
    reactivate(emp);
  };
  const reactivate = async (emp) => {
    setTogglingId(emp.id);
    setError('');
    try { await api.updateEmployee(emp.id, { ...emp, is_active: true }); reload(); }
    catch (err) { setError(err.message); }
    finally { setTogglingId(null); }
  };
  const confirmDeactivate = async (rehireEligible) => {
    const emp = deactivating;
    setDeactivating(null);
    setTogglingId(emp.id);
    setError('');
    try {
      await api.updateEmployee(emp.id, { ...emp, is_active: false, rehire_eligible: rehireEligible });
      reload();
    } catch (err) { setError(err.message); }
    finally { setTogglingId(null); }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <RollupH2 collapsed={rolled} onToggle={toggleRolled} style={{ margin: 0 }}>
          Roster
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
            · {visible.length} employee{visible.length === 1 ? '' : 's'}
          </span>
        </RollupH2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Show inactive
          </label>
          <AddressBackfillButton onDone={reload} />
          <button style={styles.btn} onClick={startCreate}>+ Add Employee</button>
        </div>
      </div>
      {error && <div style={styles.errorBox}>{error}</div>}

      {!rolled && <>
      <RosterImport onImported={reload} />

      {editing && <EmployeeForm employee={editing} onChange={setEditing} onSave={save} onCancel={cancel} />}

      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="🔍 Search name, craft, email, phone…"
            style={{ ...styles.input, maxWidth: 320 }} />
          <span style={{ fontSize: 12, color: '#6B7280' }}>
            {displayRows.length === visible.length ? `${visible.length} shown` : `${displayRows.length} of ${visible.length} shown`}
          </span>
        </div>
        <ActiveFiltersBar filters={filters} clearFilter={clearFilter} clearAll={clearAll} colLabels={ROSTER_COL_LABELS} />
        <div style={{ overflowX: 'auto' }}>
        <table style={{ ...styles.table, fontSize: 13 }}>
          <thead>
            <tr>
              <SortableFilterTh label="Name" {...headerProps('name')} />
              <SortableFilterTh label="Type" {...headerProps('type')} />
              <SortableFilterTh label="Craft" {...headerProps('craft')} />
              <SortableFilterTh label="Email" {...headerProps('email')} />
              <SortableFilterTh label="Phone" {...headerProps('phone')} />
              <SortableFilterTh label="Address" {...headerProps('address')} />
              <SortableFilterTh label="Pay ST" {...headerProps('pay_st')} align="right" noFilter />
              <SortableFilterTh label="Pay OT" {...headerProps('pay_ot')} align="right" noFilter />
              <SortableFilterTh label="Paylocity" {...headerProps('paylocity')} noFilter />
              <SortableFilterTh label="Status" {...headerProps('status')} noFilter />
              <th style={{ background: '#1F2937' }}></th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map(e => (
              <tr key={e.id} style={!e.is_active ? { opacity: 0.5 } : {}}>
                <td style={styles.td}>
                  <button onClick={() => setRecordFor(e)} title="View all records (DISA / Training / ISN)"
                    style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 600, color: colors.orange, cursor: 'pointer', textAlign: 'left' }}>
                    {e.full_name}
                  </button>
                </td>
                <td style={styles.td}>{e.type || '—'}</td>
                <td style={styles.td}>{e.craft || '—'}</td>
                <td style={{ ...styles.td, fontSize: 11 }}>{e.email || '—'}</td>
                <td style={{ ...styles.td, fontSize: 12 }}>{e.phone || e.mobile || '—'}</td>
                <td style={{ ...styles.td, fontSize: 11 }} title={empAddress(e) || 'No address on file — read it from the DL scan in 📂 Docs'}>
                  {empAddress(e) || <span style={{ color: colors.red }}>— add for mileage</span>}
                </td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{e.pay_st_default ? `$${e.pay_st_default}` : '—'}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{e.pay_ot_default ? `$${e.pay_ot_default}` : '—'}</td>
                <td style={styles.td}>
                  <ToggleChip
                    active={e.paylocity_status === 'Active'}
                    activeLabel="✓ Active"
                    inactiveLabel="Not set"
                    busy={togglingId === e.id}
                    onClick={() => togglePaylocity(e)}
                    title="Click to toggle Paylocity status (Active ↔ Not set)"
                  />
                </td>
                <td style={styles.td}>
                  <ToggleChip
                    active={e.is_active}
                    activeLabel="✓ Active"
                    inactiveLabel="Inactive"
                    busy={togglingId === e.id}
                    onClick={() => toggleActive(e)}
                    title="Click to activate/deactivate this employee. Deactivating seals the record read-only until reactivated."
                  />
                  {!e.is_active && e.rehire_eligible !== null && (
                    <div style={{ fontSize: 10, marginTop: 3, color: e.rehire_eligible ? colors.green : colors.red, fontWeight: 600 }}>
                      {e.rehire_eligible ? '✓ Rehire eligible' : '✕ Not eligible for rehire'}
                    </div>
                  )}
                </td>
                <td style={styles.td}>
                  <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 8px' }}
                    title="DL / TWIC / cert scans + badging facts (used by the badging form filler)"
                    onClick={() => setDocsFor(e)}>📂 Docs</button>
                  {' '}
                  <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 8px' }}
                    title="Permanent site badges — once issued, overrides the rolling drug-test-age check for that customer/site"
                    onClick={() => setBadgesFor(e)}>🪪 Badges</button>
                  {' '}
                  <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 8px' }} onClick={() => startEdit(e)}>Edit</button>
                  {' '}
                  <button style={{ ...styles.btnDanger, fontSize: 11, padding: '4px 8px' }} onClick={() => remove(e.id, e.full_name)}>Del</button>
                </td>
              </tr>
            ))}
            {displayRows.length === 0 && (
              <tr><td colSpan={10} style={{ ...styles.td, textAlign: 'center', color: '#6B7280' }}>
                {visible.length === 0
                  ? 'No employees yet. Click "+ Add Employee" or import via Allocations → Spreadsheet Upload.'
                  : 'No employees match your search or filters.'}
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {docsFor && (
        <EmployeeDocsModal
          employee={docsFor}
          onClose={() => setDocsFor(null)}
          onError={setError}
          // An address read off the DL must show in the roster's Address
          // column without a manual refresh.
          onSaved={(updated) => {
            setDocsFor(updated);
            setRows(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
          }}
        />
      )}

      {badgesFor && (
        <EmployeeBadgesModal
          employee={badgesFor}
          onClose={() => setBadgesFor(null)}
          onError={setError}
        />
      )}

      {recordFor && (
        <EmployeeRecordModal
          employee={recordFor}
          onClose={() => setRecordFor(null)}
          onError={setError}
        />
      )}

      {deactivating && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 20, width: '100%', maxWidth: 380, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#1F2937' }}>Deactivate {deactivating.full_name}?</h3>
            <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 14px' }}>
              Their record — profile, documents, badges — becomes read-only until reactivated.
              Mark rehire eligibility now; it can't be changed while the record is sealed.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button style={{ ...styles.btn, background: colors.green }} onClick={() => confirmDeactivate(true)}>✓ Eligible for rehire</button>
              <button style={styles.btnDanger} onClick={() => confirmDeactivate(false)}>✕ Not eligible for rehire</button>
              <button style={styles.btnSecondary} onClick={() => confirmDeactivate(null)}>Skip — not sure yet</button>
              <button style={{ ...styles.btnSecondary, marginTop: 6 }} onClick={() => setDeactivating(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      </>}
    </>
  );
}

// Drag-and-drop names-only roster importer (Roster tab). Drop an xlsx/csv
// with a column of employee names -> preview (new vs already on roster) ->
// confirm to insert the new ones. "Last, First" is reordered + title-cased
// server-side. Backend: POST /api/workforce/roster/upload (?dry=true).
function RosterImport({ onImported }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setError(''); setResult(null); };

  const runPreview = async (f) => {
    if (!f) return;
    setError(''); setResult(null); setBusy(true); setFile(f);
    try { setPreview(await api.previewRoster(f)); }
    catch (e) { setError(e.message); setPreview(null); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!file) return;
    setBusy(true); setError('');
    try {
      const r = await api.importRoster(file);
      setResult(r); setPreview(null); setFile(null);
      onImported?.();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) runPreview(f);
  };

  return (
    <div style={{ ...styles.card, marginBottom: 12 }}>
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? colors.orange : '#CBD5E1'}`,
          background: dragOver ? '#FFF7ED' : '#F8FAFC',
          borderRadius: 8, padding: '18px 16px', textAlign: 'center', cursor: 'pointer',
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 14, color: colors.charcoal }}>
          ⬆ Import roster — drag &amp; drop a spreadsheet here
        </div>
        <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
          .xlsx / .xls / .csv — names-only works (header “Employee Name”, or names in the first column),
          or use the full template for every category. “Last, First” is reordered automatically.
          {' '}{busy && !preview ? 'Reading…' : 'Click to browse.'}
        </div>
        <button
          style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px', marginTop: 8 }}
          onClick={e => { e.stopPropagation(); api.downloadRosterTemplate().catch(err => setError(err.message)); }}>
          ⬇ Download Template (all categories)
        </button>
        <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) runPreview(f); }} />
      </div>

      {error && <div style={{ ...styles.errorBox, marginTop: 10 }}>{error}</div>}

      {preview && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            {file?.name ? <strong>{file.name}</strong> : null}
            {preview.name_column ? <span style={{ color: '#6B7280' }}> · column “{preview.name_column}”</span> : null}
            {preview.field_columns?.length ? <span style={{ color: '#6B7280' }}> · detected: {preview.field_columns.join(', ')}</span> : null}
            <span style={{ color: '#6B7280' }}> · {preview.total_in_file} name{preview.total_in_file === 1 ? '' : 's'} found</span>
            {' · '}<span style={{ color: '#047857', fontWeight: 700 }}>{preview.new_count} new</span>
            {' · '}<span style={{ color: '#6B7280' }}>{preview.exists_count} already on roster</span>
            {preview.possible_dup_count > 0 &&
              <>{' · '}<span style={{ color: '#B45309', fontWeight: 700 }}>⚠ {preview.possible_dup_count} possible dup{preview.possible_dup_count === 1 ? '' : 's'} to review</span></>}
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 6 }}>
            {[...preview.items].sort((a, b) => (b.possible_dup ? 1 : 0) - (a.possible_dup ? 1 : 0)).map((it, idx) => (
              <div key={idx} title={it.raw !== it.name ? `from “${it.raw}”` : ''}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px', fontSize: 12,
                  borderTop: idx ? '1px solid #F1F5F9' : 'none',
                  background: it.possible_dup ? '#FFFBEB' : 'transparent',
                  color: it.status === 'new' ? colors.charcoal : '#9CA3AF',
                }}>
                <span style={{ width: 16, textAlign: 'center', color: it.possible_dup ? '#B45309' : (it.status === 'new' ? '#047857' : '#9CA3AF') }}>
                  {it.possible_dup ? '⚠' : (it.status === 'new' ? '＋' : '✓')}
                </span>
                <span style={{ fontWeight: it.status === 'new' ? 600 : 400 }}>{it.name}</span>
                {it.status === 'exists' && <span style={{ fontSize: 11 }}>{it.roster_name ? `(on roster as “${it.roster_name}” — matched by employee #)` : '(on roster)'}</span>}
                {it.possible_dup &&
                  <span style={{ marginLeft: 'auto', color: '#B45309', fontSize: 11, paddingLeft: 8 }}>
                    possible dup of {it.possible_dup.join(', ')}
                  </span>}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
            <button style={styles.btn} onClick={commit} disabled={busy || preview.new_count === 0}>
              {busy ? 'Importing…' : `Import ${preview.new_count} employee${preview.new_count === 1 ? '' : 's'}`}
            </button>
            <button style={styles.btnSecondary} onClick={reset} disabled={busy}>Cancel</button>
            {preview.new_count === 0 && (
              <span style={{ fontSize: 12, color: '#6B7280' }}>Everyone in this file is already on the roster.</span>
            )}
          </div>
          {preview.possible_dup_count > 0 && (
            <div style={{ fontSize: 11, color: '#92400E', marginTop: 6 }}>
              ⚠ Amber rows match someone already on the roster by surname + first initial (a nickname or
              spelling twin — e.g. “Chris” vs “Christopher”). They still import — Cancel to fix the file first,
              or import and delete any true duplicate after.
            </div>
          )}
          {preview.employee_no_conflicts?.length > 0 && (
            <div style={{ fontSize: 11, color: '#92400E', marginTop: 6 }}>
              ⚠ {preview.employee_no_conflicts.length} Employee No value{preview.employee_no_conflicts.length === 1 ? ' appears' : 's appear'} on
              more than one row in the file — kept on the first row, skipped on the later one{preview.employee_no_conflicts.length === 1 ? '' : 's'} (the rest of the row still imports):
              {' '}{preview.employee_no_conflicts.slice(0, 8).map(c => `${c.name} (#${c.employee_no})`).join(', ')}
              {preview.employee_no_conflicts.length > 8 ? ', …' : ''}
            </div>
          )}
        </div>
      )}

      {result && (
        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 6, background: '#ECFDF5', border: '1px solid #A7F3D0', fontSize: 13 }}>
          ✓ Imported <strong>{result.inserted}</strong> employee{result.inserted === 1 ? '' : 's'}
          {result.backfilled ? <> · filled blank fields on {result.backfilled} existing</> : null}
          {result.employee_no_conflicts?.length ? <> · {result.employee_no_conflicts.length} employee number{result.employee_no_conflicts.length === 1 ? '' : 's'} skipped (duplicated in file)</> : null}
          {result.skipped_existing ? <> · skipped {result.skipped_existing} already on roster</> : null}.
          {' '}<button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px', marginLeft: 6 }} onClick={reset}>Done</button>
        </div>
      )}
    </div>
  );
}

function EmployeeForm({ employee, onChange, onSave, onCancel }) {
  const set = (k, v) => onChange({ ...employee, [k]: v });
  // Same 1.5× auto-derive on the employee's default OT as the Allocation
  // form. Roster sets the baseline; per-job allocations can override.
  const setPayST = (v) => {
    const num = parseFloat(v);
    const ot = isFinite(num) ? (num * 1.5).toFixed(2) : '';
    onChange({ ...employee, pay_st_default: v, pay_ot_default: ot });
  };
  // A deactivated employee's record is sealed server-side (assertEmployeeEditable
  // in workforce.js) — the form mirrors that read-only state client-side. The
  // Active checkbox is sealed too; reactivate from the roster row's Status
  // chip instead (one click, no rehire prompt needed on the way back in).
  const sealed = !!employee.id && !employee.is_active;
  return (
    <div style={styles.card}>
      <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>{employee.id ? 'Edit Employee' : 'New Employee'}</h3>
      {sealed && (
        <div style={{ background: '#FEF2F2', border: `1px solid ${colors.red}`, borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#991B1B', marginBottom: 10 }}>
          🔒 This employee is deactivated — the record is sealed read-only. Reactivate from the Status column on the Roster row to edit again.
        </div>
      )}
      <fieldset disabled={sealed} style={{ border: 'none', margin: 0, padding: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
        <Field label="Full Name *"><input style={styles.input} value={employee.full_name || ''} onChange={e => set('full_name', e.target.value)} /></Field>
        <Field label="Employee #"><input style={styles.input} value={employee.employee_no || ''} onChange={e => set('employee_no', e.target.value)} /></Field>
        <Field label="Type">
          <select style={styles.input} value={employee.type || ''} onChange={e => set('type', e.target.value)}>
            <option value="">—</option>
            <option>Full-Time</option>
            <option>Part-Time</option>
            <option>Contractor</option>
            <option>Subcontractor</option>
          </select>
        </Field>
        <Field label="Craft"><input style={styles.input} value={employee.craft || ''} onChange={e => set('craft', e.target.value)} /></Field>
        <Field label="Email"><input type="email" style={styles.input} value={employee.email || ''} onChange={e => set('email', e.target.value)} /></Field>
        <Field label="Phone"><input style={styles.input} value={employee.phone || ''} onChange={e => set('phone', e.target.value)} /></Field>
        <Field label="Mobile (SMS)"><input style={styles.input} value={employee.mobile || ''} onChange={e => set('mobile', e.target.value)} placeholder="for compliance texts" /></Field>
        <Field label="SMS Opt-In">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8 }}>
            <input type="checkbox" checked={!!employee.sms_opt_in} onChange={e => set('sms_opt_in', e.target.checked)} /> Consented to texts
          </label>
        </Field>
        <Field label="Default Pay ST / hr"><MoneyInput value={employee.pay_st_default} onChange={e => setPayST(e.target.value)} /></Field>
        <Field label="Default Pay OT / hr (auto: 1.5× ST)"><MoneyInput value={employee.pay_ot_default} onChange={e => set('pay_ot_default', e.target.value)} /></Field>
        <Field label="Paylocity Status">
          <select style={styles.input} value={employee.paylocity_status || ''} onChange={e => set('paylocity_status', e.target.value)}>
            <option value="">—</option>
            <option>Active</option>
            <option>Pending</option>
            <option>Not Set Up</option>
          </select>
        </Field>
        {/* Other names this person appears under in name-only imports (Safety
            Council export, DISA, ISN) so the upload files certs here instead
            of adding a duplicate person. Comma-separated. */}
        <Field label="Also Listed As (imports)"><input style={styles.input} value={Array.isArray(employee.match_aliases) ? employee.match_aliases.join(', ') : (employee.match_aliases || '')} onChange={e => set('match_aliases', e.target.value)} placeholder="e.g. George J Smith III" /></Field>
        <Field label="Emergency Contact"><input style={styles.input} value={employee.emergency_contact_name || ''} onChange={e => set('emergency_contact_name', e.target.value)} /></Field>
        <Field label="Emergency Phone"><input style={styles.input} value={employee.emergency_contact_phone || ''} onChange={e => set('emergency_contact_phone', e.target.value)} /></Field>
        {/* Home address — crosses to the Field Services crew roster so the PM
            can bill mileage. Auto-fillable from the DL scan via 📂 Docs. */}
        <Field label="Home Address"><input style={styles.input} value={employee.address_line1 || ''} onChange={e => set('address_line1', e.target.value)} placeholder="street address" /></Field>
        <Field label="Apt / Unit"><input style={styles.input} value={employee.address_line2 || ''} onChange={e => set('address_line2', e.target.value)} /></Field>
        <Field label="City"><input style={styles.input} value={employee.city || ''} onChange={e => set('city', e.target.value)} /></Field>
        <Field label="State"><input style={styles.input} maxLength={10} value={employee.state || ''} onChange={e => set('state', e.target.value.toUpperCase())} placeholder="TX" /></Field>
        <Field label="Zip"><input style={styles.input} maxLength={15} value={employee.zip || ''} onChange={e => set('zip', e.target.value)} /></Field>
        <Field label="Active">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8 }}>
            <input type="checkbox" checked={!!employee.is_active} onChange={e => set('is_active', e.target.checked)} /> Active
          </label>
        </Field>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Notes"><textarea style={{ ...styles.input, minHeight: 50 }} value={employee.notes || ''} onChange={e => set('notes', e.target.value)} /></Field>
        </div>
      </div>
      </fieldset>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button style={styles.btnSecondary} onClick={onCancel}>Cancel</button>
        {!sealed && <button style={styles.btn} onClick={onSave}>{employee.id ? 'Save' : 'Create'}</button>}
      </div>
    </div>
  );
}

// ===== Crafts ============================================================

function CraftsTab() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [rolled, toggleRolled] = useRollup('crafts');

  const reload = () => api.listCrafts().then(setRows).catch(e => setError(e.message));
  useEffect(() => { reload(); }, []);

  const startCreate = () => { setEditing({ name: '', sort_order: (rows.length + 1) * 10, is_active: true }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const startEdit = (c) => { setEditing(c); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const save = async () => {
    setError('');
    try {
      if (editing.id) await api.updateCraft(editing.id, editing);
      else await api.createCraft(editing);
      setEditing(null);
      reload();
    } catch (err) { setError(err.message); }
  };
  const remove = async (id, name) => {
    if (!confirm(`Delete craft "${name}"?`)) return;
    try { await api.deleteCraft(id); reload(); }
    catch (err) { setError(err.message); }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <RollupH2 collapsed={rolled} onToggle={toggleRolled} style={{ margin: 0 }}>Crafts <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>· {rows.length}</span></RollupH2>
        <button style={styles.btn} onClick={startCreate}>+ Add Craft</button>
      </div>
      {error && <div style={styles.errorBox}>{error}</div>}
      {!rolled && <>
      {editing && (
        <div style={styles.card}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto auto', gap: 8, alignItems: 'end' }}>
            <Field label="Craft Name *"><input style={styles.input} value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} /></Field>
            <Field label="Sort Order"><input type="number" style={styles.input} value={editing.sort_order} onChange={e => setEditing({ ...editing, sort_order: e.target.value })} /></Field>
            <Field label="Active"><label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8 }}><input type="checkbox" checked={!!editing.is_active} onChange={e => setEditing({ ...editing, is_active: e.target.checked })} /> Active</label></Field>
            <button style={styles.btn} onClick={save}>{editing.id ? 'Save' : 'Create'}</button>
            <button style={styles.btnSecondary} onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      )}
      <div style={styles.card}>
        <table style={{ ...styles.table, fontSize: 13 }}>
          <thead><tr>
            <th style={{ ...styles.th, width: 80 }}>Order</th>
            <th style={styles.th}>Craft</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}></th>
          </tr></thead>
          <tbody>
            {rows.map(c => (
              <tr key={c.id} style={!c.is_active ? { opacity: 0.5 } : {}}>
                <td style={styles.td}>{c.sort_order}</td>
                <td style={{ ...styles.td, fontWeight: 600 }}>{c.name}</td>
                <td style={styles.td}>{c.is_active ? '✓ Active' : 'Inactive'}</td>
                <td style={styles.td}>
                  <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 8px' }} onClick={() => startEdit(c)}>Edit</button>
                  {' '}
                  <button style={{ ...styles.btnDanger, fontSize: 11, padding: '4px 8px' }} onClick={() => remove(c.id, c.name)}>Del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>}
    </>
  );
}

// ===== ISNetworld (client-level compliance) ==============================
// One row per hiring client SSS is connected to in ISN. Tracks SSS's standing
// with that client: connection/enrollment, overall grade, RAVS review status,
// the client's required safety-stat thresholds (TRIR/EMR/DART), and enrollment
// + next-review/expiration dates. Manual entry; admin/leadership can edit,
// ops/supervisor read-only.

const ISN_CONNECTION_OPTS = ['Connected', 'Pending', 'Not Connected'];
// ISN_GRADE_OPTS / ISN_RAVS_OPTS / isnGradeColor removed 2026-07-30 — the
// manual Grade/RAVS inputs they served are gone (grades are scraped now).

function IsnTab() {
  const { user } = useAuth();
  const canEdit = isAdminLike(user);
  // Two levels of ISN data live under this tab and they answer different
  // questions: 'clients' = SSS's company standing with each hiring client
  // (manual entry, below); 'training' = the per-EMPLOYEE training assignments
  // imported from ISN's Online Training Analytics export (2026-07-29).
  const [view, setView] = useState('clients');
  const [rows, setRows] = useState([]);
  // The hiring-client strings actually present in the ISN training feed, so
  // linking a CRM customer to ISN is a pick, not a retype.
  const [hcOptions, setHcOptions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [gradeDetail, setGradeDetail] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rolledClients, toggleClients] = useRollup('isn-clients');
  const [rolledReqs, toggleReqs] = useRollup('isn-requirements');

  // What ISN actually requires, grouped by its own Requirement column (the
  // site / worker-group), so a multi-site client like CVR isn't flattened.
  const [groups, setGroups] = useState(null);

  const reload = () => api.isnList()
    .then(d => { setRows(d.rows || []); setHcOptions(d.hiring_client_options || []); })
    .catch(e => setError(e.message));
  useEffect(() => { reload(); }, []);
  useEffect(() => { api.isnRequirementGroups().then(d => setGroups(d.groups || [])).catch(() => {}); }, []);

  const blank = {
    client_name: '', customer_id: '', isn_hiring_client: '', connection_status: '', grade: '', ravs_status: '',
    threshold_trir: '', threshold_emr: '', threshold_dart: '', enrolled_on: '', expires_on: '', notes: '',
  };
  // The customer-link dropdown's list, loaded on first Add / Edit. bee3b17
  // deleted this line and left both callers: every Add / Edit click threw a
  // ReferenceError and no form opened.
  const ensureCustomers = () => { if (!customers.length) api.listCustomers().then(setCustomers).catch(() => {}); };
  const startCreate = () => { ensureCustomers(); setEditing({ ...blank }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const startEdit = (r) => {
    ensureCustomers();
    setEditing({
      ...r,
      customer_id: r.customer_id || '',
      isn_hiring_client: r.isn_hiring_client || '',
      enrolled_on: r.enrolled_on ? r.enrolled_on.slice(0, 10) : '',
      expires_on: r.expires_on ? r.expires_on.slice(0, 10) : '',
      threshold_trir: r.threshold_trir ?? '', threshold_emr: r.threshold_emr ?? '', threshold_dart: r.threshold_dart ?? '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const set = (k, v) => setEditing(e => ({ ...e, [k]: v }));
  const save = async () => {
    if (!editing.client_name.trim()) { setError('Client name is required.'); return; }
    setError(''); setSaving(true);
    try {
      if (editing.id) await api.isnUpdate(editing.id, editing);
      else await api.isnCreate(editing);
      setEditing(null);
      reload();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };
  const remove = async (r) => {
    if (!confirm(`Delete ISN record for "${r.client_name}"?`)) return;
    try { await api.isnDelete(r.id); reload(); }
    catch (err) { setError(err.message); }
  };

  const subTab = (v, label) => (
    <button
      key={v}
      onClick={() => setView(v)}
      style={{
        ...styles.btnSecondary, fontSize: 12, padding: '5px 12px',
        ...(view === v ? { background: colors.charcoal, color: '#fff', borderColor: colors.charcoal } : {}),
      }}
    >{label}</button>
  );

  return (
    <>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {subTab('clients', '🏭 Client Standing')}
        {subTab('training', '🎓 Worker Training')}
        {subTab('requirements', '📋 What ISN Requires')}
        {subTab('grades', '🏅 Compliance Grades')}
        {/* Straight to the ISN manual — the tabs carry a lot of rules
            (per-site grades, the gate rule, coverage limits) that are worth
            reading once. */}
        <Link to="/manuals?m=isnetworld" title="Full guide to these tabs"
          style={{ ...styles.btnSecondary, fontSize: 12, padding: '5px 12px',
                   marginLeft: 'auto', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
          📖 ISN Guide
        </Link>
      </div>

      {view === 'grades' && <IsnGrades />}

      {view === 'requirements' && (
        <>
          <RollupH2 collapsed={rolledReqs} onToggle={toggleReqs}>What ISN Requires
            <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
              · {(groups || []).length} group{(groups || []).length === 1 ? '' : 's'}
            </span>
          </RollupH2>
          {!rolledReqs && <>
          <div style={{ ...styles.card, padding: '10px 14px', background: '#EFF6FF', border: '1px solid #BFDBFE', marginBottom: 12, fontSize: 12, color: '#1E3A8A' }}>
            💡 The classes each hiring client actually assigns, grouped by ISN's own <strong>Requirement</strong> — its site or worker-group (Lima, East Dubuque, All Workers…).
            <strong>Nothing to load — these ARE the requirements.</strong> Compliance reads this feed live, so at job setup a crew member with
            <strong> no ISN assignment</strong> for one of these classes reads “NOT assigned to this worker in ISNetworld — assign it in ISN”.
            Which group applies is decided by the crew: whichever group your allocated people are already assigned in is the site they are working,
            so a multi-site client like CVR never demands Wynnewood classes of an East Dubuque crew. On a brand-new job where nobody is assigned yet,
            every group at that client is listed and the reason line says so.
            <strong> Coverage</strong> is the share of that group's workers who already hold the class.
            <div style={{ marginTop: 6, color: '#92400E' }}>
              ⚠ <strong>This is not every ISN site.</strong> It is built from the training export, which only reports clients where SSS already has
              workers ASSIGNED in ISN — so a site we are connected to but have not staffed yet shows nothing here. Compliance Grades is the complete
              list of connected sites; if one is missing from this tab, assign a worker to it in ISN and it will appear on the next sync.
            </div>
          </div>
          {!groups && <div style={styles.card}>Loading…</div>}
          {groups && !groups.length && <div style={styles.card}>No ISN training data yet — import an export on the Worker Training tab.</div>}
          {(groups || []).map(g => (
            <div key={`${g.hiring_client}|${g.requirement}`} style={{ ...styles.card, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <strong>{g.hiring_client}</strong>
                  <span style={{ color: colors.textMuted }}> · requirement “{g.requirement}” · {g.workers} worker{g.workers === 1 ? '' : 's'} in the feed</span>
                  {g.customer_name
                    ? <span style={{ color: colors.green, fontSize: 12 }}> · 🔗 {g.customer_name}</span>
                    : <span style={{ color: colors.orange, fontSize: 12 }}> · not linked to a CRM customer — set “ISN Hiring Client” on Client Standing so compliance can use it</span>}
                </div>
              </div>
              <ul style={{ margin: '8px 0 0 18px', padding: 0, fontSize: 13 }}>
                {g.classes.map(cl => (
                  <li key={cl.training_name}>
                    {cl.training_name}
                    <span style={{ color: cl.coverage === 100 ? colors.green : colors.orange, fontSize: 11 }}>
                      {' '}· {cl.coverage}% ({cl.have}/{g.workers})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          </>}
        </>
      )}

      {view === 'training' && <IsnWorkerTraining />}
      {view === 'clients' && (
      <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <RollupH2 collapsed={rolledClients} onToggle={toggleClients} style={{ margin: 0 }}>
          ISNetworld <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>· {rows.length} client{rows.length === 1 ? '' : 's'}</span>
        </RollupH2>
        {canEdit && <button style={styles.btn} onClick={startCreate}>+ Add Client</button>}
      </div>
      {error && <div style={styles.errorBox}>{error}</div>}
      {!rolledClients && <>
      <div style={{ ...styles.card, padding: '10px 14px', background: '#EFF6FF', border: '1px solid #BFDBFE', marginBottom: 12, fontSize: 12, color: '#1E3A8A' }}>
        💡 SSS's ISNetworld standing per hiring client. <strong>Grade and RAVS are scraped from ISN daily — nothing here is typed.</strong>
        <div style={{ marginTop: 6 }}>
          <strong>Grade</strong> comes straight from ISN and is set <strong>per site</strong>, not per company. The column shows that client's
          <strong> lowest</strong> site grade, because one failing site is what stops a crew at the gate — <strong>click it</strong> for the site-by-site breakdown.
          Grades are not one scale:
          <span style={{ color: colors.green }}> A / B / Approved / Preferred / Excellent / Acceptable / Safety Status Met = in good standing</span>;
          <span style={{ color: colors.orange }}> C / Evaluate / Cautionary = needs attention</span>;
          <span style={{ color: colors.red }}> F / Not Approved = failing</span>;
          <span style={{ color: colors.textMuted }}> Not Shared / Not Available = ISN is not publishing one, which is not a pass or a fail.</span>
        </div>
      </div>

      {gradeDetail && (() => {
        const live = gradeDetail.live_grades || [];
        const bad = live.filter(g => isnGradeTone(g.grade).rank <= 1);
        return (
          <div onClick={() => setGradeDetail(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 900,
                     display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 40, overflowY: 'auto' }}>
            <div onClick={e => e.stopPropagation()} style={{ ...styles.card, maxWidth: 720, width: '100%' }}>
              <h2 style={{ ...styles.h2, marginTop: 0 }}>{gradeDetail.isn_hiring_client || gradeDetail.client_name}</h2>
              <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 10 }}>
                ISN grades this client separately at each site. The Client Standing column shows the lowest of these.
              </div>

              {bad.length ? (
                <div style={{ ...styles.card, padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FCA5A5', marginBottom: 12, fontSize: 12, color: '#991B1B' }}>
                  ⚠ <strong>{bad.length} site{bad.length === 1 ? '' : 's'} below standard.</strong> The grade itself is set by ISN from SSS's
                  safety statistics, RAVS review and insurance for that site — fix it in ISNetworld under that client, not here.
                </div>
              ) : (
                <div style={{ ...styles.card, padding: '10px 14px', background: '#ECFDF5', border: '1px solid #A7F3D0', marginBottom: 12, fontSize: 12, color: '#065F46' }}>
                  ✅ Every site is in good standing with this client.
                </div>
              )}

              <table style={styles.table}>
                <thead><tr><th style={styles.th}>Site</th><th style={styles.th}>Grade</th><th style={styles.th}>Since</th></tr></thead>
                <tbody>
                  {live.map(g => (
                    <tr key={g.site_name}>
                      <td style={styles.td}>{g.site_name || '—'}</td>
                      <td style={{ ...styles.td, color: isnGradeTone(g.grade).color, fontWeight: 600 }}>{g.grade || '—'}</td>
                      <td style={{ ...styles.td, fontSize: 12 }}>{g.graded_on ? fmtDate(g.graded_on) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* What the CRM itself can act on, as opposed to the ISN-side grade. */}
              <div style={{ marginTop: 14, fontSize: 13 }}>
                <strong>What we can fix from here</strong>
                <div style={{ marginTop: 4, color: gradeDetail.open_items ? colors.red : colors.green }}>
                  {gradeDetail.open_items
                    ? `${gradeDetail.open_items} outstanding ISN training item${gradeDetail.open_items === 1 ? '' : 's'} across ${gradeDetail.open_workers} worker${gradeDetail.open_workers === 1 ? '' : 's'} — see the Worker Training tab.`
                    : 'No outstanding ISN training for this client.'}
                </div>
                <div style={{ marginTop: 6, fontSize: 12, color: colors.textMuted }}>
                  Worker training is ours to close. The site grade is ISN's own scoring of SSS — it moves when the
                  underlying safety stats, RAVS review or insurance are updated in ISNetworld.
                </div>
              </div>

              <div style={{ marginTop: 14, textAlign: 'right' }}>
                <button style={styles.btn} onClick={() => setGradeDetail(null)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {editing && (
        <div style={styles.card}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
            <Field label="Client Name *"><input style={styles.input} value={editing.client_name} onChange={e => set('client_name', e.target.value)} placeholder="Hiring client / operator" /></Field>
            <Field label="CRM Customer (optional)">
              <select style={styles.input} value={editing.customer_id} onChange={e => set('customer_id', e.target.value)}>
                <option value="">— Not linked —</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="ISN Hiring Client (training feed)">
              <select style={styles.input} value={editing.isn_hiring_client || ''} onChange={e => set('isn_hiring_client', e.target.value)}>
                <option value="">— match by name —</option>
                {hcOptions.map(o => (
                  <option key={o.hiring_client} value={o.hiring_client}>
                    {o.hiring_client} ({o.workers} workers)
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Connection">
              <input style={styles.input} list="isn-conn-opts" value={editing.connection_status || ''} onChange={e => set('connection_status', e.target.value)} placeholder="Connected…" />
              <datalist id="isn-conn-opts">{ISN_CONNECTION_OPTS.map(o => <option key={o} value={o} />)}</datalist>
            </Field>
            {/* Grade / RAVS / thresholds inputs REMOVED (George 2026-07-30:
                "get rid of any manual stuff because it will never get done").
                Grade and RAVS are scraped; a typed value here saved into
                columns nothing renders — a silent black hole. */}
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Notes"><textarea style={{ ...styles.input, minHeight: 56, resize: 'vertical' }} value={editing.notes || ''} onChange={e => set('notes', e.target.value)} /></Field>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button style={styles.btn} onClick={save} disabled={saving}>{saving ? 'Saving…' : (editing.id ? 'Save' : 'Create')}</button>
            <button style={styles.btnSecondary} onClick={() => { setEditing(null); setError(''); }} disabled={saving}>Cancel</button>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <table style={{ ...styles.table, fontSize: 13 }}>
          <thead><tr>
            <th style={styles.th}>Client</th>
            <th style={styles.th}>Connection</th>
            <th style={styles.th}>Grade</th>
            <th style={styles.th}>ISN FEED &amp; LIVE GRADE</th><th style={styles.th}>RAVS</th>
            <th style={styles.th}>Notes</th>
            {canEdit && <th style={styles.th}></th>}
          </tr></thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={canEdit ? 7 : 6} style={{ ...styles.td, textAlign: 'center', color: '#6B7280' }}>
                No ISN clients yet.{canEdit ? ' Click + Add Client.' : ''}
              </td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id}>
                {/* No expiryNameStyle here: expires_on has no visible column
                    or edit field anymore, so a tint from it would be a red
                    name nothing on the tab explains (2026-07-30 audit). */}
                <td style={{ ...styles.td, fontWeight: 600 }}>
                  {r.client_name}
                  {r.customer_name && <div style={{ fontSize: 11, fontWeight: 400, color: '#6B7280' }}>↳ {r.customer_name}</div>}
                </td>
                <td style={styles.td}>{r.connection_status || '—'}</td>
                {/* LIVE grade from ISN, falling back to the hand-typed one.
                    A client is only as good as its WORST site, so that is the
                    grade shown — an average or a first-row pick would hide
                    exactly the site that stops a crew at the gate. Clickable:
                    a red grade you cannot open is not worth showing. */}
                <td style={styles.td}>
                  {(() => {
                    const live = r.live_grades || [];
                    if (!live.length) {
                      return <span style={{ color: colors.textMuted }}
                        title="No grade in ISN's feed for this client yet — pin the ISN Hiring Client so the scrape can match it">—</span>;
                    }
                    const worst = [...live].sort(
                      (a, b) => isnGradeTone(a.grade).rank - isnGradeTone(b.grade).rank)[0];
                    const tone = isnGradeTone(worst.grade);
                    const needs = live.filter(g => isnGradeTone(g.grade).rank <= 1).length;
                    return (
                      <button
                        onClick={() => setGradeDetail(r)}
                        title={`ISN grades this client per SITE. Showing the lowest of ${live.length} site${live.length === 1 ? '' : 's'} — click for the breakdown.`}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
                        <span style={{ fontWeight: 600, color: tone.color, textDecoration: 'underline' }}>
                          {worst.grade}
                        </span>
                        <div style={{ fontSize: 10, color: needs ? colors.red : colors.textMuted }}>
                          {live.length === 1
                            ? worst.site_name
                            : `lowest of ${live.length} sites${needs ? ` · ${needs} need attention` : ''}`}
                        </div>
                      </button>
                    );
                  })()}
                </td>
                <td style={styles.td}>
                  {r.isn_hiring_client
                    ? <span style={{ fontSize: 11, color: colors.green }} title="Exactly matched to this string in the ISN training feed">🔗 {r.isn_hiring_client}</span>
                    : <span style={{ fontSize: 11, color: colors.textMuted }} title="No exact link — falling back to name matching">matched by name</span>}
                </td>
                {/* RAVS review status, live from ISN. Was hand-typed and blank. */}
                <td style={styles.td}>
                  {r.live_ravs
                    ? <span title={r.live_ravs.ravs_date ? `Reviewed ${r.live_ravs.ravs_date}` : undefined}
                        style={{ fontWeight: 600, color: /deficien|fail|expired/i.test(r.live_ravs.ravs_status || '') ? colors.red : colors.green }}>
                        {r.live_ravs.ravs_status}
                      </span>
                    : <span style={{ color: colors.textMuted }} title="ISN has not published a RAVS status for this client">—</span>}
                </td>
                <td style={{ ...styles.td, maxWidth: 220, fontSize: 12, color: '#6B7280', whiteSpace: 'pre-wrap' }}>{r.notes || ''}</td>
                {canEdit && (
                  <td style={styles.td}>
                    <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 8px' }} onClick={() => startEdit(r)}>Edit</button>
                    {' '}
                    <button style={{ ...styles.btnDanger, fontSize: 11, padding: '4px 8px' }} onClick={() => remove(r)}>Del</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>}
      </>
      )}
    </>
  );
}

// ===== ISNetworld — SSS's compliance grade per client, per site ==========
// Scraped from ISN's My Clients page. The Grade column on Client Standing is
// hand-typed and was blank for 5 of 6 clients; this is the live answer to "are
// we currently compliant", and it covers every connected client (73 rows),
// not just the handful with assigned workers.

// ISN grades are not a single scale — A/B/C/F sit alongside "Approved",
// "Preferred", "Excellent", "Safety Status Met", "Evaluate - Level 2",
// "SubTracker - Cautionary", "Not Shared". Classify on meaning, not on a
// letter, and treat anything unrecognised as neutral rather than guessing.
const isnGradeTone = (g) => {
  const s = String(g || '').trim().toLowerCase();
  if (!s) return { color: colors.textMuted, rank: 3 };
  if (/^f\b|not approved|non-compliant|suspend|denied/.test(s)) return { color: colors.red, rank: 0 };
  if (/^c\b|cautionary|evaluate|conditional|pending|review/.test(s)) return { color: colors.orange, rank: 1 };
  if (/^a\b|^b\b|approved|preferred|excellent|acceptable|accepted|met|recommended|connected|green/.test(s)) return { color: colors.green, rank: 2 };
  return { color: colors.textMuted, rank: 3 };
};

function IsnGrades() {
  const [detail, setDetail] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [only, setOnly] = useState('all');
  const [rolled, toggleRolled] = useRollup('isn-grades');

  useEffect(() => { api.isnGrades().then(setData).catch(e => setError(e.message)); }, []);

  const all = data?.rows || [];
  const rows = all.filter(r => {
    const t = isnGradeTone(r.grade);
    if (only === 'problem' && t.rank > 1) return false;
    if (only === 'good' && t.rank !== 2) return false;
    if (!q.trim()) return true;
    const n = q.trim().toLowerCase();
    return [r.hiring_client, r.site_name, r.grade, r.customer_name]
      .some(v => String(v || '').toLowerCase().includes(n));
  });
  const problems = all.filter(r => isnGradeTone(r.grade).rank <= 1).length;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <RollupH2 collapsed={rolled} onToggle={toggleRolled} style={{ margin: 0 }}>
          Compliance Grades
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
            · {rows.length} of {all.length} shown
            {problems ? (
              <button onClick={e => { e.stopPropagation(); setOnly(only === 'problem' ? 'all' : 'problem'); }}
                title="Show only the sites below standard"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                         color: colors.red, font: 'inherit', textDecoration: 'underline' }}>
                {' · '}{problems} needing attention
              </button>
            ) : null}
          </span>
        </RollupH2>
      </div>
      {error && <div style={styles.errorBox}>{error}</div>}
      {!rolled && <>
      <div style={{ ...styles.card, padding: '10px 14px', background: '#EFF6FF', border: '1px solid #BFDBFE', marginBottom: 12, fontSize: 12, color: '#1E3A8A' }}>
        💡 SSS's standing with every connected hiring client, <strong>per site</strong>, straight from ISN's My Clients page — refreshed by the daily scrape, not typed.
        Grades are not one scale: A/B/C/F sit next to “Approved”, “Preferred”, “Safety Status Met”, “Evaluate – Level 2”. Anything unrecognised is left neutral rather than guessed at.
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <select style={{ ...styles.input, width: 210 }} value={only} onChange={e => setOnly(e.target.value)}>
          <option value="all">All grades</option>
          <option value="problem">Needing attention (F / C / cautionary)</option>
          <option value="good">In good standing</option>
        </select>
        <input style={{ ...styles.input, width: 260 }} placeholder="Search client / site…"
          value={q} onChange={e => setQ(e.target.value)} />
      </div>

      {detail && (
        <div onClick={() => setDetail(null)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:900,
                   display:'flex', alignItems:'flex-start', justifyContent:'center', padding:40, overflowY:'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ ...styles.card, maxWidth:680, width:'100%' }}>
            <h2 style={{ ...styles.h2, marginTop:0 }}>{detail.hiring_client}</h2>
            <div style={{ color: colors.textMuted, fontSize:13, marginBottom:10 }}>{detail.site_name}</div>
            <div style={{ fontSize:22, fontWeight:700, color: isnGradeTone(detail.grade).color }}>
              {detail.grade}
              <span style={{ fontSize:12, fontWeight:400, color: colors.textMuted }}>
                {detail.graded_on ? `  ·  set ${fmtDate(detail.graded_on)}` : ''}
              </span>
            </div>

            <div style={{ marginTop: 14, fontSize: 13 }}><strong>What we can see</strong></div>
            <table style={{ ...styles.table, marginTop: 6 }}>
              <tbody>
                <tr>
                  <td style={styles.td}>Requirement gaps</td>
                  <td style={{ ...styles.td, fontWeight:600,
                       color: detail.has_gaps === true ? colors.red
                            : detail.has_gaps === false ? colors.green : colors.textMuted }}>
                    {detail.has_gaps === true ? 'YES — ISN shows unmet requirements at this site'
                      : detail.has_gaps === false ? 'none' : 'unknown'}
                  </td>
                </tr>
                <tr>
                  <td style={styles.td}>Blocked from site</td>
                  <td style={{ ...styles.td, fontWeight:600,
                       color: detail.is_blocked ? colors.red : colors.green }}>
                    {detail.is_blocked ? 'YES' : 'no'}
                  </td>
                </tr>
                <tr>
                  <td style={styles.td}>RAVS review</td>
                  <td style={{ ...styles.td, fontWeight:600,
                       color: /deficien|fail|expired/i.test(detail.ravs_status || '') ? colors.red
                            : detail.ravs_status ? colors.green : colors.textMuted }}>
                    {detail.ravs_status || 'no RAVS status published'}
                    {detail.ravs_date ? ` · ${fmtDate(detail.ravs_date)}` : ''}
                  </td>
                </tr>
                <tr>
                  <td style={styles.td}>Outstanding ISN training</td>
                  <td style={{ ...styles.td, fontWeight:600, color: detail.open_items ? colors.red : colors.green }}>
                    {detail.open_items
                      ? `${detail.open_items} item(s) across ${detail.open_workers} worker(s)`
                      : 'none'}
                  </td>
                </tr>
                <tr>
                  <td style={styles.td}>CRM customer</td>
                  <td style={styles.td}>{detail.customer_name || 'not linked'}</td>
                </tr>
              </tbody>
            </table>

            {/deficien|fail|expired/i.test(detail.ravs_status || '') && (
              <div style={{ ...styles.card, padding:'10px 14px', background:'#FEF2F2', border:'1px solid #FCA5A5',
                            marginTop:12, fontSize:12, color:'#991B1B' }}>
                ⚠ RAVS shows <strong>{detail.ravs_status}</strong> for this client. A RAVS deficiency drags the grade —
                this is very likely the reason, and it is fixed by clearing the deficiency in ISNetworld's RAVS module.
              </div>
            )}

            <div style={{ ...styles.card, padding:'10px 14px', background:'#F3F4F6', border:'1px solid #E5E7EB',
                          marginTop:12, fontSize:12, color:'#374151' }}>
              <strong>How to read this.</strong> “Requirement gaps” is ISN's own flag that something is unmet at this
              site — it is the closest thing ISN publishes to a reason, and it is what to chase first. The itemised
              list of which requirements are short still lives behind that site's page in ISNetworld. Grades themselves
              are driven by SSS's safety statistics (TRIR / EMR / DART), the RAVS review and insurance certificates for
              that client, all maintained in ISNetworld, not here.
            </div>

            <div style={{ marginTop:14, textAlign:'right' }}>
              <button style={styles.btn} onClick={() => setDetail(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Hiring Client</th>
              <th style={styles.th}>Site</th>
              <th style={styles.th}>Grade</th>
              <th style={styles.th}>Since</th>
              <th style={styles.th}>CRM Customer</th>
            </tr>
          </thead>
          <tbody>
            {!rows.length && (
              <tr><td style={styles.td} colSpan={5}>
                {data ? 'Nothing matches — or the grades scrape has not run yet.' : 'Loading…'}
              </td></tr>
            )}
            {rows.map(r => {
              const tone = isnGradeTone(r.grade);
              return (
                <tr key={`${r.hiring_client}|${r.site_name}`}>
                  <td style={styles.td}>{r.hiring_client}</td>
                  <td style={{ ...styles.td, fontSize: 12 }}>{r.site_name || '—'}</td>
                  <td style={styles.td}>
                    <button onClick={() => setDetail(r)} title="What we know about this grade"
                      style={{ background:'none', border:'none', padding:0, cursor:'pointer', font:'inherit',
                               color: tone.color, fontWeight:600, textDecoration:'underline' }}>
                      {r.grade || '—'}
                    </button>
                  </td>
                  <td style={{ ...styles.td, fontSize: 12 }}>{r.graded_on ? fmtDate(r.graded_on) : '—'}</td>
                  <td style={{ ...styles.td, fontSize: 12, color: r.customer_name ? colors.text : colors.textMuted }}>
                    {r.customer_name || 'not linked'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </>}
    </>
  );
}

// ===== ISNetworld — per-employee training assignments ====================
// The employee-level half of the ISN tab (2026-07-29). Source: the ISN
// "Online Training Analytics → Export Details" workbook, imported via
// services/isnImport.js.
//
// Why this exists: employee_training only holds COMPLETED certs. It has
// nowhere to put "assigned at Cenovus Lima, not started" — which is exactly
// the row that gets a man turned away at the gate. Defaults to the OPEN items
// for that reason; the full list is one click away.

function IsnWorkerTraining() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('open');
  const [client, setClient] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Bumped after a successful import so the status badge refetches.
  const [uploadedAt, setUploadedAt] = useState(0);
  const [rolled, toggleRolled] = useRollup('isn-training');

  const reload = () => api.isnAssignments({ status, client: client || undefined })
    .then(setData).catch(e => setError(e.message));
  useEffect(() => { reload(); }, [status, client]);

  const doUpload = async (file) => {
    if (!file) return;
    setError(''); setResult(null); setBusy(true);
    try {
      // Dry run first so a wrong file (or a surprise batch of new roster
      // names) is visible BEFORE anything is written — same two-step the
      // roster and requirements importers use.
      const preview = await api.uploadIsnTraining(file, { dry: true });
      const msg = `Import this ISN export?\n\n` +
        `${preview.assignments} assignments across ${preview.workers_in_file} workers\n` +
        `${preview.open_assignments} outstanding (not passed)\n` +
        `${preview.employees_matched} matched to the roster` +
        `${preview.matched_by_isn_number ? ` (${preview.matched_by_isn_number} by ISN number)` : ''}\n` +
        `${preview.employees_added} NEW people would be added to the roster` +
        `${preview.employees_added ? `:\n  • ${preview.to_add.slice(0, 12).map(w => w.worker_name).join('\n  • ')}` : ''}`;
      if (!confirm(msg)) { setBusy(false); return; }
      const r = await api.uploadIsnTraining(file);
      setResult(r);
      setUploadedAt(Date.now());
      reload();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const blockWorker = async (id) => {
    if (!confirm(`Block ${id}?

Its rows are removed now and dropped on every future ISN sync. Nothing else changes. You can undo this from the blocked list.`)) return;
    try {
      const r = await api.blockIsnWorker(id, 'Duplicate ISN worker record');
      setResult({ blocked_note: `Blocked ${id} — removed ${r.purged} row(s).` });
      reload();
    } catch (e) { setError(e.message); }
  };
  const unblockWorker = async (id) => {
    try { await api.unblockIsnWorker(id); reload(); }
    catch (e) { setError(e.message); }
  };

  const rows = (data?.rows || []).filter(r => {
    if (!q.trim()) return true;
    const n = q.trim().toLowerCase();
    return [r.worker_name, r.employee_name, r.training_name, r.hiring_client, r.requirement, r.isn_worker_id]
      .some(v => String(v || '').toLowerCase().includes(n));
  });

  const statusChip = (s) => {
    const v = String(s || '').toLowerCase();
    const c = v === 'pass' ? colors.green : v === 'incomplete' ? colors.orange : colors.red;
    return <span style={{ color: c, fontWeight: 600, fontSize: 12 }}>{s || 'Not Taken'}</span>;
  };

  const totalOpen = (data?.summary || []).reduce((a, s) => a + s.open, 0);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <RollupH2 collapsed={rolled} onToggle={toggleRolled} style={{ margin: 0 }}>
          Worker Training
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
            · {rows.length} shown{totalOpen ? ` · ${totalOpen} outstanding` : ''}
          </span>
        </RollupH2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Shows "Never uploaded" until the first import, then who/when —
              covers BOTH the manual upload and the scraper's automatic sync,
              which records under the same key as "ISN sync (auto)". */}
          <UploadStatusBadge statusKey="last_isn_import" refreshSignal={uploadedAt} />
          <DropZone onFiles={(files) => doUpload(files[0])}>
            <label style={{ ...styles.btn, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
              {busy ? 'Importing…' : '⬆ Upload ISN Export'}
              <input type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} disabled={busy}
                onChange={e => { doUpload(e.target.files[0]); e.target.value = ''; }} />
            </label>
          </DropZone>
        </div>
      </div>

      {!rolled && (
      <div style={{ ...styles.card, padding: '10px 14px', background: '#EFF6FF', border: '1px solid #BFDBFE', marginBottom: 12, fontSize: 12, color: '#1E3A8A' }}>
        💡 Training each hiring client has assigned to an SSS worker in ISNetworld — including the ones <strong>not yet taken</strong>, which is what a completed-certs list can never show you.
        Export it from ISN → ISN Analytics → Online Training Analytics → <strong>Export Details</strong> (leave every filter on All except Employee Active = Active; do <strong>not</strong> filter Training Status), then drop the .xlsx here.
        Passed items also mirror into the roster Training tab as issuer <strong>ISNetworld</strong>.

      </div>
      )}

      {error && <div style={styles.errorBox}>{error}</div>}
      {result?.blocked_note && (
        <div style={{ ...styles.card, padding: '10px 14px', background: '#ECFDF5', border: '1px solid #A7F3D0', marginBottom: 12, fontSize: 12, color: '#065F46' }}>
          ✅ {result.blocked_note}
        </div>
      )}
      {result && !result.blocked_note && (
        <div style={{ ...styles.card, padding: '10px 14px', background: '#ECFDF5', border: '1px solid #A7F3D0', marginBottom: 12, fontSize: 12, color: '#065F46' }}>
          ✅ Imported {result.assignments_upserted} assignments for {result.workers_in_file} workers —
          {' '}{result.employees_matched} matched{result.employees_added ? `, ${result.employees_added} added to the roster` : ''}
          {result.isn_numbers_set ? `, ${result.isn_numbers_set} ISN number(s) filled in on the DISA tab` : ''}.
          {' '}{result.open_assignments} outstanding. Training tab: {result.training_inserted} new, {result.training_updated} refreshed.
          {result.added_names?.length ? <div style={{ marginTop: 4 }}>Added: {result.added_names.join(', ')}</div> : null}
        </div>
      )}

      {!rolled && <>
      {!!data?.duplicates?.length && (
        <div style={{ ...styles.card, padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FCA5A5', marginBottom: 12, fontSize: 12, color: '#991B1B' }}>
          ⚠ <strong>ISNetworld holds more than one worker record for {data.duplicates.length === 1 ? 'this person' : 'these people'}.</strong>{' '}
          Until ISN merges them, the worker has to complete every class twice — and can still be turned away at the gate on whichever ID the client checks. Fix it in ISN, not here.
          <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
            {data.duplicates.map(d => (
              <li key={d.worker_ids.join('|')}>
                <strong>{(d.employee_names || []).join(' / ') || d.worker_names.join(' / ')}</strong>
                {' — '}{d.worker_ids.join(' and ')}
                <span style={{ color: '#7F1D1D' }}> (ISN has them as “{d.worker_names.join('”, “')}”)</span>
                {Number(d.employee_count) > 1 && (
                  <span style={{ color: '#7F1D1D' }}> · <strong>also split across {d.employee_count} roster rows</strong></span>
                )}
                <div style={{ marginTop: 4 }}>
                  {d.worker_ids.map(id => (
                    <button key={id} style={{ ...styles.btnSecondary, fontSize: 11, padding: '2px 8px', marginRight: 6 }}
                      onClick={() => blockWorker(id)}>
                      🚫 Block {id}
                    </button>
                  ))}
                  <span style={{ fontSize: 11, color: '#7F1D1D' }}>
                    Blocking drops that id now and on every future sync — keep the one with the DISA record.
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!!data?.blocked?.length && (
        <div style={{ ...styles.card, padding: '10px 14px', background: '#F3F4F6', border: '1px solid #E5E7EB', marginBottom: 12, fontSize: 12, color: '#374151' }}>
          🚫 <strong>Blocked ISN worker id{data.blocked.length === 1 ? '' : 's'}</strong> — dropped on every sync, never stored.
          {data.blocked.map(b => (
            <div key={b.isn_worker_id} style={{ marginTop: 3 }}>
              <span style={{ fontFamily: 'monospace' }}>{b.isn_worker_id}</span>
              {b.worker_name ? ` · ${b.worker_name}` : ''}{b.reason ? ` · ${b.reason}` : ''}
              <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '1px 7px', marginLeft: 8 }}
                onClick={() => unblockWorker(b.isn_worker_id)}>Unblock</button>
            </div>
          ))}
        </div>
      )}

      {!!data?.unmatched && (
        <div style={{ ...styles.card, padding: '10px 14px', background: '#FEF3C7', border: '1px solid #FBBF24', marginBottom: 12, fontSize: 12, color: '#92400E' }}>
          ⚠ {data.unmatched} assignment row{data.unmatched === 1 ? '' : 's'} could not be matched to a roster employee. They are listed below with a blank Employee — fix the name on the Roster tab, or set the worker's ISN number, and re-import.
        </div>
      )}

      {/* Per-client outstanding counts. Computed over the whole table, so the
          numbers don't move when the filters below change. */}
      {!!data?.summary?.length && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {data.summary.map(s => (
            <button key={s.hiring_client}
              onClick={() => setClient(client === s.hiring_client ? '' : s.hiring_client)}
              style={{
                ...styles.card, padding: '8px 12px', cursor: 'pointer', textAlign: 'left',
                border: client === s.hiring_client ? `2px solid ${colors.orange}` : '1px solid #E5E7EB',
              }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{s.hiring_client}</div>
              <div style={{ fontSize: 11, color: s.open ? colors.red : colors.green }}>
                {s.open ? `${s.open} outstanding · ${s.open_workers} worker${s.open_workers === 1 ? '' : 's'}` : 'all current'}
                <span style={{ color: colors.textMuted }}> · {s.total} total</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
        <select style={{ ...styles.input, width: 190 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="open">Outstanding only</option>
          <option value="all">All assignments</option>
          <option value="Pass">Pass</option>
          <option value="Not Taken">Not Taken</option>
          <option value="Incomplete">Incomplete</option>
          <option value="Fail">Fail</option>
        </select>
        <input style={{ ...styles.input, width: 240 }} placeholder="Search worker / training…"
          value={q} onChange={e => setQ(e.target.value)} />
        {client && (
          <button style={{ ...styles.btnSecondary, fontSize: 12 }} onClick={() => setClient('')}>
            ✕ {client}
          </button>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Employee</th>
              <th style={styles.th}>ISN Worker</th>
              <th style={styles.th}>Hiring Client</th>
              <th style={styles.th}>Requirement</th>
              <th style={styles.th}>Training</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Assigned</th>
              <th style={styles.th}>Completed</th>
              <th style={styles.th}>Expires</th>
            </tr>
          </thead>
          <tbody>
            {!rows.length && (
              <tr><td style={styles.td} colSpan={9}>
                {data ? 'Nothing to show — upload the ISN export, or switch the filter to All assignments.' : 'Loading…'}
              </td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id}>
                <td style={{ ...styles.td, ...expiryNameStyle(r.expiring_date) }}>
                  {r.employee_name || <span style={{ color: colors.red }}>⚠ unmatched</span>}
                  {r.employee_active === false && <span style={{ color: colors.textMuted, fontSize: 11 }}> · inactive</span>}
                </td>
                <td style={{ ...styles.td, fontSize: 12 }}>
                  <div>{r.worker_name}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 11, color: colors.textMuted }}>{r.isn_worker_id}</div>
                </td>
                <td style={{ ...styles.td, fontSize: 12 }}>{r.hiring_client}</td>
                <td style={{ ...styles.td, fontSize: 12 }}>{r.requirement || '—'}</td>
                <td style={{ ...styles.td, fontSize: 12 }}>{r.training_name}</td>
                <td style={styles.td}>{statusChip(r.status)}</td>
                <td style={{ ...styles.td, fontSize: 12 }}>{r.date_assigned ? fmtDate(r.date_assigned) : '—'}</td>
                <td style={{ ...styles.td, fontSize: 12 }}>{r.date_completed ? fmtDate(r.date_completed) : '—'}</td>
                <td style={styles.td}>{expiryChip(r.expiring_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>}
    </>
  );
}

// ===== DISA ==============================================================

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.floor((d - now) / 86400000);
};
const expiryColor = (days) => {
  if (days === null) return colors.textMuted;
  if (days < 0) return colors.red;
  if (days <= 30) return colors.red;
  if (days <= 60) return colors.orange;
  return colors.green;
};
// Name-cell color matching the row's expiry chip: red when expired/≤30d,
// amber when ≤60d, default otherwise — so a hot row reads at a glance.
const expiryNameStyle = (dateStr) => {
  if (!dateStr) return {};
  const c = expiryColor(daysUntil(dateStr));
  return (c === colors.red || c === colors.orange) ? { color: c } : {};
};
// Councils encode "doesn't expire" as issue + 40/50 years (HASC does this on
// site orientations; LINK's SSV rows do the same). A countdown chip on a 2076
// date reads as a bug — show a neutral "No expiry" instead.
const NO_EXPIRY_DAYS = 20 * 365;
const expiryChip = (dateStr) => {
  const days = daysUntil(dateStr);
  if (!dateStr) return <span style={{ color: colors.textMuted, fontSize: 11 }}>—</span>;
  if (days > NO_EXPIRY_DAYS) {
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', fontSize: 11 }}>
        <span style={{ fontWeight: 600, color: colors.textMuted }}>No expiry</span>
        <span style={{ color: colors.textMuted, fontSize: 10 }}>{fmtDate(dateStr)}</span>
      </span>
    );
  }
  const color = expiryColor(days);
  const label = days < 0 ? `Expired ${-days}d` : days === 0 ? 'Expires today' : days <= 60 ? `${days}d left` : `${days}d`;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', fontSize: 11 }}>
      <span style={{ fontWeight: 600 }}>{fmtDate(dateStr)}</span>
      <span style={{ color, fontSize: 10 }}>{label}</span>
    </span>
  );
};

// DISA report freshness banner — the MinisForum task "SSS DISA Daily Pull"
// pulls the saved DISAWorks views to R2 ONCE DAILY at 03:00 and CRM auto-
// imports them; this shows when the report last refreshed. report_modified =
// the R2 file's date = when the pull last succeeded. Green when fresh, red
// when overdue (>27h = one day plus slack, so a single missed daily run trips
// it). NOTE: this is deliberately NOT the Equipment Gantt's 3h stock
// threshold — the RM stock scraper really does run every 2h; DISA does not.
const DISA_STALE_HOURS = 27;
const disaRelAge = (ms) => {
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h}h ${min % 60}m ago`;
  return `${Math.floor(h / 24)}d ago`;
};
function DisaSyncStatus() {
  const [st, setSt] = useState(null);
  useEffect(() => { api.disaReportStatus().then(setSt).catch(() => setSt(false)); }, []);
  if (!st) return null; // loading or endpoint unavailable — stay quiet

  const base = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13, flexWrap: 'wrap' };
  const dot = { width: 8, height: 8, borderRadius: '50%', flexShrink: 0, display: 'inline-block' };
  const mod = st.report_modified ? new Date(st.report_modified).getTime() : null;

  // Never fed yet (scraper not running / no file in R2 / R2 unconfigured).
  if (!mod) {
    return (
      <div style={{ ...base, background: '#FEF3C7', border: '1px solid #FBBF24', color: '#92400E' }}>
        <span style={{ ...dot, background: '#F59E0B' }} />
        <span>
          <strong>DISA auto-sync:</strong> no report pulled from the portal yet
          {st.last_status === 'error' && st.last_error ? ` — ${st.last_error}` : '.'}
          {!st.enabled && ' (auto-sync is OFF)'}
        </span>
      </div>
    );
  }

  const ageMs = Date.now() - mod;
  const overdue = ageMs >= DISA_STALE_HOURS * 3600000 || !st.enabled;
  const bg = overdue ? '#FEE2E2' : '#DCFCE7';
  const bd = overdue ? '#FCA5A5' : '#86EFAC';
  const fg = overdue ? '#991B1B' : '#166534';
  return (
    <div style={{ ...base, background: bg, border: `1px solid ${bd}`, color: fg }}
      title="Auto-pulled from the DISAWorks portal (Jorge saved views) once daily at 3 AM and imported automatically.">
      {overdue && <style>{'@keyframes disaPulse{0%,100%{opacity:1}50%{opacity:.25}}'}</style>}
      <span style={{ ...dot, background: overdue ? '#EF4444' : '#22C55E', animation: overdue ? 'disaPulse 1.2s ease-in-out infinite' : 'none' }} />
      <span>
        <strong>DISA report last ran:</strong> {fmtDateTime(st.report_modified)} ({disaRelAge(ageMs)})
        {' · '}{st.last_matched} matched · {st.last_added} added
        {overdue && st.enabled && ' · ⚠ daily 3 AM pull may have missed a run'}
        {!st.enabled && ' · auto-sync is OFF'}
      </span>
    </div>
  );
}

function DisaTab() {
  const { user } = useAuth();
  const canDelete = isAdminLike(user); // DELETE /employees is requireAdmin — hide it for ops/supervisor
  const [rolled, toggleRolled] = useRollup('disa');
  // Jump-to-Roster: same ?tab= URL convention as the "Go to Roster →"
  // buttons elsewhere on this page, plus an ?emp= param RosterTab reads on
  // mount to pre-fill its search box to this employee's row.
  const [disaParams, setSearchParams] = useSearchParams();
  const goToRoster = (name) => setSearchParams(prev => {
    const n = new URLSearchParams(prev);
    n.set('tab', 'roster');
    n.set('emp', name);
    return n;
  });
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);

  const reload = () => api.listDisa().then(setRows).catch(e => setError(e.message));
  useEffect(() => { reload(); }, []);

  const save = async () => {
    setError('');
    try {
      await api.saveDisa(editing.employee_id, editing);
      setEditing(null);
      reload();
    } catch (err) { setError(err.message); }
  };

  // Delete the EMPLOYEE (not just the DISA record) — same endpoint as the
  // Roster tab's Del button; server refuses if they have allocations.
  const removeEmployee = async () => {
    if (!confirm(`Delete ${editing.full_name} from the roster entirely? This removes their DISA record, documents, and training too.`)) return;
    setError('');
    try {
      await api.deleteEmployee(editing.employee_id);
      setEditing(null);
      reload();
    } catch (err) {
      if (/allocation/i.test(err.message)) {
        if (confirm(`${editing.full_name} is assigned to jobs — ${err.message}\n\nDelete anyway? Their job assignments will be removed with them.`)) {
          try { await api.deleteEmployee(editing.employee_id, { force: true }); setEditing(null); reload(); return; }
          catch (err2) { alert(`Delete failed: ${err2.message}`); }
        }
        return;
      }
      alert(`Delete failed: ${err.message}`);
    }
  };

  // DISA report upload -- one-shot (no separate preview step). Posts the
  // file; every person is matched to the roster or ADDED to it (so the DISA
  // upload feeds the one roster), then their DISA record is written. Alerts
  // with the matched/added counts and reloads the table.
  const handleUpload = async (file) => {
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const r = await api.uploadDisaReport(file);
      const added = r.added_names || [];
      const addedMsg = r.employees_added > 0
        ? `\n\n➕ ${r.employees_added} added to the roster (were missing):\n${added.slice(0, 15).join(', ')}${added.length > 15 ? '\n…and more' : ''}`
        : '';
      alert(`DISA report imported.\n\nRows in file: ${r.rows_in_file}\nUnique employees: ${r.employees_in_file}\n✓ Matched on roster: ${r.employees_matched}${addedMsg}`);
      reload();
    } catch (err) { setError(err.message); }
    finally { setUploading(false); }
  };

  const imported = rows.filter(r => r.source === 'disa_import').length;

  // Employee search + sortable/filterable headers (same TableControls pattern
  // as the Equipment Gantt). Dates normalized to ISO so the header sort on
  // Last Test / BG Expiry is chronological regardless of the raw format.
  // Seeded once from ?emp= (a Site Access fix-it link), like the Roster.
  const [q, setQ] = useState(() => disaParams.get('emp') || '');
  const isoDay = (v) => { if (!v) return ''; const d = new Date(v); return isNaN(d.getTime()) ? String(v) : d.toISOString().slice(0, 10); };
  const s = q.trim().toLowerCase();
  const searched = s
    ? rows.filter(r =>
        (r.full_name || '').toLowerCase().includes(s) ||
        (r.craft || '').toLowerCase().includes(s) ||
        (Array.isArray(r.policies) ? r.policies.join(' ') : '').toLowerCase().includes(s))
    : rows;
  const disaCols = {
    employee: r => r.full_name,
    craft: r => r.craft || '',
    isn: r => r.isn_number || '',
    policies: r => (Array.isArray(r.policies) ? r.policies.join(' ') : ''),
    last_test: r => isoDay(r.drug_test_date),
    bg_expiry: r => isoDay(r.background_check_expiry),
    random: r => (r.random_pool ? 'yes' : 'no'),
    source: r => (r.source === 'disa_import' ? 'import' : 'manual'),
  };
  const { displayRows, headerProps, filters, clearFilter, clearAll } = useTableControls(searched, disaCols);
  const DISA_COL_LABELS = { employee: 'Employee', craft: 'Craft', isn: 'ISN #', policies: 'Policies', last_test: 'Last Test', bg_expiry: 'BG Expiry', random: 'Random', source: 'Source' };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <RollupH2 collapsed={rolled} onToggle={toggleRolled} style={{ margin: 0 }}>
          DISA
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
            · {rows.length} employees · {imported} from DISA report
          </span>
        </RollupH2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <UploadStatusBadge statusKey="last_disa_import" />
          <button style={{ ...styles.btnSecondary, fontSize: 12 }}
            onClick={() => api.downloadDisaTemplate().catch(err => setError(err.message))}>
            ⬇ Template
          </button>
          <DropZone onFiles={files => handleUpload(files[0])} disabled={uploading} title="Drop the DISA report here">
            <label style={{ ...styles.btn, display: 'inline-block', cursor: uploading ? 'wait' : 'pointer' }}>
              {uploading ? '… Uploading' : '⬆ Upload DISA Report'}
              <input type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} disabled={uploading}
                onChange={e => { handleUpload(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
          </DropZone>
        </div>
      </div>
      {error && <div style={styles.errorBox}>{error}</div>}
      {!rolled && <>
      <DisaSyncStatus />
      <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', color: '#075985', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 12 }}>
        💡 Policies + most-recent drug test dates <strong>auto-refresh from the DISAWorks portal once daily at 3 AM</strong> (the status above shows when it last ran). You can also <strong>upload a DISA report</strong> (CSV / XLSX) to refresh now. Manual fields (DISA ID, ISN #, background check, random pool, notes) are preserved on re-upload (a report that carries an ISN # wins over a typed one).
      </div>
      {editing && (
        <div style={styles.card}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>DISA · {editing.full_name}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <Field label="DISA ID"><input style={styles.input} value={editing.disa_id || ''} onChange={e => setEditing({ ...editing, disa_id: e.target.value })} /></Field>
            <Field label="ISN Number"><input style={styles.input} placeholder="ISN-nnnnnnnn" value={editing.isn_number || ''} onChange={e => setEditing({ ...editing, isn_number: e.target.value })} /></Field>
            <Field label="Drug Test Date"><input type="date" min="2000-01-01" max="2100-12-31" style={styles.input} value={editing.drug_test_date || ''} onChange={e => setEditing({ ...editing, drug_test_date: e.target.value })} /></Field>
            <Field label="Drug Test Expiry"><input type="date" min="2000-01-01" max="2100-12-31" style={styles.input} value={editing.drug_test_expiry || ''} onChange={e => setEditing({ ...editing, drug_test_expiry: e.target.value })} /></Field>
            <Field label="Background Check Date"><input type="date" min="2000-01-01" max="2100-12-31" style={styles.input} value={editing.background_check_date || ''} onChange={e => setEditing({ ...editing, background_check_date: e.target.value })} /></Field>
            <Field label="Background Check Expiry"><input type="date" min="2000-01-01" max="2100-12-31" style={styles.input} value={editing.background_check_expiry || ''} onChange={e => setEditing({ ...editing, background_check_expiry: e.target.value })} /></Field>
            <Field label="Random Pool"><label style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8 }}><input type="checkbox" checked={!!editing.random_pool} onChange={e => setEditing({ ...editing, random_pool: e.target.checked })} /> In random pool</label></Field>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Notes"><textarea style={{ ...styles.input, minHeight: 50 }} value={editing.notes || ''} onChange={e => setEditing({ ...editing, notes: e.target.value })} /></Field>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {canDelete && (
              <button style={{ ...styles.btnDanger }} onClick={removeEmployee}
                title="Removes the employee from the roster (DISA record, documents, and training go with them)">
                🗑 Delete Employee
              </button>
            )}
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <button style={styles.btnSecondary} onClick={() => setEditing(null)}>Cancel</button>
              <button style={styles.btn} onClick={save}>Save</button>
            </div>
          </div>
        </div>
      )}
      <div style={styles.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="🔍 Search employee, craft, or policy…"
            style={{ ...styles.input, maxWidth: 320 }} />
          <span style={{ fontSize: 12, color: '#6B7280' }}>
            {displayRows.length === rows.length ? `${rows.length} employees` : `${displayRows.length} of ${rows.length} shown`}
          </span>
        </div>
        <ActiveFiltersBar filters={filters} clearFilter={clearFilter} clearAll={clearAll} colLabels={DISA_COL_LABELS} />
        <div style={{ overflowX: 'auto' }}>
        <table style={{ ...styles.table, fontSize: 13 }}>
          <thead><tr>
            <SortableFilterTh label="Employee" {...headerProps('employee')} />
            <SortableFilterTh label="Craft" {...headerProps('craft')} />
            <SortableFilterTh label="ISN #" {...headerProps('isn')} />
            <SortableFilterTh label="Policies" {...headerProps('policies')} />
            <SortableFilterTh label="Last Test" {...headerProps('last_test')} />
            <SortableFilterTh label="BG Expiry" {...headerProps('bg_expiry')} />
            <SortableFilterTh label="Random" {...headerProps('random')} align="center" noFilter />
            <SortableFilterTh label="Source" {...headerProps('source')} noFilter />
            <th style={{ background: '#1F2937' }}></th>
          </tr></thead>
          <tbody>
            {displayRows.map(r => (
              <tr key={r.employee_id}>
                <td style={{ ...styles.td, fontWeight: 600, ...expiryNameStyle(r.background_check_expiry) }}>
                  <button onClick={() => goToRoster(r.full_name)} title="Open this employee's roster record"
                    style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 'inherit', color: 'inherit', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2 }}>
                    {r.full_name}
                  </button>
                </td>
                <td style={{ ...styles.td, fontSize: 12, color: '#6B7280' }}>{r.craft || '—'}</td>
                <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 11, color: r.isn_number ? '#374151' : '#9CA3AF' }}>{r.isn_number || '—'}</td>
                <td style={{ ...styles.td, fontSize: 11 }}>
                  {Array.isArray(r.policies) && r.policies.length > 0
                    ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                        {r.policies.map(p => (
                          <span key={p} title={p} style={{
                            background: '#E0E7FF', color: '#3730A3', padding: '1px 6px',
                            borderRadius: 10, fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap',
                            maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis'
                          }}>{p}</span>
                        ))}
                      </div>
                    )
                    : <span style={{ color: '#9CA3AF' }}>—</span>}
                </td>
                <td style={{ ...styles.td, fontSize: 12 }}>
                  {r.drug_test_date
                    ? <div>
                        <div>{fmtDate(r.drug_test_date)}</div>
                        {r.last_test_purpose && (
                          <div style={{ fontSize: 10, color: '#6B7280', fontStyle: 'italic' }}>{r.last_test_purpose}</div>
                        )}
                      </div>
                    : <span style={{ color: '#9CA3AF' }}>—</span>}
                </td>
                <td style={styles.td}>{expiryChip(r.background_check_expiry)}</td>
                <td style={styles.td}>{r.random_pool ? '✓' : '—'}</td>
                <td style={{ ...styles.td, fontSize: 11, color: colors.textMuted }}>
                  {r.source === 'disa_import'
                    ? <span title={r.imported_at ? `Imported ${fmtDateTime(r.imported_at)}` : ''} style={{ color: '#065F46', fontWeight: 600 }}>📥 import</span>
                    : r.updated_at
                      ? <span title={r.updated_by_name ? `by ${r.updated_by_name}` : ''}>{fmtDateTime(r.updated_at)}</span>
                      : '—'}
                </td>
                <td style={styles.td}>
                  <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 8px' }} onClick={() => { setEditing(r); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {!displayRows.length && <p style={{ color: '#6B7280', fontSize: 13, padding: '10px 2px', margin: 0 }}>No employees match your search or filters.</p>}
      </div>
      </>}
    </>
  );
}

// ===== Site Access =======================================================
// Pick a customer (+ optional site), or a job → the whole active roster (or
// that job's crew) evaluated Site Ready (green) / Expiring (amber) / Not
// Ready (red) / No Rules (gray) against the customer's site requirements.
// Click a chip → the same reasons modal the Job Hub uses (ComplianceModal),
// click a name → the employee record. Read-only apart from Quick-Mark and the
// badge loader, which sit BELOW the results. The Site Access overhaul (George
// "fix all" 2026-09-23) added the URL state, the unevaluable-jobs list, the
// Missing / Next action columns, the blocker bar, CSV and "Ready through";
// the row logic lives in utils/siteAccessView.js (pinned by
// server/test/siteAccessView.test.js).
const saFieldLabel = { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6B7280', fontWeight: 700, marginBottom: 3 };
const saPill = (on, c) => ({
  fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 99, cursor: 'pointer',
  border: `1px solid ${on ? c : '#E5E7EB'}`, background: on ? c : '#fff', color: on ? '#fff' : c,
});
const saTag = (bg, fg) => ({
  display: 'inline-block', background: bg, color: fg, borderRadius: 8, padding: '1px 7px',
  fontSize: 10, fontWeight: 700, margin: '1px 3px 1px 0', whiteSpace: 'nowrap',
});

// Email to Dispatch (Site Access overhaul decision 10, 2026-09-23), replacing
// the old window.prompt / alert: a recipients field this browser remembers per
// viewer (localStorage, every access in try/catch: blocked storage just means
// the default list), Preview = the exact email built by the server with
// ?dry=true (nothing sent, nothing logged), Send = the real send, which the
// server logs as sent or failed. No site is sent: the server judges the email
// on the job's own site. Site Key Contacts are deliberately not in it.
const DISPATCH_TO_DEFAULT = 'dispatch@selectsafetyusa.com';
const dispatchToKey = (userId) => `sss_crm_sa_dispatch_to:${userId || 'anon'}`;
const readDispatchTo = (userId) => {
  try { return localStorage.getItem(dispatchToKey(userId)) || DISPATCH_TO_DEFAULT; }
  catch (_) { return DISPATCH_TO_DEFAULT; }
};
const saveDispatchTo = (userId, v) => {
  try {
    if (v.trim()) localStorage.setItem(dispatchToKey(userId), v.trim());
    else localStorage.removeItem(dispatchToKey(userId));
  } catch (_) { /* storage blocked: the field still works for this visit */ }
};

function DispatchEmailBar({ customerId, jobId, userId }) {
  const [to, setTo] = useState(() => readDispatchTo(userId));
  const [busy, setBusy] = useState('');       // '' | 'preview' | 'send'
  const [msg, setMsg] = useState(null);       // { ok, text } after Send (or a failed Preview)
  const [preview, setPreview] = useState(null);
  const payload = () => ({ customer_id: customerId, project_id: jobId, to });

  // A preview shows the addresses it was built for, so editing them drops it.
  const changeTo = (v) => { setTo(v); saveDispatchTo(userId, v); setMsg(null); setPreview(null); };

  const doPreview = async () => {
    setBusy('preview'); setMsg(null);
    try { setPreview(await api.siteAccessEmailPreview(payload())); }
    catch (e) { setPreview(null); setMsg({ ok: false, text: `Preview failed: ${e.message}` }); }
    finally { setBusy(''); }
  };

  const doSend = async () => {
    if (!to.trim()) { setMsg({ ok: false, text: 'Type at least one address to send to.' }); return; }
    setBusy('send'); setMsg(null);
    try {
      const r = await api.siteAccessEmail(payload());
      const at = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      setMsg(r.sent
        ? { ok: true, text: `Sent ${at} to ${r.to.join(', ')}${r.cc ? ` (cc ${r.cc})` : ''}: ${r.counts.green} ready / ${r.counts.amber} expiring / ${r.counts.red} not ready.` }
        : { ok: false, text: 'Not sent: email (SMTP) is not configured on the server.' });
    } catch (e) { setMsg({ ok: false, text: `Send failed: ${e.message}` }); }
    finally { setBusy(''); }
  };

  return (
    <div style={{ marginTop: 10, padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: 8, background: '#F9FAFB' }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ ...saFieldLabel, marginBottom: 0 }}>📧 Email to Dispatch</span>
        <input value={to} onChange={e => changeTo(e.target.value)} aria-label="Dispatch email recipients"
          placeholder={`${DISPATCH_TO_DEFAULT}, another@selectsafetyusa.com`}
          style={{ ...styles.input, fontSize: 12, padding: '4px 8px', flex: '1 1 260px', maxWidth: 460 }} />
        <button style={{ ...styles.btnSecondary, fontSize: 12, padding: '5px 12px' }} disabled={!!busy} onClick={doPreview}
          title="Build the exact email without sending it.">
          {busy === 'preview' ? 'Building…' : '👁 Preview'}
        </button>
        <button style={{ ...styles.btn, fontSize: 12, padding: '5px 12px' }} disabled={!!busy} onClick={doSend}
          title="Email this job's crew readiness to these addresses: job requirements, each employee's needs and arrival dates, plus who's ready.">
          {busy === 'send' ? 'Sending…' : 'Send'}
        </button>
      </div>
      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
        Comma-separate addresses. You are CC'd at your login's email. This browser remembers the list for you.
      </div>
      {msg && <div style={{ fontSize: 12, marginTop: 6, fontWeight: 600, color: msg.ok ? '#15803D' : '#B91C1C' }}>{msg.text}</div>}
      {preview && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: 12, color: '#374151' }}>
            <div>
              <div><strong>Subject:</strong> {preview.subject}</div>
              <div>
                <strong>To:</strong> {preview.to.length ? preview.to.join(', ') : <em style={{ color: '#B45309' }}>no address yet</em>}
                {preview.cc ? <> · <strong>CC:</strong> {preview.cc}</> : null}
              </div>
              <div>
                <strong>Crew:</strong> {preview.crew_count} ({preview.counts.green} ready / {preview.counts.amber} expiring / {preview.counts.red} not ready{preview.open_seats ? ` · ${preview.open_seats} open seat${preview.open_seats === 1 ? '' : 's'}` : ''})
              </div>
              {!preview.smtp_configured && <div style={{ color: '#B45309' }}>⚠ Email (SMTP) is not configured on the server, so Send will not go out.</div>}
            </div>
            <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 10px', alignSelf: 'flex-start' }} onClick={() => setPreview(null)}>
              Close preview
            </button>
          </div>
          {/* sandbox="": the email is shown, never run (no scripts, no forms). */}
          <iframe title="Dispatch email preview" sandbox="" srcDoc={preview.html}
            style={{ width: '100%', height: 420, border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', marginTop: 6 }} />
          <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
            Preview only: nothing was sent or logged. Send checks everyone again at the moment it goes out.
          </div>
        </div>
      )}
    </div>
  );
}

function SiteAccessTab() {
  const { user } = useAuth();
  // Quick-Mark writes through POST /training/bulk, which is requireAdmin, so
  // the grid is offered to admin-like users only (decision 8).
  const canQuickMark = isAdminLike(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [custQuery, setCustQuery] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all | green | amber | red | gray
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const [recordFor, setRecordFor] = useState(null); // employee record modal (name click)
  const [viewMode, setViewMode] = useState('overall'); // overall | disa
  const [allSitesOnly, setAllSitesOnly] = useState(false);
  // '' = whole roster; a project id narrows to that job's allocated crew
  // (George 2026-07-16 — "see their site readiness" for one job's crew).
  const [jobFilter, setJobFilter] = useState('');
  // "Ready through" (no job picked): every expiry judged at this date + 30.
  const [through, setThrough] = useState('');
  const [blocker, setBlocker] = useState(''); // a top-blocker check label, used as a filter
  const [oneAway, setOneAway] = useState(false);
  const [rolled, toggleRolled] = useRollup('siteaccess');
  const [unevalRolled, toggleUnevalRolled] = useRollup('siteaccess-unevaluable');
  // Won + Active jobs for the "find by job" dropdown (George 2026-07-16) —
  // picking one selects its customer AND pre-scopes the Job filter to it.
  const [wonJobs, setWonJobs] = useState([]);
  const [jobsLoaded, setJobsLoaded] = useState(false);
  const [unevaluable, setUnevaluable] = useState(null);
  const [unevalError, setUnevalError] = useState(false);
  const [hubId, setHubId] = useState(null); // Job Hub opened from the unevaluable list
  const loadSeq = useRef(0);   // newest evaluation wins; a slow reply never lands over a newer pick
  const pickSeq = useRef(0);   // same for the picked customer's site list
  const wroteKey = useRef(null); // the Site Access URL params this tab last wrote

  const loadLists = () => {
    api.siteAccessJobs().then(setWonJobs).catch(() => { /* dropdown optional */ })
      .finally(() => setJobsLoaded(true));
    api.siteAccessUnevaluableJobs()
      .then(r => { setUnevaluable(r); setUnevalError(false); })
      .catch(() => setUnevalError(true));
  };
  useEffect(() => {
    api.listCustomers({ status: 'A', limit: 5000 }).then(setCustomers).catch(e => setError(e.message));
    loadLists();
  }, []);

  // customer / site / job / through ride in the URL (?tab=siteaccess&...) so
  // a refresh, a bookmark or a Job Hub link lands on the same view. Written
  // with replace (no history entry per click); wroteKey tells our own writes
  // apart from an outside change (a link followed while this tab is open).
  const writeUrl = (st) => setSearchParams(prev => {
    const next = writeSiteAccessParams(prev, st);
    wroteKey.current = saParamsKey(next);
    return next;
  }, { replace: true });

  // jobId rides the fetch: the server gives allocated employees their
  // first/last day onsite + the compliance horizon (the later of the last day
  // and the job's end, + 30; amber "expiring" checks), so a job change
  // refetches. The rows stay on screen while it reloads (no flash, and
  // Quick-Mark keeps its message); a FAILED reload clears them, because the
  // site / job / through above already show the new pick and those verdicts
  // were computed for the old one.
  const load = async (cid, sid, jobId, th, { adoptJobSite = false } = {}) => {
    if (!cid) return;
    const seq = ++loadSeq.current;
    setLoading(true); setError('');
    try {
      const d = await api.siteAccess(cid, sid || null, jobId || null, jobId ? null : (th || null));
      if (seq !== loadSeq.current) return;
      // A link that named a job but no site, for a job outside the Won /
      // Active list: judge it on the job's own site, as picking it would.
      const jj = adoptJobSite && jobId ? (d.jobs || []).find(x => String(x.id) === String(jobId)) : null;
      if (jj && jj.customer_site_id && String(jj.customer_site_id) !== String(sid || '')) {
        const jsid = String(jj.customer_site_id);
        setSiteId(jsid);
        writeUrl({ customer: cid, site: jsid, job: jobId });
        load(cid, jsid, jobId, '');
        return;
      }
      setData(d);
      // An open reasons modal follows the re-judged row.
      setDetail(cur => (cur && !cur.open_seat ? (d.employees || []).find(e => e.employee_id === cur.employee_id) || null : cur));
    } catch (e) {
      if (seq === loadSeq.current) { setError(e.message); setData(null); setDetail(null); }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  };
  // Leaving the tab retires any load in flight: a late reply must not write
  // the old ?tab=siteaccess&... URL back (the adopt-the-job's-site branch
  // above calls writeUrl) and pull the user off the tab he moved to.
  useEffect(() => () => { loadSeq.current += 1; }, []);

  const pickCustomer = async (id, jobId = '', sid = '', { th = '', adoptJobSite = false } = {}) => {
    const pick = ++pickSeq.current;
    setCustomerId(id); setSiteId(sid); setData(null); setSites([]); setFilter('all'); setQ('');
    setViewMode('overall'); setAllSitesOnly(false); setJobFilter(jobId); setThrough(jobId ? '' : th);
    setBlocker(''); setOneAway(false); setDetail(null);
    writeUrl({ customer: id, site: sid, job: jobId, through: jobId ? '' : th });
    if (!id) { loadSeq.current += 1; setLoading(false); return; }
    load(id, sid, jobId, th, { adoptJobSite });
    try {
      const r = await api.getCustomerRequirements(id);
      if (pick === pickSeq.current) setSites((r.sites || []).filter(s => s.is_active !== false));
    } catch (_) { /* sites optional */ }
  };

  // Jump straight from a job to its customer's readiness, pre-scoped to that
  // job's crew (and evaluated against that job's onsite window). The job's
  // own site (set on the Job Hub; null when none or stale) is picked too, so
  // the roster is judged on the same requirements row as its Site Ready chips.
  const pickJob = (jobId) => {
    const j = wonJobs.find(x => String(x.id) === jobId);
    if (!j) return;
    setCustQuery('');
    pickCustomer(String(j.customer_id), String(j.id), j.customer_site_id ? String(j.customer_site_id) : '');
  };

  const clearCustomer = () => pickCustomer('');

  const changeSite = (sid) => {
    setSiteId(sid);
    writeUrl({ customer: customerId, site: sid, job: jobFilter, through });
    load(customerId, sid, jobFilter, through);
  };

  // Picking a job switches to ITS site (none = customer-wide); back to the
  // whole roster keeps the site picked. A job's crew is judged through their
  // own last day onsite, so "Ready through" clears.
  const changeJob = (v) => {
    const jj = (data?.jobs || []).find(x => String(x.id) === v);
    const sid = v ? (jj && jj.customer_site_id ? String(jj.customer_site_id) : '') : siteId;
    const th = v ? '' : through;
    setJobFilter(v); setSiteId(sid); setThrough(th); setFilter('all'); setBlocker(''); setOneAway(false);
    writeUrl({ customer: customerId, site: sid, job: v, through: th });
    load(customerId, sid, v, th);
  };

  const changeThrough = (v) => {
    const t = v || '';
    if (t && !/^(20\d\d|2100)-\d\d-\d\d$/.test(t)) { setError('Ready-through year must be between 2000 and 2100.'); return; }
    setThrough(t);
    if (!t) setFilter(f => (f === 'amber' ? 'all' : f));
    writeUrl({ customer: customerId, site: siteId, job: '', through: t });
    load(customerId, siteId, '', t);
  };

  // Apply the URL: on arrival (once the jobs list is in, so a job can resolve
  // its customer and site) and whenever something outside this tab changes it.
  const urlKey = saParamsKey(searchParams);
  useEffect(() => {
    if (!jobsLoaded || urlKey === wroteKey.current) return;
    wroteKey.current = urlKey;
    const p = readSiteAccessParams(searchParams);
    if (p.job) {
      const j = wonJobs.find(x => String(x.id) === p.job);
      const cid = p.customer || (j ? String(j.customer_id) : '');
      if (!cid) {
        setError(`Job #${p.job} is not a Won / Active job with a customer Site Access can resolve. Check the list of jobs Site Access cannot evaluate.`);
        return;
      }
      const sid = p.hasSite ? p.site
        : (j && String(j.customer_id) === cid && j.customer_site_id ? String(j.customer_site_id) : '');
      pickCustomer(cid, p.job, sid, { adoptJobSite: !p.hasSite && !j });
    } else if (p.customer) {
      pickCustomer(p.customer, '', p.site, { th: p.through });
    } else if (customerId) {
      pickCustomer('');
    }
  }, [jobsLoaded, urlKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const custName = customers.find(c => String(c.id) === String(customerId))?.name
    || (data && String(data.customer?.id) === String(customerId) ? data.customer.name : '');

  const filteredCustomers = custQuery.trim()
    ? customers.filter(c => (c.name || '').toLowerCase().includes(custQuery.trim().toLowerCase())).slice(0, 50)
    : [];

  // "DISA only" view swaps which status field drives the filter buttons +
  // chip color (server precomputes disa_status from just the DISA-category
  // checks). "Ready at every site" narrows to employees green at EVERY
  // site-level requirement row this customer has on file — only meaningful
  // (and only shown) when the customer actually has 2+ sites.
  const statusField = viewMode === 'disa' ? 'disa_status' : 'status';
  // Job scope narrows the (already-evaluated) roster to one job's allocated
  // crew — applied BEFORE the pill and blocker counts so "Not Ready (n)"
  // reads as "n of this crew", which is the question being asked.
  // A jobFilter the loaded jobs list can't resolve (job dropped out of the
  // customer's list, status change) degrades to NO scope — the whole roster,
  // same as "no job selected". An empty Set here would filter every employee
  // away and read as "nobody is on this crew", which is a lie. A job that IS
  // found keeps its scope verbatim, empty crew included.
  const jobRow = jobFilter ? (data?.jobs || []).find(j => String(j.id) === jobFilter) || null : null;
  const jobScope = jobRow ? new Set(jobRow.employee_ids) : null;
  const allEmployees = data?.employees || [];
  const jobCrew = jobScope ? allEmployees.filter(e => jobScope.has(e.employee_id)) : allEmployees;
  const scopedEmployees = jobCrew.filter(e => !allSitesOnly || e.all_sites_status === 'green');
  const viewCounts = { green: 0, amber: 0, red: 0, gray: 0 };
  scopedEmployees.forEach(e => { viewCounts[e[statusField]] = (viewCounts[e[statusField]] || 0) + 1; });
  // Expiring exists only against a horizon (a job's crew, or Ready through).
  const horizonApplies = !!(data && data.horizon_applies);
  const effFilter = filter === 'amber' && !horizonApplies ? 'all' : filter;
  const blockers = blockerCounts(scopedEmployees, viewMode);
  const activeBlocker = blocker && blockers.some(b => b.label === blocker) ? blocker : '';
  const oneAwayCount = scopedEmployees.filter(e => isOneItemAway(e, viewMode)).length;

  const search = q.trim().toLowerCase();
  const employees = scopedEmployees.filter(e => {
    if (effFilter !== 'all' && e[statusField] !== effFilter) return false;
    if (activeBlocker && !hasBlocker(e, activeBlocker, viewMode)) return false;
    if (oneAway && !isOneItemAway(e, viewMode)) return false;
    if (search && !(e.name || '').toLowerCase().includes(search) && !(e.craft || '').toLowerCase().includes(search)) return false;
    return true;
  });

  // For the chip: in DISA view, scope status + checks down to DISA-only so
  // the "N issues" count on a red chip doesn't include training/TWIC fails
  // unrelated to what the view claims to show. Clicking still opens the
  // FULL entry (all categories) in the detail modal — the lens only affects
  // the at-a-glance chip, never hides data from the drill-in.
  const chipEntry = (e) => {
    if (viewMode !== 'disa') return e;
    const disaChecks = e.checks.filter(c => c.category === 'disa');
    return {
      ...e, status: e.disa_status,
      checks: disaChecks.length ? disaChecks : [{ label: 'DISA', status: 'na', detail: 'No DISA requirements defined for this customer.' }],
    };
  };

  // Sortable/filterable column headers, same pattern as Roster/DISA/Training.
  // "Site Access" sorts not ready first, then expiring, then ready, then no
  // rules, fewest open items first inside each (statusSortKey). Missing and
  // Next action are text-filterable.
  const siteAccessCols = {
    employee: e => e.name || '',
    craft: e => e.craft || '',
    isn: e => e.isn_number || '',
    first_day: e => e.first_day_onsite || '',
    all_sites: e => e.all_sites_status || '',
    issues: e => statusSortKey(e, viewMode),
    missing: e => openItems(e, viewMode).map(c => c.label).join('; '),
    next: e => actionsText(rowActions(e, viewMode)),
  };
  // Job view defaults to ARRIVAL order (first day onsite, earliest first;
  // undated last) — a header click still overrides via TableControls sort.
  const employeesOrdered = jobFilter
    ? [...employees].sort((a, b) =>
        String(a.first_day_onsite || '9999').localeCompare(String(b.first_day_onsite || '9999'))
        || String(a.name || '').localeCompare(String(b.name || '')))
    : employees;
  const { displayRows: saRows, headerProps: saHeaderProps, filters: saFilters, clearFilter: saClearFilter, clearAll: saClearAll } = useTableControls(employeesOrdered, siteAccessCols);
  const SA_COL_LABELS = { employee: 'Employee', craft: 'Craft', isn: 'ISN #', first_day: 'First Day Onsite', all_sites: 'All Sites', issues: 'Site Access', missing: 'Missing', next: 'Next action' };
  const fmtOnsite = (d) => fmtIsoDate(d) || '—';
  const colFilterOn = Object.values(saFilters || {}).some(f => f && f.value);
  // Placeholder seats on the picked job: listed under the crew, never judged
  // as people, and only while nothing narrows the list.
  const openSeats = jobRow && effFilter === 'all' && !activeBlocker && !oneAway && !search && !allSitesOnly && !colFilterOn
    ? (data?.open_seats || []) : [];
  const siteCols = data && data.site_count > 1;

  const exportCsv = () => {
    const rows = siteAccessCsvRows(saRows, { viewMode, withJob: !!jobFilter, withAllSites: !!siteCols });
    const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const slug = (v) => String(v || '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
    a.download = ['site-access', slug(custName) || 'customer', jobRow ? slug(jobRow.job_number || jobRow.site) : '',
      new Date().toISOString().slice(0, 10)].filter(Boolean).join('-') + '.csv';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  // project_id: the picked job, so "Register with HASC" in the modal defaults
  // the PO to its job number and judges the class through the job's horizon.
  const job = data ? { customer: data.customer, requirements_source: data.requirements_source, isn: data.isn, project_id: jobRow ? jobRow.id : null } : null;
  const reload = () => load(customerId, siteId, jobFilter, through);
  const colCount = 6 + (jobFilter ? 1 : 0) + (siteCols ? 1 : 0);

  return (
    <>
      <RollupH2 collapsed={rolled} onToggle={toggleRolled} style={{ margin: '0 0 12px' }}>
        🔑 Site Access
        <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
          who's cleared to access a customer's site — and what's missing
        </span>
      </RollupH2>
      {error && <div style={styles.errorBox}>{error}</div>}

      {!rolled && <>
      {/* The DATA FRESHNESS strip for the reports behind this tab sits at the
          top of the Operations page (components/DataFreshness.jsx). */}

      {/* Live jobs the engine cannot judge (the Job Readiness Digest's own
          coverageIssue): nobody on these crews gets a verdict until fixed. */}
      {unevalError && (
        <div style={{ ...styles.card, color: '#B45309', fontSize: 12 }}>Could not load the list of jobs Site Access cannot evaluate.</div>
      )}
      {unevaluable && unevaluable.length > 0 && (
        <div style={{ ...styles.card, borderLeft: '4px solid #D97706' }}>
          <RollupH2 as="h3" collapsed={unevalRolled} onToggle={toggleUnevalRolled}>
            ⚠ {unevaluable.length} live job{unevaluable.length === 1 ? '' : 's'} Site Access cannot evaluate
          </RollupH2>
          {!unevalRolled && (
            <>
              <div style={{ fontSize: 12, color: '#6B7280', margin: '6px 0 8px' }}>
                Won / Active jobs with no customer the engine can resolve, or a customer with no 📋 Reqs row and no
                ISNetworld link. Nobody on these crews gets a Site Ready verdict until it is fixed (the same rule as the
                Job Readiness Digest).
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ ...styles.table, fontSize: 12 }}>
                  <thead><tr>
                    <th style={styles.th}>Job</th>
                    <th style={styles.th}>Starts</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Why</th>
                    <th style={styles.th}></th>
                  </tr></thead>
                  <tbody>
                    {unevaluable.map(j => (
                      <tr key={j.id}>
                        <td style={{ ...styles.td, fontWeight: 600 }}>{j.site || 'Job'}{j.job_number ? <span style={{ color: '#6B7280', fontWeight: 400 }}> · {j.job_number}</span> : ''}</td>
                        <td style={styles.td}>{fmtIsoDate(j.date_planned) || '—'}</td>
                        <td style={styles.td}>{j.status}</td>
                        <td style={styles.td}>
                          {j.issue === 'no_customer'
                            ? (j.customer_name
                              ? `Customer "${j.customer_name}" is not in the customer master: set the facility on the Job Hub.`
                              : 'No customer linked: set the facility on the Job Hub.')
                            : `No site requirements for ${j.customer_name || 'the customer'} and no ISNetworld link (Customers → 📋 Reqs).`}
                        </td>
                        <td style={styles.td}>
                          <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px', whiteSpace: 'nowrap' }} onClick={() => setHubId(j.id)}>Open Job Hub →</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      <div style={styles.card}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ position: 'relative' }}>
            <div style={saFieldLabel}>Customer</div>
            {customerId ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{custName || `Customer #${customerId}`}</span>
                <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px' }} onClick={clearCustomer}>Change</button>
              </div>
            ) : (
              <>
                <input value={custQuery} onChange={e => setCustQuery(e.target.value)} placeholder="Type a customer name…" style={{ ...styles.input, width: 300 }} />
                {filteredCustomers.length > 0 && (
                  <div style={{ position: 'absolute', zIndex: 20, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', maxHeight: 280, overflowY: 'auto', width: 300, marginTop: 2 }}>
                    {filteredCustomers.map(c => (
                      <div key={c.id} onClick={() => { setCustQuery(''); pickCustomer(String(c.id)); }}
                        style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #F3F4F6' }}>
                        {c.name}{c.account_no ? <span style={{ color: '#9CA3AF', marginLeft: 6, fontSize: 11 }}>#{c.account_no}</span> : ''}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          {!customerId && wonJobs.length > 0 && (
            <div>
              <div style={saFieldLabel}>…or pick a Won / Active job</div>
              <select value="" onChange={e => pickJob(e.target.value)} style={{ ...styles.input, minWidth: 320, maxWidth: '100%' }}>
                <option value="">— Won &amp; Active jobs (upcoming first) —</option>
                {wonJobs.map(j => (
                  <option key={j.id} value={j.id}>{jobOptionLabel(j, { withCustomer: true })}</option>
                ))}
              </select>
            </div>
          )}
          {customerId && sites.length > 0 && (
            <div>
              <div style={saFieldLabel}>Site (optional)</div>
              <select value={siteId} onChange={e => changeSite(e.target.value)} style={{ ...styles.input, minWidth: 220 }}>
                <option value="">All / customer-wide</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          {customerId && !jobFilter && (
            <div>
              <div style={saFieldLabel}>Ready through (optional)</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <BlurDateInput value={through} onCommit={changeThrough} min="2000-01-01" max="2100-12-31" style={styles.input}
                  title="Judge every expiry as of this date + 30 days: a class current today that lapses before then reads Expiring." />
                {through && <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px' }} onClick={() => changeThrough('')}>Clear</button>}
              </div>
            </div>
          )}
        </div>
        {!customerId && <div style={{ color: '#6B7280', fontSize: 12, marginTop: 10 }}>Pick a customer to evaluate every active employee against that customer's site requirements, or pick a job to see its crew. Only customers with requirements on file (Customers → 📋 Reqs) or an ISNetworld link produce green/red — others show "No Rules".</div>}
      </div>

      {loading && <div style={{ color: '#6B7280', fontSize: 13, padding: 10 }}>Evaluating roster…</div>}

      {data && !data.requirements_source && (
        <div style={styles.card}>
          <div style={{ color: '#B45309', fontSize: 14, fontWeight: 600 }}>
            No site requirements on file for {data.customer.name}{data.site && !data.site.stale ? ` (checked ${data.site.name} and customer-wide)` : ''}, and no ISNetworld link.
          </div>
          <div style={{ color: '#6B7280', fontSize: 12, marginTop: 6 }}>
            Everyone would read No Rules, which is not the same as ready. Add them on the{' '}
            <Link to={`/customers/${data.customer.id}`}>customer's 📋 Reqs</Link>, then check back here.
          </div>
        </div>
      )}

      {data && data.requirements_source && (
        <>
          <div style={styles.card}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
              <div>
                <strong>{data.customer.name}</strong>{' '}
                {data.isn_only
                  ? <span style={{ color: '#6B7280' }}>· requirements: <strong style={{ color: '#1D4ED8' }}>ISNetworld only</strong> (no 📋 Reqs row: judged on ISNetworld assignments alone)</span>
                  : <span style={{ color: '#6B7280' }}>· requirements: {data.requirements_source}</span>}
                {!jobFilter && data.through && <span style={{ color: '#6B7280' }}> · ready through {fmtIsoDate(data.through)} (+30 days)</span>}
              </div>
              {data.isn && <div style={{ color: '#6B7280' }}>🌐 ISN: {data.isn.client_name ? `${data.isn.client_name} — ${data.isn.detail}` : 'no client record'}</div>}
            </div>
            {data.site && data.site.stale && (
              <div style={{ color: '#B45309', fontSize: 12, marginTop: 6 }}>
                ⚠ {data.site.name || 'That site'} is not one of this customer's sites, so it is ignored and the customer's own row applies.
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'inline-flex', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
                {[
                  { k: 'overall', label: 'Overall' },
                  { k: 'disa', label: 'DISA only' },
                ].map(v => (
                  <button key={v.k} onClick={() => { setViewMode(v.k); setFilter('all'); setBlocker(''); setOneAway(false); }}
                    style={{ fontSize: 12, fontWeight: 600, padding: '5px 12px', cursor: 'pointer', border: 'none',
                      background: viewMode === v.k ? '#1F2937' : '#fff', color: viewMode === v.k ? '#fff' : '#374151' }}>
                    {v.label}
                  </button>
                ))}
              </div>
              {data.site_count > 1 && (
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, color: '#374151' }}
                  title={`This customer has ${data.site_count} sites on file — show only employees who are Site Ready at every one of them.`}>
                  <input type="checkbox" checked={allSitesOnly} onChange={e => { setAllSitesOnly(e.target.checked); setFilter('all'); }} />
                  Ready at every site
                </label>
              )}
              {/* Also rendered when the list is empty but a job filter is set,
                  so an unresolvable scope is always visible and clearable. */}
              {((data.jobs || []).length > 0 || jobFilter) && (
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, color: '#374151' }}
                  title="Narrow to the crew allocated to one of this customer's jobs — the status counts below re-scope to that crew.">
                  Job:
                  <select value={jobFilter} onChange={e => changeJob(e.target.value)}
                    style={{ ...styles.input, fontSize: 12, padding: '4px 8px', maxWidth: 420 }}>
                    <option value="">All employees (whole roster)</option>
                    {(data.jobs || []).map(j => (
                      <option key={j.id} value={String(j.id)}>{jobOptionLabel(j)}</option>
                    ))}
                    {jobFilter && !jobRow && (
                      <option value={jobFilter}>Job #{jobFilter} — not in this customer's list (showing whole roster)</option>
                    )}
                  </select>
                </label>
              )}
            </div>
            {/* Keyed on the job: a preview or result for one job never shows under another. */}
            {jobFilter && (
              <DispatchEmailBar key={`${customerId}:${jobFilter}`} customerId={customerId} jobId={jobFilter} userId={user && user.id} />
            )}
            {/* HASC Phase 2: the crew-wide "Register missing classes" review
                for the picked job (hidden unless a HASC account is set and
                this user may register). Keyed on the job; the plan reloads
                whenever this tab re-judges the crew. */}
            {jobRow && (
              <HascCrewRegister key={`hasc:${jobRow.id}`} projectId={jobRow.id} refreshKey={data} onDone={reload} />
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {[
                { k: 'all', label: `All (${scopedEmployees.length})`, c: '#374151' },
                { k: 'green', label: `✓ ${statusLabel('green', viewMode)} (${viewCounts.green})`, c: '#16A34A' },
                // Expiring needs a horizon (a job's crew, or Ready through).
                ...(horizonApplies ? [{ k: 'amber', label: `⚠ Expiring (${viewCounts.amber})`, c: '#D97706' }] : []),
                { k: 'red', label: `✗ Not Ready (${viewCounts.red})`, c: '#DC2626' },
                { k: 'gray', label: `— No Rules (${viewCounts.gray})`, c: '#6B7280' },
              ].map(f => (
                <button key={f.k} onClick={() => setFilter(f.k)}
                  style={{ fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 99, cursor: 'pointer',
                    border: `1px solid ${effFilter === f.k ? f.c : '#E5E7EB'}`,
                    background: effFilter === f.k ? f.c : '#fff', color: effFilter === f.k ? '#fff' : f.c }}>
                  {f.label}
                </button>
              ))}
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 name or craft…" style={{ ...styles.input, maxWidth: 220, marginLeft: 'auto' }} />
            </div>
            {/* Top blockers, counted over the scoped rows (this crew when a
                job is picked), so they agree with the pills. Click to filter. */}
            {(blockers.length > 0 || oneAwayCount > 0) && (
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ ...saFieldLabel, marginBottom: 0 }}>Top blockers{jobRow ? ' (this crew)' : ''}:</span>
                {oneAwayCount > 0 && (
                  <button onClick={() => setOneAway(v => !v)} style={saPill(oneAway, '#7C3AED')}
                    title="Not ready or expiring with exactly one open item: one class, test or record from green.">
                    ☝ One item away ({oneAwayCount})
                  </button>
                )}
                {blockers.map(b => (
                  <button key={b.label} onClick={() => setBlocker(cur => (cur === b.label ? '' : b.label))}
                    style={saPill(activeBlocker === b.label, '#B91C1C')} title={`Show only the people failing "${b.label}"`}>
                    {b.label} ({b.count})
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={styles.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: '#6B7280' }}>
                {saRows.length} of {scopedEmployees.length} shown{openSeats.length ? ` · ${openSeats.length} open seat${openSeats.length === 1 ? '' : 's'}` : ''}
              </span>
              <button style={{ ...styles.btnSecondary, fontSize: 12, padding: '4px 10px' }} onClick={exportCsv} disabled={!saRows.length}
                title="The rows shown, in this order, with what each person is missing and the next action.">
                ⬇ Export CSV
              </button>
            </div>
            <ActiveFiltersBar filters={saFilters} clearFilter={saClearFilter} clearAll={saClearAll} colLabels={SA_COL_LABELS} />
            <div style={{ overflowX: 'auto' }}>
              <table style={{ ...styles.table, fontSize: 13 }}>
                <thead><tr>
                  <SortableFilterTh label="Employee" {...saHeaderProps('employee')} />
                  <SortableFilterTh label="Craft" {...saHeaderProps('craft')} />
                  <SortableFilterTh label="ISN #" {...saHeaderProps('isn')} />
                  {jobFilter && <SortableFilterTh label="First Day Onsite" {...saHeaderProps('first_day')} noFilter />}
                  {siteCols && <SortableFilterTh label="All Sites" {...saHeaderProps('all_sites')} noFilter />}
                  <SortableFilterTh label="Site Access" {...saHeaderProps('issues')} noFilter />
                  <SortableFilterTh label="Missing" {...saHeaderProps('missing')} />
                  <SortableFilterTh label="Next action" {...saHeaderProps('next')} />
                </tr></thead>
                <tbody>
                  {saRows.map(e => (
                    <tr key={e.employee_id}>
                      <td style={{ ...styles.td, fontWeight: 600 }}>
                        <button type="button" onClick={() => setRecordFor({ id: e.employee_id, full_name: e.name, craft: e.craft, employee_no: e.employee_no })}
                          title="Open the employee record (DISA, training, ISN, and every customer he is cleared for)"
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 600, color: '#1F2937', textAlign: 'left' }}>
                          {e.name}
                        </button>
                        {e.employee_no ? <span style={{ color: '#9CA3AF', fontWeight: 400, fontSize: 11 }}> · #{e.employee_no}</span> : ''}
                        {e.inactive_allocated && (
                          <div><span style={saTag('#FEF3C7', '#92400E')} title="Inactive on the roster but still allocated to this job: evaluated here, but reactivate or remove him before he goes.">Inactive, still allocated</span></div>
                        )}
                        {e.possible_duplicate_of && e.possible_duplicate_of.name && (
                          <div><span style={saTag('#EDE9FE', '#5B21B6')} title="Same surname and a first-name variant of another active roster row, and this row has no DISA or training record of its own. Check the other row before chasing this red. Nothing is ever merged automatically.">Possible duplicate of {e.possible_duplicate_of.name}</span></div>
                        )}
                      </td>
                      <td style={{ ...styles.td, color: '#6B7280', fontSize: 12 }}>{e.craft || '—'}</td>
                      <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 11, color: e.isn_number ? '#374151' : '#9CA3AF' }}>{e.isn_number || '—'}</td>
                      {jobFilter && (
                        <td style={{ ...styles.td, fontSize: 12, color: e.first_day_onsite ? '#374151' : '#9CA3AF' }}
                          title={e.last_day_onsite ? `Onsite through ${fmtOnsite(e.last_day_onsite)} · compliance verified through ${fmtOnsite(e.compliance_through)} (later of last day / job end, + 30)` : undefined}>
                          {fmtOnsite(e.first_day_onsite)}
                        </td>
                      )}
                      {siteCols && (
                        <td style={styles.td}>
                          {e.all_sites_status === 'green' && <span style={{ color: '#16A34A', fontWeight: 700, fontSize: 12 }}>✓ All</span>}
                          {e.all_sites_status === 'amber' && <span style={{ color: '#D97706', fontWeight: 700, fontSize: 12 }}>⚠ Expiring</span>}
                          {e.all_sites_status === 'red' && <span style={{ color: '#DC2626', fontSize: 12 }}>Some sites</span>}
                          {(!e.all_sites_status || e.all_sites_status === 'gray') && <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>}
                        </td>
                      )}
                      <td style={styles.td}><ComplianceChip entry={chipEntry(e)} onClick={() => setDetail(e)} /></td>
                      <td style={{ ...styles.td, maxWidth: 280 }}>
                        {openItems(e, viewMode).map((c, i) => (
                          <span key={i} style={c.status === 'fail' ? saTag('#FEE2E2', '#991B1B') : saTag('#FEF3C7', '#92400E')} title={c.detail}>{c.label}</span>
                        ))}
                      </td>
                      <td style={{ ...styles.td, maxWidth: 260 }}>
                        {rowActions(e, viewMode).map(a => (
                          <span key={a.label} style={a.key === 'duplicate' ? saTag('#EDE9FE', '#5B21B6') : saTag('#E5E7EB', '#374151')}>
                            {a.label}{a.count > 1 ? ` x${a.count}` : ''}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                  {openSeats.map((s, i) => (
                    <tr key={`seat-${s.allocation_id || `${s.employee_id}-${i}`}`} style={{ background: '#F9FAFB' }}>
                      <td style={{ ...styles.td, color: '#6B7280', fontStyle: 'italic' }}>Open seat{s.name ? ` (${s.name})` : ''}</td>
                      <td style={{ ...styles.td, color: '#6B7280', fontSize: 12 }}>{s.craft || '—'}{s.shift ? ` · ${s.shift}` : ''}</td>
                      <td style={{ ...styles.td, color: '#9CA3AF' }}>—</td>
                      {jobFilter && <td style={{ ...styles.td, fontSize: 12, color: '#6B7280' }}>{fmtOnsite(s.first_day_onsite)}</td>}
                      {siteCols && <td style={{ ...styles.td, color: '#9CA3AF' }}>—</td>}
                      <td style={styles.td}><span style={saTag('#F3F4F6', '#6B7280')}>Open seat</span></td>
                      <td colSpan={2} style={{ ...styles.td, color: '#6B7280', fontSize: 12 }}>Nobody assigned yet: a placeholder is never judged as a person. Fill it on Allocations.</td>
                    </tr>
                  ))}
                  {!saRows.length && !openSeats.length && (
                    <tr><td colSpan={colCount} style={{ ...styles.td, textAlign: 'center', color: '#6B7280' }}>No employees match this filter.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Keyed on the class list and the crew scope: a tick is
              "employee|class index", so a site or job change starts a fresh
              grid instead of re-pointing old ticks at another class or at
              people no longer shown. A save's reload keeps the same key, so
              "Saved N" survives. */}
          {canQuickMark && (
            <TrainingQuickMark
              key={[data.customer && data.customer.id, data.site_id || '', jobFilter,
                (data.required_certs || []).map(c => `${c.name}[${c.code || ''}]`).join(';')].join('|')}
              requiredCerts={data.required_certs} employees={jobCrew} jobScoped={!!jobRow} onSaved={reload} />
          )}
        </>
      )}

      {customerId && (
        <BadgeBulkImport customerId={customerId} customerName={custName} onImported={reload} />
      )}

      {detail && <ComplianceModal entry={detail} job={job} onClose={() => setDetail(null)} onChanged={reload} />}
      {recordFor && <EmployeeRecordModal employee={recordFor} onClose={() => setRecordFor(null)} onError={setError} />}
      {hubId && (
        <JobHub projectId={hubId} FilesPanel={JobFilesPanel}
          onClose={() => { setHubId(null); loadLists(); if (customerId) reload(); }} />
      )}
      </>}
    </>
  );
}

// Quick-mark training — for a customer with no report to bulk-import (Training
// tab's Safety Council uploader, or HASC/CSCCB sync), let ops check off who
// already has each required class by hand, MANY classes x MANY employees at
// once, with a shared expiration date. Writes via POST /training/bulk (shared
// upsertTrainingRecords, same match-by-code-then-name as the CSC upload) —
// the grid is just a faster, crew-wide front end for the same underlying
// write the Training tab's manual form uses. George 2026-07-15: "classes
// shouldn't be marked forever" (expiration date, not permanent-by-default)
// and "should be able to assign multiple classes to multiple employees at
// once" (grid, not one class/one employee per save). Add-only by design — to
// un-mark someone, delete the row on the Training tab like any other manual
// correction. Decision 8 (2026-09-23): admin-like only (the caller gates it,
// matching requireAdmin on the route), rows = the job's crew when a job is
// picked, and a class ISNetworld governs shows "ISN decides" (a training
// record cannot satisfy it) instead of a checkbox.
function TrainingQuickMark({ requiredCerts, employees, jobScoped = false, onSaved }) {
  const [open, setOpen] = useState(true);
  const [checked, setChecked] = useState(new Set()); // keys "employeeId|certIdx"
  const [search, setSearch] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [issuedDate, setIssuedDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  if (!requiredCerts || !requiredCerts.length) return null;

  const certLabel = quickMarkLabel;
  const checkFor = (e, certIdx) => quickMarkCheck(e, requiredCerts[certIdx]);
  // A box ops can usefully tick: not on file, not ISN-governed, and not a
  // deactivated (sealed) record (utils/siteAccessView.js quickMarkable).
  const markable = (e, certIdx) => quickMarkable(e, requiredCerts[certIdx]);

  const s = search.trim().toLowerCase();
  const rows = employees.filter(e => {
    if (s && !(e.name || '').toLowerCase().includes(s) && !(e.craft || '').toLowerCase().includes(s)) return false;
    if (onlyMissing && !requiredCerts.some((_, i) => checkFor(e, i)?.status === 'fail' && markable(e, i))) return false;
    return true;
  });

  const key = (empId, certIdx) => `${empId}|${certIdx}`;
  const toggle = (empId, certIdx) => {
    const k = key(empId, certIdx);
    const next = new Set(checked);
    next.has(k) ? next.delete(k) : next.add(k);
    setChecked(next);
  };
  // Only ticks that still point at a person in this grid's scope and a box
  // that is still tickable are saved or counted (liveQuickMarks; the grid is
  // also re-keyed on a scope change by its caller); a tick a search hides
  // still counts.
  const live = liveQuickMarks(checked, employees, requiredCerts);

  const save = async () => {
    if (!live.length) return;
    setSaving(true); setError(''); setMsg('');
    const records = live.map(k => {
      const [empId, certIdx] = k.split('|');
      const rc = requiredCerts[Number(certIdx)];
      return {
        employee_id: Number(empId), cert_type: rc.name,
        certificate_no: rc.code ? String(rc.code).split('/')[0].trim() : null,
        issued_date: issuedDate || null, expiry_date: expiryDate || null,
        issuer: 'Manual (Site Access quick-mark)',
      };
    });
    try {
      const r = await api.bulkCreateTraining(records);
      setChecked(new Set());
      setMsg(`Saved ${records.length} record${records.length === 1 ? '' : 's'} across ${r.employees} employee${r.employees === 1 ? '' : 's'} (${r.inserted} added, ${r.updated} refreshed).`);
      onSaved();
    } catch (err) {
      setError(err.message || 'Bulk save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>✓ Quick-Mark Training{jobScoped ? ' (this job\'s crew)' : ''}</div>
        <span style={{ fontSize: 12, color: '#6B7280' }}>{open ? '▲ hide' : '▼ no source report? check people off manually'}</span>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>
            No Safety Council report or HASC sync data for these classes yet? Check off who already has each
            one across the grid below (multiple classes, multiple employees, one save), set an expiration if it
            has one, and save. This is add-only; to correct a mistake, delete the record on Operations →
            Training like any other manual entry. "ISN decides" marks a class ISNetworld assigned: only ISN can clear it.
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 }}>
            <div>
              <div style={saFieldLabel}>Issued date (optional)</div>
              <input type="date" min="2000-01-01" max="2100-12-31" style={styles.input} value={issuedDate} onChange={e => setIssuedDate(e.target.value)} />
            </div>
            <div>
              <div style={saFieldLabel}>Expiration date (optional)</div>
              <input type="date" min="2000-01-01" max="2100-12-31" style={styles.input} value={expiryDate} onChange={e => setExpiryDate(e.target.value)} />
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF', maxWidth: 220 }}>Applied to every box you check below. Leave Expiration blank for a class that doesn't lapse.</div>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 name or craft…" style={{ ...styles.input, maxWidth: 200, marginLeft: 'auto' }} />
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={onlyMissing} onChange={e => setOnlyMissing(e.target.checked)} />
              Only missing at least one
            </label>
          </div>
          <div style={{ maxHeight: 380, overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: 6 }}>
            <table style={{ ...styles.table, fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, position: 'sticky', left: 0, background: '#F9FAFB', zIndex: 1 }}>Employee</th>
                  {requiredCerts.map((rc, i) => (
                    <th key={i} style={{ ...styles.th, minWidth: 150 }} title={certLabel(rc)}>{rc.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(e => (
                  <tr key={e.employee_id}>
                    <td style={{ ...styles.td, fontWeight: 600, position: 'sticky', left: 0, background: '#fff' }}>
                      {e.name}{e.craft ? <span style={{ color: '#9CA3AF', fontWeight: 400, fontSize: 11 }}> · {e.craft}</span> : ''}
                      {e.inactive_allocated && <span style={{ color: '#B45309', fontWeight: 400, fontSize: 11 }}> · inactive</span>}
                    </td>
                    {requiredCerts.map((rc, i) => {
                      const c = checkFor(e, i);
                      return (
                        <td key={i} style={{ ...styles.td, textAlign: 'center' }}>
                          {c && c.status === 'pass'
                            ? <span style={{ color: '#16A34A', fontWeight: 700 }} title="Already on file">✓</span>
                            : isIsnGoverned(c)
                              ? <span style={{ color: '#1D4ED8', fontSize: 10, fontWeight: 700 }} title={c.detail}>ISN decides</span>
                              : e.inactive_allocated
                                ? <span style={{ color: '#9CA3AF', fontSize: 10 }} title="Deactivated records are sealed: reactivate on the Roster first.">sealed</span>
                                : <input type="checkbox" checked={checked.has(key(e.employee_id, i))} onChange={() => toggle(e.employee_id, i)} />}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td colSpan={requiredCerts.length + 1} style={{ ...styles.td, textAlign: 'center', color: '#6B7280' }}>No employees match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
            <button style={styles.btn} disabled={!live.length || saving} onClick={save}>
              {saving ? '… Saving' : `Save ${live.length || ''} checked box${live.length === 1 ? '' : 'es'}`}
            </button>
            {checked.size > 0 && <button style={styles.btnSecondary} onClick={() => setChecked(new Set())}>Clear</button>}
          </div>
          {msg && <div style={{ fontSize: 12, color: '#16A34A', marginTop: 8 }}>{msg}</div>}
          {error && <div style={{ fontSize: 12, color: '#DC2626', marginTop: 8 }}>{error}</div>}
        </div>
      )}
    </div>
  );
}

// ===== Training ==========================================================

function TrainingTab() {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showExpired, setShowExpired] = useState(false);
  const [rolled, toggleRolled] = useRollup('train');
  // Jump-to-Roster on name click — same mechanism as the DISA tab: RosterTab
  // reads ?emp= once on mount and pre-fills its search to that employee.
  const [trainParams, setSearchParams] = useSearchParams();
  const goToRoster = (name) => setSearchParams(prev => {
    const n = new URLSearchParams(prev);
    n.set('tab', 'roster');
    n.set('emp', name);
    return n;
  });

  // Safety Council report upload — one-shot like the DISA uploader.
  const handleUpload = async (file) => {
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const r = await api.uploadTrainingReport(file);
      const added = r.added_names || [];
      const addedMsg = r.employees_added > 0
        ? `\n\n➕ ${r.employees_added} added to the roster (were missing):\n${added.slice(0, 15).join(', ')}${added.length > 15 ? '\n…and more' : ''}`
        : '';
      alert(`Safety Council report imported.\n\nRows in file: ${r.rows_in_file} (${r.skipped_not_passed} not passed, skipped)\nEmployees: ${r.employees_in_file} (✓ ${r.employees_matched} matched)\nCerts: ${r.certs_inserted} added · ${r.certs_updated} refreshed${addedMsg}`);
      reload();
    } catch (err) { setError(err.message); alert(`Upload failed: ${err.message}`); }
    finally { setUploading(false); }
  };

  const reload = () => api.listTraining().then(setRows).catch(e => setError(e.message));
  useEffect(() => {
    reload();
    api.listEmployees().then(setEmployees).catch(err => console.warn('listEmployees failed:', err));
  }, []);

  const startCreate = () => { setEditing({
    employee_id: '', cert_type: '', issued_date: '', expiry_date: '', certificate_no: '', issuer: '', notes: ''
  }); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const startEdit = (t) => { setEditing(t); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const save = async () => {
    setError('');
    try {
      if (editing.id) await api.updateTraining(editing.id, editing);
      else await api.createTraining(editing);
      setEditing(null);
      reload();
    } catch (err) { setError(err.message); }
  };
  const remove = async (id) => {
    if (!confirm('Delete this certification?')) return;
    try { await api.deleteTraining(id); reload(); }
    catch (err) { setError(err.message); }
  };

  // Default view hides expired certs — the HASC/CSCCB backfill pulled 16
  // years of history, and a flat table of 1000+ mostly-lapsed rows drowns
  // out what's actually actionable. History stays in the DB either way (no
  // delete) for audit/recordkeeping; the toggle reveals it on demand.
  const todayIso = new Date().toISOString().slice(0, 10);
  const isExpired = (t) => t.expiry_date && t.expiry_date < todayIso;
  const visible = showExpired ? rows : rows.filter(t => !isExpired(t));

  const trainingCols = {
    employee: t => t.employee_name || '',
    cert: t => t.cert_type || '',
    issued: t => t.issued_date || '',
    expires: t => t.expiry_date || '',
    cert_no: t => t.certificate_no || '',
    issuer: t => t.issuer || '',
  };
  const { displayRows, headerProps, filters, setFilter, clearFilter, clearAll } = useTableControls(visible, trainingCols);
  // A Site Access fix-it link (?emp=) pre-filters the Employee column once.
  useEffect(() => {
    const emp = trainParams.get('emp');
    if (emp) setFilter('employee', 'contains', emp);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const TRAINING_COL_LABELS = { employee: 'Employee', cert: 'Certification', issued: 'Issued', expires: 'Expires', cert_no: 'Cert #', issuer: 'Issuer' };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap' }}>
        <RollupH2 collapsed={rolled} onToggle={toggleRolled} style={{ margin: 0 }}>Training <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>· {displayRows.length === rows.length ? `${rows.length} certs` : `${displayRows.length} of ${rows.length} certs`}</span></RollupH2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={showExpired} onChange={e => setShowExpired(e.target.checked)} />
            Show expired
          </label>
          <UploadStatusBadge statusKey="last_training_import" />
          <DropZone onFiles={files => handleUpload(files[0])} disabled={uploading} title="Drop the Safety Council report here">
            <label style={{ ...styles.btnSecondary, display: 'inline-block', cursor: uploading ? 'wait' : 'pointer' }}
              title="Bulk import the Safety Council training report export (Course Code / Trainee / Completed / Expiration). Pass rows only; missing employees are added to the roster.">
              {uploading ? '… Uploading' : '⬆ Upload Safety Council Report'}
              <input type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} disabled={uploading}
                onChange={e => { handleUpload(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
          </DropZone>
          <button style={styles.btn} onClick={startCreate}>+ Add Certification</button>
        </div>
      </div>
      {error && <div style={styles.errorBox}>{error}</div>}
      {!rolled && <>
      {editing && (
        <div style={styles.card}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>{editing.id ? 'Edit Certification' : 'New Certification'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <Field label="Employee *">
              <select style={styles.input} value={editing.employee_id || ''} onChange={e => setEditing({ ...editing, employee_id: e.target.value })}>
                <option value="">— Pick employee ({employees.filter(e => e.is_active !== false).length} available) —</option>
                {employees.filter(e => e.is_active !== false).map(emp => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}
              </select>
            </Field>
            <Field label="Cert Type *">
              <input style={styles.input} list="cert-types" placeholder="OSHA 10, H2S, Fall Protection..." value={editing.cert_type || ''} onChange={e => setEditing({ ...editing, cert_type: e.target.value })} />
              <datalist id="cert-types">
                <option>OSHA 10</option>
                <option>OSHA 30</option>
                <option>H2S</option>
                <option>Confined Space</option>
                <option>Fall Protection</option>
                <option>CPR/First Aid</option>
                <option>Respirator Fit Test</option>
                <option>Forklift</option>
                <option>Aerial Lift</option>
                <option>BAPE (Breathing Air)</option>
                <option>Rescue</option>
              </datalist>
            </Field>
            <Field label="Issued Date"><input type="date" min="2000-01-01" max="2100-12-31" style={styles.input} value={editing.issued_date || ''} onChange={e => setEditing({ ...editing, issued_date: e.target.value })} /></Field>
            <Field label="Expiry Date"><input type="date" min="2000-01-01" max="2100-12-31" style={styles.input} value={editing.expiry_date || ''} onChange={e => setEditing({ ...editing, expiry_date: e.target.value })} /></Field>
            <Field label="Certificate #"><input style={styles.input} value={editing.certificate_no || ''} onChange={e => setEditing({ ...editing, certificate_no: e.target.value })} /></Field>
            <Field label="Issuer"><input style={styles.input} value={editing.issuer || ''} onChange={e => setEditing({ ...editing, issuer: e.target.value })} /></Field>
            <div style={{ gridColumn: '1 / -1' }}>
              <Field label="Notes"><textarea style={{ ...styles.input, minHeight: 50 }} value={editing.notes || ''} onChange={e => setEditing({ ...editing, notes: e.target.value })} /></Field>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button style={styles.btnSecondary} onClick={() => setEditing(null)}>Cancel</button>
            <button style={styles.btn} onClick={save}>{editing.id ? 'Save' : 'Create'}</button>
          </div>
        </div>
      )}
      <div style={styles.card}>
        <ActiveFiltersBar filters={filters} clearFilter={clearFilter} clearAll={clearAll} colLabels={TRAINING_COL_LABELS} />
        <div style={{ overflowX: 'auto' }}>
        <table style={{ ...styles.table, fontSize: 13 }}>
          <thead><tr>
            <SortableFilterTh label="Employee" {...headerProps('employee')} />
            <SortableFilterTh label="Certification" {...headerProps('cert')} />
            <SortableFilterTh label="Issued" {...headerProps('issued')} />
            <SortableFilterTh label="Expires" {...headerProps('expires')} />
            <SortableFilterTh label="Cert #" {...headerProps('cert_no')} />
            <SortableFilterTh label="Issuer" {...headerProps('issuer')} />
            <th style={styles.th}></th>
          </tr></thead>
          <tbody>
            {displayRows.map(t => (
              <tr key={t.id}>
                <td style={{ ...styles.td, fontWeight: 600, ...expiryNameStyle(t.expiry_date) }}>
                  <button onClick={() => goToRoster(t.employee_name)} title="Open this employee's roster record"
                    style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 'inherit', color: 'inherit', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2 }}>
                    {t.employee_name}
                  </button>
                </td>
                <td style={styles.td}>{t.cert_type}</td>
                <td style={{ ...styles.td, fontSize: 12 }}>{fmtDate(t.issued_date)}</td>
                <td style={styles.td}>{expiryChip(t.expiry_date)}</td>
                <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 11 }}>{t.certificate_no || '—'}</td>
                <td style={{ ...styles.td, fontSize: 12 }}>{t.issuer || '—'}</td>
                <td style={styles.td}>
                  <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 8px' }} onClick={() => startEdit(t)}>Edit</button>
                  {' '}
                  <button style={{ ...styles.btnDanger, fontSize: 11, padding: '4px 8px' }} onClick={() => remove(t.id)}>Del</button>
                </td>
              </tr>
            ))}
            {displayRows.length === 0 && (
              <tr><td colSpan={7} style={{ ...styles.td, textAlign: 'center', color: '#6B7280' }}>
                {rows.length === 0 ? 'No certifications recorded yet.' : 'No certifications match the filters.'}
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
      </>}
    </>
  );
}

// ===== Allocations =======================================================

const VERIFY_KEYS = [
  ['verify_safety_council', 'Safety Council'],
  ['verify_disa',           'DISA'],
  ['verify_drug_test',      'Drug Test'],
  ['verify_pd_setup',       'PD Setup'],
  ['verify_hotel_booked',   'Hotel'],
  ['verify_flt_out',        'Flt Out'],
  ['verify_flt_return',     'Flt Return'],
  ['verify_travel_conf',    'Travel'],
  ['verify_emp_notified',   'Notified'],
  ['verify_emrg_contact',   'Emrg'],
  ['verify_paylocity',      'Paylocity']
];

// computeContextualVerifications lives in utils/allocationDefaults.js (shared
// with the QuickCrewAdd modal, 2026-09-22) — imported above.

// Date-field helper: Postgres returns dates as ISO timestamps when
// serialized via JSON ("2026-06-04T00:00:00.000Z"), but <input type="date">
// only accepts YYYY-MM-DD. Slice to fix; null/undefined/empty stay empty.
const toDateInputValue = (v) => v ? String(v).slice(0, 10) : '';

const verifyChip = (status) => {
  const colorMap = {
    'Complete': { bg: '#D1FAE5', fg: '#065F46' },
    'Pending':  { bg: '#FEF3C7', fg: '#92400E' },
    'N/A':      { bg: '#F3F4F6', fg: '#6B7280' }
  };
  const c = colorMap[status] || colorMap['Pending'];
  return <span style={{ background: c.bg, color: c.fg, padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{status[0]}</span>;
};

// AllocationsTab is job-centric -- the default landing view is a list of
// every job with its allocation count + date span, not a flat 56-row table
// across all jobs. Click a job to drill into its allocations. From the
// drill-in view: Add/Edit/Delete allocations, see verification status per
// employee. "All Jobs (cross-view)" toggle still available for power use
// (e.g. "show me everyone allocated next week regardless of job").
function AllocationsTab() {
  const [allRows, setAllRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [crafts, setCrafts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  // Ad-hoc job creation modal -- for jobs that didn't flow through Sales
  // (emergency response, walk-up customer, etc.). Holds the in-progress
  // form state; null means modal closed.
  const [newJob, setNewJob] = useState(null);
  const [branches, setBranches] = useState([]);
  // selectedJobId: null = job picker view ; number = drill into that job ;
  // 'all' = flat cross-job table (escape hatch for power users).
  const [selectedJobId, setSelectedJobId] = useState(null);
  // Inline form for adding a new craft requirement to the current job.
  const [addingReq, setAddingReq] = useState(null);
  // Show archived (Complete / Lost / Closed) jobs in the picker. Default
  // false so the picker is the to-do list (Won + Active = action items).
  const [showArchived, setShowArchived] = useState(false);
  // Tracks which job's status is currently being updated (for spinner / disable).
  const [closingJobId, setClosingJobId] = useState(null);
  // Cross-view status filter: 'all' (default) | 'active' | 'archived'.
  // Only affects the All Jobs (cross-view) screen.
  const [crossViewStatus, setCrossViewStatus] = useState('all');
  const [rolled, toggleRolled] = useRollup('alloc');
  // Sales-side detail for the currently drilled-in job. Populated by an
  // effect that fires whenever selectedJobId changes to a numeric id, so
  // the sales context (AM, $, equipment, notes) is always pinned at the
  // top of the staffing view -- no extra clicks needed.
  const [salesDetail, setSalesDetail] = useState(null);
  const [salesDetailLoading, setSalesDetailLoading] = useState(false);
  // DISA records (one row per active employee, with NULL columns when no
  // record exists). Used to compute site-readiness in the warning banner.
  const [disaRows, setDisaRows] = useState([]);
  // useSearchParams from Operations parent -- used for the Roster jump
  // button on the readiness warning banner.
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep link (?tab=alloc&job=<id>) from the Job Hub lands on that job's
  // drill-in instead of the picker (George 2026-09-22: one screen fewer).
  const jobParam = searchParams.get('job');
  useEffect(() => {
    const j = Number(jobParam);
    if (Number.isInteger(j) && j > 0) setSelectedJobId(j);
  }, [jobParam]);
  // One-screen "+ Add crew" modal (QuickCrewAdd). Holds the picker/summary
  // row of the job it is adding to; null = closed.
  const [quickAdd, setQuickAdd] = useState(null);
  // Computed crew compliance (green/red vs the customer's site requirements)
  // for the drilled-in job. Null in picker/cross view — one job at a time.
  const jobComp = useJobCompliance(selectedJobId && selectedJobId !== 'all' ? selectedJobId : null);
  // The open modal's ALLOCATION id, not its entry: the entry is re-read from
  // the latest jobComp, so a badge added from the modal re-judges it too.
  const [compEntryId, setCompEntryId] = useState(null);
  // Layoff/separation paperwork (2026-07-17, replaces the Payroll app's form)
  const [separating, setSeparating] = useState(null);
  const compBy = new Map((jobComp?.crew || []).map(c => [c.allocation_id, c]));
  const compEntry = compEntryId != null ? compBy.get(compEntryId) || null : null;

  const reload = () => api.listAllocations().then(setAllRows).catch(e => setError(e.message));
  const reloadRequirements = () => api.listJobRequirements().then(setRequirements).catch(() => {});
  useEffect(() => { reload(); reloadRequirements(); }, []);
  // Crew editor payload (2026-07-30, George: "everyone manages crew in the
  // same location"): the drilled job's current-estimate Personnel window,
  // cost-walled server-side (utils/crewEditorShape.js). has_estimate:false —
  // ops-manual / legacy jobs — keeps the fully editable legacy behavior.
  const [crewData, setCrewData] = useState(null);
  const crewRef = useRef(null);
  const reloadCrew = () => {
    if (selectedJobId && selectedJobId !== 'all') {
      api.getCrewEditor(selectedJobId).then(setCrewData).catch(() => setCrewData(null));
    } else setCrewData(null);
  };
  useEffect(() => { reloadCrew(); }, [selectedJobId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const hasEstimate = !!(crewData && crewData.has_estimate);
  // Load reference data WITHOUT server-side filters; filter client-side in
  // the dropdowns. Matches the Roster tab pattern (which works) and avoids
  // any server-side filter quirks.
  useEffect(() => {
    api.listEmployees().then(setEmployees).catch(err => console.warn('listEmployees failed:', err));
    api.listCrafts().then(setCrafts).catch(err => console.warn('listCrafts failed:', err));
    // /api/projects is denyOps (sales data) — ops users got a silent 403 and
    // an empty job picker ("No jobs to staff yet") on the very tab that is
    // their job. Fall back to the ops feed, which carries the same fields
    // the picker needs (id/status/site/job_number/date_planned/duration).
    api.listProjects({})
      .then(setProjects)
      .catch(() => api.opsFeed({}).then(setProjects).catch(err => console.warn('project list failed:', err)));
    api.listDisa().then(setDisaRows).catch(err => console.warn('listDisa failed:', err));
    api.listBranches().then(setBranches).catch(err => console.warn('listBranches failed:', err));
  }, []);

  const startCreate = (preselectProjectId, preselectCraft) => {
    // Inherit dates from the job: start_date = job's planned_date,
    // end_date = planned_date + duration_days. The user can override
    // per-employee on the form (rotations, partial assignments). Job
    // dates are the source of truth -- allocation dates default from them.
    const proj = preselectProjectId ? projects.find(p => String(p.id) === String(preselectProjectId)) : null;
    let startDate = '';
    let endDate = '';
    if (proj?.date_planned) {
      startDate = String(proj.date_planned).slice(0, 10);
      if (proj.duration_days) {
        const d = new Date(startDate + 'T00:00:00');
        d.setDate(d.getDate() + parseInt(proj.duration_days, 10));
        endDate = d.toISOString().slice(0, 10);
      }
    }
    const pdType = 'None';
    const travelType = 'Local (No Travel)';
    setEditing({
      project_id: preselectProjectId || '', employee_id: '', craft: preselectCraft || '', shift: 'Day',
      pay_st: '', pay_ot: '', bill_st: '', bill_ot: '',
      start_date: startDate, end_date: endDate,
      pd_type: pdType, pd_amount: '', hotel_amount: '', meal_amount: '', pd_start_date: '', pd_load: '',
      travel_type: travelType, travel_rate: '', bill_miles: '', pay_miles: '', rt_miles: '',
      hotel_in_date: '', hotel_out_date: '',
      // Always-applicable verifications start Pending; context-dependent
      // ones get N/A based on the default PD/Travel types (None / Local).
      verify_safety_council: 'Pending', verify_disa: 'Pending', verify_drug_test: 'Pending',
      verify_emp_notified: 'Pending', verify_emrg_contact: 'Pending', verify_paylocity: 'Pending',
      ...computeContextualVerifications(pdType, travelType),
      notes: ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const startEdit = (a) => {
    // Normalize the row before editing:
    //  - Slice ISO timestamps to YYYY-MM-DD so date inputs render the
    //    saved value (Postgres dates serialize to "2026-06-04T00:00:00.000Z"
    //    which date inputs reject).
    //  - If start/end dates are missing, default-fill from the job (same
    //    rule as create -- job dates own, employee dates inherit).
    //  - Re-apply contextual verifications so a row whose PD Type was
    //    changed since save (or imported from spreadsheet) shows N/A on
    //    the right fields.
    const proj = projects.find(p => String(p.id) === String(a.project_id));
    let startDate = toDateInputValue(a.start_date);
    let endDate = toDateInputValue(a.end_date);
    if (!startDate && proj?.date_planned) {
      startDate = toDateInputValue(proj.date_planned);
    }
    if (!endDate && proj?.date_planned && proj?.duration_days) {
      const d = new Date(toDateInputValue(proj.date_planned) + 'T00:00:00');
      d.setDate(d.getDate() + parseInt(proj.duration_days, 10));
      endDate = d.toISOString().slice(0, 10);
    }
    setEditing({
      ...a,
      start_date: startDate,
      end_date: endDate,
      pd_start_date: toDateInputValue(a.pd_start_date),
      hotel_in_date: toDateInputValue(a.hotel_in_date),
      hotel_out_date: toDateInputValue(a.hotel_out_date),
      ...computeContextualVerifications(a.pd_type, a.travel_type, a),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const save = async () => {
    setError('');
    try {
      if (editing.id) await api.updateAllocation(editing.id, editing);
      else await api.createAllocation(editing);
      setEditing(null);
      reload();
    } catch (err) { setError(err.message); }
  };
  // "Save & Add Another" -- saves the current allocation, then resets only
  // the per-employee fields (employee, pay rates, verifications) while
  // keeping everything that's typically the same across multiple people on
  // the same craft (project, craft, shift, dates, per diem, travel). One
  // click per employee instead of re-opening the form from scratch.
  const saveAndAddAnother = async () => {
    setError('');
    try {
      if (editing.id) await api.updateAllocation(editing.id, editing);
      else await api.createAllocation(editing);
      reload();
      // Keep job/craft/shift/dates/per-diem/travel; clear per-person fields.
      // Verification reset respects the kept PD/Travel context so we don't
      // silently re-Pending fields that should stay N/A.
      setEditing({
        ...editing,
        id: undefined,
        employee_id: '',
        pay_st: '', pay_ot: '', bill_st: '', bill_ot: '',
        verify_safety_council: 'Pending', verify_disa: 'Pending', verify_drug_test: 'Pending',
        verify_emp_notified: 'Pending', verify_emrg_contact: 'Pending', verify_paylocity: 'Pending',
        ...computeContextualVerifications(editing.pd_type, editing.travel_type),
        notes: '',
      });
    } catch (err) { setError(err.message); }
  };
  const remove = async (id) => {
    if (!confirm('Delete this allocation?')) return;
    try { await api.deleteAllocation(id); reload(); }
    catch (err) { setError(err.message); }
  };

  // ---- Swap the person on an assignment (Staffing Plan ✎) ---------------
  // An estimate seat is renamed through the Crew editor save (the estimate is
  // where that name lives; the forward sync moves the assignment with it).
  // Anything else (off-plan, beyond-plan, no-estimate job) swaps employee_id
  // on the assignment row. Returns true on success.
  const swapEmployee = async (alloc, newName) => {
    setError('');
    const emp = employees.find(e => (e.full_name || '').trim().toLowerCase() === newName.toLowerCase());
    try {
      if (hasEstimate && alloc.origin === 'estimate') {
        const { slots, init } = buildCrewSlots(crewData);
        const same = (x, y) => String(x || '').trim().toLowerCase() === String(y || '').trim().toLowerCase();
        const hit = slots.find(s => same((init[s.key] || {}).name, alloc.employee_name) && same(s.craft, alloc.craft))
          || slots.find(s => same((init[s.key] || {}).name, alloc.employee_name));
        if (!hit) { setError(`Couldn't find ${alloc.employee_name}'s seat on the estimate — change it in the Crew section below.`); return false; }
        const personnel = slots.map(s => {
          const v = s.key === hit.key
            ? { name: emp ? emp.full_name : newName, email: emp ? emp.email || '' : '', phone: emp ? emp.phone || '' : '' }
            : (init[s.key] || {});
          return { labor_line_id: s.labor_line_id, shift: s.shift, slot_index: s.slot_index, name: v.name || '', email: v.email || '', phone: v.phone || '' };
        });
        await api.saveCrewEditor(selectedJobId, personnel);
      } else {
        if (!emp) { setError(`"${newName}" isn't on the roster — add them on the Roster tab first, or pick a name from the list.`); return false; }
        await api.updateAllocation(alloc.id, { employee_id: emp.id });
      }
      reload(); reloadCrew(); reloadRequirements();
      return true;
    } catch (err) { setError(err.message); return false; }
  };

  // ---- Job craft requirements (staffing plan) handlers -------------------
  const saveReq = async (r) => {
    setError('');
    try {
      if (r.id) await api.updateJobRequirement(r.id, r);
      else await api.createJobRequirement(r);
      setAddingReq(null);
      reloadRequirements();
    } catch (err) { setError(err.message); }
  };
  const removeReq = async (id, craft) => {
    if (!confirm(`Remove the "${craft}" requirement from this job? (Existing assignments to this craft are NOT affected.)`)) return;
    try { await api.deleteJobRequirement(id); reloadRequirements(); }
    catch (err) { setError(err.message); }
  };

  // ---- Change a job's status (inline dropdown + Close Job button) -------
  // Reuses the operations status-update endpoint that the Active Jobs tab
  // already uses. Both the dropdown and the Close Job button funnel
  // through here. Refreshes the projects list afterward so the picker
  // reflects the new status (filter applied, archive moved, etc.).
  const updateJobStatus = async (projectId, newStatus, jobNumber) => {
    setClosingJobId(projectId);
    setError('');
    try {
      await api.opsUpdateStatus(projectId, newStatus);
      // reloadProjects (below) falls back to the ops feed: /api/projects is
      // denyOps. Declared later, but only called at click time — safe.
      await reloadProjects();
    } catch (err) { setError(err.message); }
    finally { setClosingJobId(null); }
  };
  const closeJob = async (projectId, jobNumber) => {
    if (!confirm(`Close out job ${jobNumber}? It will be marked Complete and move to the archived list.`)) return;
    await updateJobStatus(projectId, 'Complete', jobNumber);
  };
  // Pull a job back out of the archive. Defaults to Active (re-opened work
  // is almost always work that's actually happening or about to). Status
  // can be flipped to Won later via the Active Jobs tab if it turns out
  // to not have started yet.
  const reopenJob = async (projectId, jobNumber) => {
    if (!confirm(`Reopen job ${jobNumber}? It will be marked Active and move back to the action-items list.`)) return;
    await updateJobStatus(projectId, 'Active', jobNumber);
  };

  // ---- Ad-hoc new job (Operations-side project create) -------------------
  // Open the New Job modal with sensible defaults: status Active (Ops only
  // creates jobs that are actually happening), planned date = today,
  // duration 30 days. User overrides any of these in the form.
  const startNewJob = () => {
    const today = new Date().toISOString().slice(0, 10);
    setNewJob({
      job_number: '', site: '', branch_id: '', status: 'Active',
      date_planned: today, duration_days: '', equipment_type: '', notes: '',
    });
  };
  const saveNewJob = async () => {
    setError('');
    try {
      const created = await api.opsCreateProject(newJob);
      // Reload the projects list so the new job appears in the picker
      // (ops-feed fallback, same as updateJobStatus).
      await reloadProjects();
      setNewJob(null);
      // Drill straight into the new job so the user can immediately add
      // staffing requirements + assignments.
      setSelectedJobId(created.id);
    } catch (err) { setError(err.message); }
  };

  // ---- Inline planned-date edit on the picker rows -----------------------
  // Same gating spinner as the status / job-number edits (closingJobId
  // doubles as a generic "row is saving" lock so multiple inline edits
  // can't fight over the same row).
  //
  // /api/projects is denyOps, so the reload falls back to the ops feed the
  // same way the mount effect does — otherwise an ops user's date edit saved
  // fine but painted a 403 in the error banner.
  const reloadProjects = () =>
    api.listProjects({}).catch(() => api.opsFeed({})).then(setProjects);

  // Crew date sync (George 2026-07-29) — same prompt as the Active Jobs feed;
  // this is the tab where the crew actually lives, so it matters most here.
  const [crewSync, setCrewSync] = useState(null);
  const offerCrewSync = async (project, kind, deltaDays, changeLabel) => {
    try {
      const plan = await api.opsPlanCrewDates(project.id, {
        mode: 'shift', kind, delta_days: deltaDays, include_travel: false,
      });
      if (plan.rows?.length) {
        setCrewSync({
          projectId: project.id,
          jobLabel: project.job_number || project.site || `Project ${project.id}`,
          kind, deltaDays, changeLabel, plan,
        });
      }
    } catch (err) { console.warn('crew date plan failed:', err.message); }
  };

  const updateJobPlannedDate = async (projectId, dateValue) => {
    const before = projects.find(p => String(p.id) === String(projectId));
    const oldIso = before?.date_planned ? String(before.date_planned).slice(0, 10) : '';
    setClosingJobId(projectId);
    setError('');
    try {
      await api.opsUpdatePlannedDate(projectId, dateValue);
      const delta = oldIso ? daysBetweenIso(oldIso, dateValue) : 0;
      offerCrewSync(before || { id: projectId }, 'start', delta, oldIso
        ? `job start moved ${fmtDate(oldIso)} → ${fmtDate(dateValue)} (${signedDays(delta)} days).`
        : `job start set to ${fmtDate(dateValue)}.`);
      await reloadProjects();
    } catch (err) { setError(err.message); throw err; }
    finally { setClosingJobId(null); }
  };
  const updateJobDuration = async (projectId, days) => {
    const before = projects.find(p => String(p.id) === String(projectId));
    const startIso = before?.date_planned ? String(before.date_planned).slice(0, 10) : '';
    const oldDur = parseInt(before?.duration_days, 10) || 0;
    setClosingJobId(projectId);
    setError('');
    try {
      await api.opsUpdateDuration(projectId, days);
      // Duration moves the END only — start dates stay put.
      const newDur = parseInt(days, 10) || 0;
      const oldEnd = startIso && oldDur ? addDaysIso(startIso, oldDur) : '';
      const newEnd = startIso && newDur ? addDaysIso(startIso, newDur) : '';
      const delta = (oldDur && newDur) ? newDur - oldDur : 0;
      offerCrewSync(before || { id: projectId }, 'end', delta, (oldEnd && newEnd)
        ? `job end moved ${fmtDate(oldEnd)} → ${fmtDate(newEnd)} (${signedDays(delta)} days).`
        : `job duration set to ${newDur || '—'} days.`);
      await reloadProjects();
    } catch (err) { setError(err.message); throw err; }
    finally { setClosingJobId(null); }
  };

  // ---- Sales-detail auto-load on drill-in --------------------------------
  // Whenever the user picks a job (selectedJobId becomes a number), pull the
  // full sales-side record so the inline SalesDetailCard at the top of the
  // staffing view can render. Resets to null on the picker view or the
  // cross-job 'all' view. Ops doesn't have access to /api/projects directly
  // (denyOps middleware), so we call the operations-scoped endpoint at
  // GET /api/operations/projects/:id.
  useEffect(() => {
    if (!selectedJobId || selectedJobId === 'all') { setSalesDetail(null); return; }
    let cancelled = false;
    setSalesDetailLoading(true);
    api.opsGetProject(selectedJobId)
      .then(p => { if (!cancelled) setSalesDetail(p); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setSalesDetailLoading(false); });
    return () => { cancelled = true; };
  }, [selectedJobId]);

  // ---- Compute per-job summaries (allocation count, date span, pending) ---
  // Aggregated client-side from allRows + projects so the picker view is
  // fast and doesn't need a separate endpoint. Includes jobs with ZERO
  // allocations so admin can drill in and add the first one. Each summary
  // row also gets the project status so the picker can filter to action
  // items (Won + Active) by default and archive Complete/Lost/Closed.
  const projectById = new Map(projects.map(p => [p.id, p]));
  // Per-project sum of staffing-plan qty so the picker can show
  // "filled / required" with a warning chip when understaffed. Also
  // build a per-project map of craft → required qty so the chip's
  // tooltip can show the breakdown (and which crafts are still open).
  // NOTE: the DB column is `needed`, NOT `qty` -- previously this used
  // r.qty and silently summed to 0 for every project, so the picker
  // chip always read "no plan" even when a plan was defined.
  const requiredByProject = new Map();
  const requirementsByProject = new Map();
  for (const r of requirements) {
    const n = r.needed || 0;
    const cur = requiredByProject.get(r.project_id) || 0;
    requiredByProject.set(r.project_id, cur + n);
    if (!requirementsByProject.has(r.project_id)) requirementsByProject.set(r.project_id, new Map());
    requirementsByProject.get(r.project_id).set(r.craft, (requirementsByProject.get(r.project_id).get(r.craft) || 0) + n);
  }
  const jobSummaries = (() => {
    const byProject = new Map();
    for (const a of allRows) {
      const p = projectById.get(a.project_id) || {};
      // Gate (listedInAllocations): a Won / Active / Mobilization Standby job
      // shows with or without a job # (George 2026-09-30); any other status
      // needs the project-side job_number, so a stale spreadsheet allocation
      // on a lost bid stays out.
      if (!listedInAllocations(p)) continue;
      if (!byProject.has(a.project_id)) {
        byProject.set(a.project_id, {
          project_id: a.project_id, job_number: p.job_number,
          site: a.project_site, date_planned: p.date_planned || a.project_date_planned,
          duration_days: p.duration_days,
          status: p.status || 'Active',
          count: 0, minStart: null, maxEnd: null, pendingVerifications: 0,
          craftCounts: new Map(),
        });
      }
      const s = byProject.get(a.project_id);
      s.count++;
      const craftLabel = a.craft || '(no craft)';
      s.craftCounts.set(craftLabel, (s.craftCounts.get(craftLabel) || 0) + 1);
      if (a.start_date && (!s.minStart || a.start_date < s.minStart)) s.minStart = a.start_date;
      if (a.end_date && (!s.maxEnd || a.end_date > s.maxEnd)) s.maxEnd = a.end_date;
      for (const [k] of VERIFY_KEYS) {
        if ((a[k] || 'Pending') === 'Pending') s.pendingVerifications++;
      }
    }
    // Add jobs with zero allocations (so admin can drill in to add the first).
    // Same gate: listedInAllocations (job # OR Won/Active/Standby).
    for (const p of projects) {
      if (!listedInAllocations(p)) continue;
      if (!byProject.has(p.id)) {
        byProject.set(p.id, {
          project_id: p.id, job_number: p.job_number,
          site: p.site, date_planned: p.date_planned,
          duration_days: p.duration_days,
          status: p.status || 'Active',
          count: 0, minStart: null, maxEnd: null, pendingVerifications: 0,
          craftCounts: new Map(),
        });
      }
    }
    // Sort priority:
    //   1. Status: Active (happening now) before Won (future) before
    //      anything else (Complete/Lost/Closed only show in archive view).
    //   2. Within Active: earliest planned date first (so the closest-to-
    //      done work bubbles up).
    //   3. Within Won: earliest planned date first (next-up work first).
    //   4. Tiebreaker: job_number ascending.
    const STATUS_RANK = { Active: 0, 'Mobilization Standby': 1, Won: 2, Complete: 3, Lost: 4, Closed: 5 };
    return Array.from(byProject.values()).sort((a, b) => {
      const ra = STATUS_RANK[a.status] ?? 99;
      const rb = STATUS_RANK[b.status] ?? 99;
      if (ra !== rb) return ra - rb;
      // Same status -- sort by planned date (NULLs last), then job number.
      const da = a.date_planned ? a.date_planned.slice(0, 10) : '￿';
      const db = b.date_planned ? b.date_planned.slice(0, 10) : '￿';
      if (da !== db) return da < db ? -1 : 1;
      return (a.job_number || '').localeCompare(b.job_number || '');
    });
  })();

  // Action-item filter: default view shows Won + Active jobs (the ones
  // that need staffing attention). Archived view shows Complete + Lost
  // + Closed. The toggle in the picker header flips between them.
  const ACTIVE_STATUSES = new Set(['Won', 'Active', 'Mobilization Standby']);
  const ARCHIVED_STATUSES = new Set(['Complete', 'Lost', 'Closed']);
  const visibleJobSummaries = jobSummaries.filter(j =>
    showArchived ? ARCHIVED_STATUSES.has(j.status) : ACTIVE_STATUSES.has(j.status)
  );
  const archivedCount = jobSummaries.filter(j => ARCHIVED_STATUSES.has(j.status)).length;
  const activeCount = jobSummaries.filter(j => ACTIVE_STATUSES.has(j.status)).length;

  // Time-based partitions of the action-items view so the picker reads as
  // urgency-ordered blocks rather than one long flat list. All jobs here
  // have status Active or Won; the partition decides which section.
  // Past-due Won jobs (date < today) fall into the "next 7 days" bucket --
  // they're equally urgent and the cron will flip them to Active overnight.
  const sevenStr = (() => {
    const d = new Date(); d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  })();
  const thirtyStr = (() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  })();
  const sectionActive = visibleJobSummaries.filter(j => j.status === 'Active');
  const sectionStandby = visibleJobSummaries.filter(j => isStandby(j.status));
  const sectionSeven  = visibleJobSummaries.filter(j =>
    j.status === 'Won' && j.date_planned && j.date_planned.slice(0, 10) <= sevenStr);
  const sectionThirty = visibleJobSummaries.filter(j =>
    j.status === 'Won' && j.date_planned &&
    j.date_planned.slice(0, 10) > sevenStr &&
    j.date_planned.slice(0, 10) <= thirtyStr);
  const sectionBeyond = visibleJobSummaries.filter(j =>
    j.status === 'Won' && (!j.date_planned || j.date_planned.slice(0, 10) > thirtyStr));

  // ---- Employee site-readiness check -------------------------------------
  // An "unready" employee is active but missing data Ops needs before
  // assigning them to a job. Three failure modes:
  //   (1) no pay rate (allocations would default to $0)
  //   (2) no/inactive Paylocity status (can't be paid)
  //   (3) no DISA record OR drug test / background check expired (legally
  //       can't be deployed at most chemical sites)
  // The DISA endpoint LEFT JOINs employees to disa records and only returns
  // active employees, so we can match on employee_id and treat a row with
  // no disa_id as "no DISA record at all".
  const today = new Date().toISOString().slice(0, 10);
  const disaByEmpId = new Map(disaRows.map(d => [d.employee_id, d]));
  const activeEmployees = employees.filter(e => e.is_active !== false);
  const unreadyEmployees = activeEmployees.filter(e => {
    const noPayRate = !e.pay_st_default || parseFloat(e.pay_st_default) === 0;
    const noPaylocity = !e.paylocity_status || e.paylocity_status === 'Not set';
    const d = disaByEmpId.get(e.id);
    const noDisa = !d || !d.disa_id;
    const drugExpired = d && d.drug_test_expiry && d.drug_test_expiry.slice(0, 10) < today;
    const bgExpired = d && d.background_check_expiry && d.background_check_expiry.slice(0, 10) < today;
    return noPayRate || noPaylocity || noDisa || drugExpired || bgExpired;
  });

  // Filter rows for the current view.
  const viewRows = selectedJobId === 'all'
    ? allRows.filter(a => {
        if (crossViewStatus === 'all') return true;
        const p = projectById.get(a.project_id);
        const status = p?.status || 'Active';
        if (crossViewStatus === 'active')   return ACTIVE_STATUSES.has(status);
        if (crossViewStatus === 'archived') return ARCHIVED_STATUSES.has(status);
        return true;
      })
    : selectedJobId
      ? allRows.filter(a => a.project_id === selectedJobId)
      : [];
  const selectedJob = selectedJobId && selectedJobId !== 'all'
    ? jobSummaries.find(j => j.project_id === selectedJobId)
    : null;

  // ===== Render =========================================================
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <RollupH2 collapsed={rolled} onToggle={toggleRolled} style={{ margin: 0 }}>
          Allocations
          {selectedJob && (
            <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
              · <span style={{ fontFamily: 'monospace' }}>{selectedJob.job_number || '(no job #)'}</span>
              {' — '}{selectedJob.site}
            </span>
          )}
          {selectedJobId === 'all' && (
            <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
              · All Jobs · {allRows.length} assignment{allRows.length === 1 ? '' : 's'}
            </span>
          )}
          {!selectedJobId && (
            <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>
              · {showArchived
                  ? `${archivedCount} archived job${archivedCount === 1 ? '' : 's'}`
                  : `${activeCount} active + upcoming job${activeCount === 1 ? '' : 's'} needing attention`}
            </span>
          )}
        </RollupH2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <UploadStatusBadge statusKey="last_workforce_import" />
          {!selectedJobId && (
            <>
              <button
                style={showArchived ? styles.tabBtnActive : styles.tabBtn}
                onClick={() => setShowArchived(!showArchived)}
                title="Toggle between active+upcoming jobs and the archived (complete/lost/closed) list"
              >
                {showArchived ? '← Back to Action Items' : `🗄 Archived (${archivedCount})`}
              </button>
              <button style={styles.btn} onClick={startNewJob} title="Create a new ad-hoc job (for work that didn't flow through Sales -- emergency response, walk-up customer, etc.)">+ New Job</button>
              <button style={styles.btnSecondary} onClick={() => setSelectedJobId('all')}>📋 All Jobs (cross-view)</button>
            </>
          )}
          {selectedJobId && (
            <>
              <button style={styles.btnSecondary} onClick={() => { setSelectedJobId(null); setEditing(null); setAddingReq(null); }}>← Back to all jobs</button>
              {selectedJob && ARCHIVED_STATUSES.has(selectedJob.status) && (
                <button
                  style={{ ...styles.btnSecondary, color: '#065F46', borderColor: '#10B981' }}
                  disabled={closingJobId === selectedJob.project_id}
                  onClick={() => reopenJob(selectedJob.project_id, selectedJob.job_number)}
                  title="This job is archived. Click to reopen -- marks Active and brings it back to the action-items list."
                >
                  {closingJobId === selectedJob.project_id ? '…' : '↻ Reopen Job'}
                </button>
              )}
              {selectedJob && (
                <button style={styles.btn} onClick={() => setQuickAdd(selectedJob)}
                  title="One screen: type the name, pick the open slot, Add. Fills estimate slots (updates the plan) or adds a plain assignment.">
                  + Add crew
                </button>
              )}
              {selectedJob && (
                // No site param: Site Access judges the job on its own site.
                <button style={styles.btnSecondary} disabled={!jobComp?.customer?.id}
                  title={jobComp?.customer?.id
                    ? "This job's crew on Site Access: what each person is missing, top blockers, CSV."
                    : 'No customer Site Access can resolve for this job yet (set the facility on the Job Hub).'}
                  onClick={() => setSearchParams(new URLSearchParams({
                    tab: 'siteaccess', customer: String(jobComp.customer.id), job: String(selectedJob.project_id),
                  }))}>
                  🔑 Open in Site Access
                </button>
              )}
              <button style={styles.btnSecondary} onClick={() => startCreate(selectedJob ? selectedJob.project_id : '')}
                title={hasEstimate ? 'The full assignment form: adds someone OUTSIDE the estimate crew (beyond plan) with per diem / travel / verifications up front. The estimate crew itself is managed in the Crew section below.' : 'The full assignment form with per diem / travel / verifications up front.'}>
                {hasEstimate ? '+ Add Beyond Plan (full form)' : '+ Assign Employee (full form)'}
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {quickAdd && (
        <QuickCrewAdd
          projectId={quickAdd.project_id}
          job={quickAdd}
          employees={employees}
          onClose={() => setQuickAdd(null)}
          onSaved={() => { reload(); reloadRequirements(); reloadCrew(); }}
        />
      )}

      {!rolled && <>
      {/* Allocation form -- only renders when editing/creating. */}
      {editing && (
        <AllocationForm
          allocation={editing}
          onChange={setEditing}
          employees={employees}
          crafts={crafts}
          projects={projects}
          onSave={save}
          onSaveAndAddAnother={saveAndAddAnother}
          onCancel={() => setEditing(null)}
          requirements={requirements}
          allocations={allRows}
          lockIdentity={hasEstimate && !!editing.id && editing.origin === 'estimate'}
        />
      )}

      {/* Job picker view (default) */}
      {!selectedJobId && (
        <div style={styles.card}>
          {/* Site-readiness warning -- counts active employees missing pay
              rate, Paylocity status, or current DISA. Hidden when zero. */}
          {!showArchived && unreadyEmployees.length > 0 && (
            <div style={{
              background: '#FEF3C7', border: '1px solid #F59E0B',
              color: '#78350F', padding: 12, borderRadius: 6,
              marginBottom: 12, fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 8,
            }}>
              <div>
                <strong>⚠ {unreadyEmployees.length} of {activeEmployees.length} employees aren't site-ready</strong>
                <div style={{ fontSize: 12, marginTop: 4, color: '#92400E' }}>
                  Missing pay rate, Paylocity status, or current DISA / drug test / background check.
                  Allocations to these employees will be incomplete and may not pass site verification.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  style={{ ...styles.btnSecondary, fontSize: 12 }}
                  onClick={() => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('tab', 'roster'); return n; })}
                >Go to Roster →</button>
                <button
                  style={{ ...styles.btnSecondary, fontSize: 12 }}
                  onClick={() => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('tab', 'disa'); return n; })}
                >Go to DISA →</button>
              </div>
            </div>
          )}
          {/* Help banner -- explains the picker only on the action-item view. */}
          {!showArchived && visibleJobSummaries.length > 0 && (
            <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', color: '#075985', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 12 }}>
              💡 Won, Active and Mobilization Standby jobs land here, with or without a <strong>Job #</strong> (assign one on the <strong>Active Jobs</strong> tab). Click any row to manage its staffing plan and assignments. When a job is finished, click <strong>Close Job</strong> to mark it Complete and move it to the archive.
            </div>
          )}
          {visibleJobSummaries.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#6B7280' }}>
              {showArchived
                ? 'No archived jobs yet. Closed jobs (status Complete / Lost / Closed) appear here.'
                : (
                  <>
                    <div style={{ fontSize: 14, marginBottom: 6 }}>No jobs to staff yet.</div>
                    <div style={{ fontSize: 12 }}>
                      Jobs appear here once Ops assigns a <strong>Job #</strong> on the <strong>Active Jobs</strong> tab. Sales books the deal → Ops claims it with a job number → it shows up here for staffing.
                    </div>
                  </>
                )}
            </div>
          ) : showArchived ? (
            // Archived view -- one flat table (no time partitioning).
            renderJobsTable(visibleJobSummaries, {
              showArchived: true, closingJobId, setSelectedJobId,
              updateJobPlannedDate, updateJobDuration,
              requiredByProject, requirementsByProject,
              closeJob, reopenJob, quickAdd: setQuickAdd,
            })
          ) : (
            // Action-items view -- four time-based sections by urgency.
            <>
              <PickerSection
                title="🔴 Active — In Progress"
                count={sectionActive.length}
                accent="#3B82F6"
                emptyMsg="No jobs running right now."
              >
                {sectionActive.length > 0 && renderJobsTable(sectionActive, {
                  showArchived: false, closingJobId, setSelectedJobId,
                  updateJobPlannedDate, updateJobDuration,
                  requiredByProject, requirementsByProject,
                  closeJob, reopenJob, quickAdd: setQuickAdd,
                })}
              </PickerSection>

              <PickerSection
                title="⏱ Mobilization Standby — Be Ready to Mobilize"
                count={sectionStandby.length}
                accent="#0E7490"
                hint="Not awarded yet, but the award could come at the last minute. Staff these now so we can mobilize immediately. No job # until the award."
                emptyMsg="No jobs on Mobilization Standby."
              >
                {sectionStandby.length > 0 && renderJobsTable(sectionStandby, {
                  showArchived: false, closingJobId, setSelectedJobId,
                  updateJobPlannedDate, updateJobDuration,
                  requiredByProject, requirementsByProject,
                  closeJob, reopenJob, quickAdd: setQuickAdd,
                })}
              </PickerSection>

              <PickerSection
                title="🟠 Starting in 1–7 days"
                count={sectionSeven.length}
                accent="#EF4444"
                hint="Includes any past-due Won jobs (planned date already passed). Auto-cron will flip those to Active overnight."
                emptyMsg="Nothing starting in 1–7 days."
              >
                {sectionSeven.length > 0 && renderJobsTable(sectionSeven, {
                  showArchived: false, closingJobId, setSelectedJobId,
                  updateJobPlannedDate, updateJobDuration,
                  requiredByProject, requirementsByProject,
                  closeJob, reopenJob, quickAdd: setQuickAdd,
                })}
              </PickerSection>

              <PickerSection
                title="🟡 Starting in 8–30 days"
                count={sectionThirty.length}
                accent="#F59E0B"
                emptyMsg="Nothing scheduled to start between 8 and 30 days from today."
              >
                {sectionThirty.length > 0 && renderJobsTable(sectionThirty, {
                  showArchived: false, closingJobId, setSelectedJobId,
                  updateJobPlannedDate, updateJobDuration,
                  requiredByProject, requirementsByProject,
                  closeJob, reopenJob, quickAdd: setQuickAdd,
                })}
              </PickerSection>

              <PickerSection
                title="🟢 Starting in 30+ days (or undated)"
                count={sectionBeyond.length}
                accent="#10B981"
                emptyMsg="No further-out jobs scheduled."
              >
                {sectionBeyond.length > 0 && renderJobsTable(sectionBeyond, {
                  showArchived: false, closingJobId, setSelectedJobId,
                  updateJobPlannedDate, updateJobDuration,
                  requiredByProject, requirementsByProject,
                  closeJob, reopenJob, quickAdd: setQuickAdd,
                })}
              </PickerSection>
            </>
          )}
        </div>
      )}

      {/* Sales-side context card -- pinned above Staffing so Ops always
          sees the AM, $, equipment, and notes while assigning crew. */}
      {selectedJobId && selectedJobId !== 'all' && (
        <SalesDetailCard project={salesDetail} loading={salesDetailLoading} />
      )}

      {/* Site key contacts — customer-side names for the PM (2026-07-19). */}
      {selectedJobId && selectedJobId !== 'all' && (
        <SiteContactsCard projectId={selectedJobId} />
      )}

      {/* Staffing section (single-job drill-in only -- not in cross-view).
          Top of the drill-in view so admin sees the "what's needed vs
          what's filled" picture before scrolling to the assignment list. */}
      {selectedJobId && selectedJobId !== 'all' && (
        <StaffingSection
          projectId={selectedJobId}
          allocations={viewRows}
          requirements={requirements.filter(r => r.project_id === selectedJobId)}
          crafts={crafts}
          addingReq={addingReq}
          setAddingReq={setAddingReq}
          onSaveReq={saveReq}
          onRemoveReq={removeReq}
          onAssignToCraft={(craftName) => startCreate(selectedJobId, craftName)}
          readOnly={hasEstimate}
          onGoToCrew={() => crewRef.current && crewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          employees={employees}
          onSwapEmployee={swapEmployee}
        />
      )}

      {/* Crew editor (2026-07-30): for estimate-born jobs this is THE place
          crew names/craft/shift are managed — ops edits the estimate's
          Personnel slots (cost-walled window, same rows as Estimating →
          Personnel) and the assignments below sync automatically. */}
      {selectedJobId && selectedJobId !== 'all' && hasEstimate && (
        <CrewEditorCard
          projectId={selectedJobId}
          data={crewData}
          employees={employees}
          innerRef={crewRef}
          onSaved={() => { reload(); reloadCrew(); }}
        />
      )}

      {/* Drill-in view (single job OR cross-view) */}
      {selectedJobId && (
        <div style={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>
              👥 Assigned Employees
              <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
                · {viewRows.length} assignment{viewRows.length === 1 ? '' : 's'}
                {selectedJobId === 'all' ? ' across all jobs' : ' on this job'}
              </span>
            </h3>
            {/* Cross-view status filter pills -- decide whether the table
                shows allocations from active+won jobs, archived jobs, or all. */}
            {selectedJobId === 'all' && (
              <div style={{ display: 'flex', gap: 4 }}>
                {[
                  { v: 'all',      label: 'All' },
                  { v: 'active',   label: '🔴 Active + Won' },
                  { v: 'archived', label: '🗄 Archived' },
                ].map(opt => (
                  <button
                    key={opt.v}
                    onClick={() => setCrossViewStatus(opt.v)}
                    style={{
                      ...(crossViewStatus === opt.v ? styles.tabBtnActive : styles.tabBtn),
                      fontSize: 11, padding: '4px 10px',
                    }}
                  >{opt.label}</button>
                ))}
              </div>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
          <table style={{ ...styles.table, fontSize: 12, minWidth: 1100 }}>
            <thead><tr>
              {selectedJobId === 'all' && <th style={styles.th}>Job #</th>}
              {selectedJobId === 'all' && <th style={styles.th}>Job Status</th>}
              <th style={styles.th}>Employee</th>
              {selectedJobId !== 'all' && (
                <th style={styles.th} title="Computed vs the customer's site requirements — click a status for every reason">Site Ready</th>
              )}
              <th style={styles.th}>Craft</th>
              <th style={styles.th}>Shift</th>
              <th style={styles.th}>Start</th>
              <th style={styles.th}>End</th>
              <th style={styles.th}>PD Type</th>
              <th style={styles.th}>Travel</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Verifications</th>
              <th style={styles.th}></th>
            </tr></thead>
            <tbody>
              {viewRows.map(a => {
                const proj = projectById.get(a.project_id);
                const projStatus = proj?.status || 'Active';
                return (
                <tr key={a.id} style={ARCHIVED_STATUSES.has(projStatus) ? { background: '#FAFAFA' } : {}}>
                  {selectedJobId === 'all' && (
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 11 }}>{a.job_number || '—'}</td>
                  )}
                  {selectedJobId === 'all' && (
                    <td style={styles.td}><StatusPill status={projStatus} /></td>
                  )}
                  <td style={{ ...styles.td, fontWeight: 600 }}>
                    {a.employee_name}
                    {a.separated_at && (
                      <span title={`Separated ${fmtDate(a.separation_date || a.separated_at)}${a.separation_reason ? ' — ' + a.separation_reason : ''}`}
                        style={{ marginLeft: 6, background: '#FEE2E2', color: '#991B1B', fontSize: 10, fontWeight: 800, borderRadius: 4, padding: '1px 5px', verticalAlign: 'middle' }}>
                        LAID OFF
                      </span>
                    )}
                    {a.origin === 'fs' && (
                      <span title={`Added automatically from the Field Services crew roster${a.fs_position ? ` (${a.fs_position})` : ''} — FS owns this row's dates and shift; deleting it here re-adds it on the next FS push while they are onsite`}
                        style={{ marginLeft: 6, background: '#DBEAFE', color: '#1E40AF', fontSize: 10, fontWeight: 800, borderRadius: 4, padding: '1px 5px', verticalAlign: 'middle' }}>
                        ONSITE (FS)
                      </span>
                    )}
                    {hasEstimate && selectedJobId !== 'all' && a.origin !== 'estimate' && a.origin !== 'fs' && (
                      <span title="Added on the Allocations tab outside the estimate crew — the forward crew sync never touches this row"
                        style={{ marginLeft: 6, background: '#F3F4F6', color: '#374151', fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '1px 5px', verticalAlign: 'middle' }}>
                        BEYOND PLAN
                      </span>
                    )}
                    {a.fs_reported && !a.fs_onsite && !a.separated_at && a.origin !== 'fs' && (
                      <span title={`Not on the Field Services crew roster as of ${a.fs_as_of ? fmtDate(a.fs_as_of) : '—'} — dispatched here, but FS does not show this person onsite`}
                        style={{ marginLeft: 6, background: '#FEF3C7', color: '#92400E', fontSize: 10, fontWeight: 800, borderRadius: 4, padding: '1px 5px', verticalAlign: 'middle' }}>
                        NOT ONSITE
                      </span>
                    )}
                  </td>
                  {selectedJobId !== 'all' && (
                    <td style={styles.td}>
                      <ComplianceChip entry={compBy.get(a.id)} onClick={() => compBy.get(a.id) && setCompEntryId(a.id)} />
                    </td>
                  )}
                  <td style={styles.td}>{a.craft}</td>
                  <td style={styles.td}>{a.shift || '—'}</td>
                  <td style={styles.td}>{fmtDate(a.start_date)}</td>
                  <td style={styles.td}>{fmtDate(a.end_date)}</td>
                  <td style={styles.td}>{a.pd_type || '—'}</td>
                  <td style={styles.td}>{a.travel_type || '—'}</td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {VERIFY_KEYS.map(([k, label]) => (
                        <span key={k} title={label} style={{ display: 'inline-block' }}>{verifyChip(a[k] || 'Pending')}</span>
                      ))}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 8px' }} onClick={() => startEdit(a)}>Edit</button>
                    {' '}
                    {!a.separated_at && (
                      <>
                        <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 8px', color: '#991B1B', borderColor: '#FCA5A5' }}
                          title="Separate this employee from the job — emails the layoff notice to payroll"
                          onClick={() => setSeparating(a)}>Layoff</button>
                        {' '}
                      </>
                    )}
                    {/* Estimate-origin rows aren't deletable here (server 403s
                        too) — clearing the name in the Crew editor is what
                        takes someone off the job; Layoff handles separations. */}
                    {!(hasEstimate && a.origin === 'estimate') && (
                      <button style={{ ...styles.btnDanger, fontSize: 11, padding: '4px 8px' }} onClick={() => remove(a.id)}>Del</button>
                    )}
                  </td>
                </tr>
                );
              })}
              {viewRows.length === 0 && (
                <tr><td colSpan={selectedJobId === 'all' ? 11 : 10} style={{ ...styles.td, textAlign: 'center', color: '#6B7280' }}>
                  {selectedJobId === 'all'
                    ? (crossViewStatus === 'archived'
                        ? 'No allocations on archived jobs.'
                        : crossViewStatus === 'active'
                          ? 'No allocations on active or upcoming jobs.'
                          : 'No allocations yet across any job.')
                    : hasEstimate
                      ? 'No one on this job yet. Crew comes from the estimate — fill names in the Crew section above and they sync here on Save. "+ Add Beyond Plan" covers anyone outside the estimate crew.'
                      : 'No employees assigned to this job yet. Use the Staffing section above to define what crafts are needed, then click "+ Assign" on any open slot — or use "+ Assign Employee" at top right for a free-form assignment.'}
                </td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {compEntry && <ComplianceModal entry={compEntry} job={jobComp} onClose={() => setCompEntryId(null)} onChanged={() => jobComp?.reload?.()} />}
      {newJob && <NewJobModal job={newJob} branches={branches} onChange={setNewJob} onSave={saveNewJob} onCancel={() => setNewJob(null)} />}
      {separating && <SeparationModal alloc={separating} onDone={() => { setSeparating(null); reload(); }} onCancel={() => setSeparating(null)} />}
      {crewSync && (
        <CrewDateSyncModal
          projectId={crewSync.projectId}
          jobLabel={crewSync.jobLabel}
          changeLabel={crewSync.changeLabel}
          kind={crewSync.kind}
          deltaDays={crewSync.deltaDays}
          initialPlan={crewSync.plan}
          onClose={() => setCrewSync(null)}
          onApplied={() => { setCrewSync(null); reload(); }}
        />
      )}
      </>}
    </>
  );
}

// Site Key Contacts (2026-07-19, George): per-job customer-side contacts —
// name / title / company / email / phone — entered here by ops and mirrored
// read-only to the Field Services PM (CRM Overview page) via integration.js.
function SiteContactsCard({ projectId }) {
  const [contacts, setContacts] = useState([]);
  const [editing, setEditing] = useState(null);   // null | {} (new) | contact
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = () => api.listSiteContacts(projectId).then(setContacts).catch(() => {});
  useEffect(() => { load(); }, [projectId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const open = (c) => { setForm(c ? { ...c } : { name: '', title: '', company: '', email: '', phone: '', notes: '' }); setEditing(c || {}); setErr(''); };
  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const save = async () => {
    if (!(form.name || '').trim()) { setErr('Name is required.'); return; }
    setSaving(true);
    setErr('');
    try {
      if (form.id) await api.updateSiteContact(form.id, form);
      else await api.createSiteContact(projectId, form);
      setEditing(null);
      load();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove site contact ${c.name}?`)) return;
    try { await api.deleteSiteContact(c.id); load(); } catch (e) { alert(e.message); }
  };

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>
          📇 Site Key Contacts
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
            customer-side names the PM needs — shows on the Field Services job automatically
          </span>
        </h3>
        <button style={{ ...styles.btnSmall || styles.btn, fontSize: 12 }} onClick={() => open(null)}>+ Add Site Contact</button>
      </div>
      {!contacts.length ? (
        <div style={{ color: '#6B7280', fontSize: 13 }}>No site contacts yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...styles.table, fontSize: 12 }}>
            <thead><tr>
              <th style={styles.th}>Name</th><th style={styles.th}>Title</th><th style={styles.th}>Company</th>
              <th style={styles.th}>Email</th><th style={styles.th}>Phone</th><th style={styles.th}></th>
            </tr></thead>
            <tbody>
              {contacts.map(c => (
                <tr key={c.id}>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{c.name}</td>
                  <td style={styles.td}>{c.title || '—'}</td>
                  <td style={styles.td}>{c.company || '—'}</td>
                  <td style={styles.td}>{c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : '—'}</td>
                  <td style={styles.td}>{c.phone ? <a href={`tel:${c.phone}`}>{c.phone}</a> : '—'}</td>
                  <td style={styles.td}>
                    <button style={{ ...styles.btnSecondary, fontSize: 11, padding: '3px 8px' }} onClick={() => open(c)}>Edit</button>
                    {' '}
                    <button style={{ ...styles.btnDanger, fontSize: 11, padding: '3px 8px' }} onClick={() => remove(c)}>Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setEditing(null)}>
          <div style={{ ...styles.card, width: 420, maxWidth: '94vw' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px' }}>{form.id ? 'Edit Site Contact' : 'Add Site Contact'}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{ fontSize: 12, color: '#374151', gridColumn: '1 / -1' }}>Name *
                <input style={{ ...styles.input, width: '100%', marginTop: 3 }} value={form.name || ''} onChange={set('name')} />
              </label>
              <label style={{ fontSize: 12, color: '#374151' }}>Title
                <input style={{ ...styles.input, width: '100%', marginTop: 3 }} value={form.title || ''} onChange={set('title')} />
              </label>
              <label style={{ fontSize: 12, color: '#374151' }}>Company
                <input style={{ ...styles.input, width: '100%', marginTop: 3 }} value={form.company || ''} onChange={set('company')} />
              </label>
              <label style={{ fontSize: 12, color: '#374151' }}>Email
                <input style={{ ...styles.input, width: '100%', marginTop: 3 }} value={form.email || ''} onChange={set('email')} />
              </label>
              <label style={{ fontSize: 12, color: '#374151' }}>Phone
                <input style={{ ...styles.input, width: '100%', marginTop: 3 }} value={form.phone || ''} onChange={set('phone')} />
              </label>
              <label style={{ fontSize: 12, color: '#374151', gridColumn: '1 / -1' }}>Notes
                <input style={{ ...styles.input, width: '100%', marginTop: 3 }} value={form.notes || ''} onChange={set('notes')} />
              </label>
            </div>
            {err && <p style={{ color: '#B91C1C', fontSize: 12, margin: '8px 0 0' }}>{err}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button style={styles.btnSecondary} onClick={() => setEditing(null)} disabled={saving}>Cancel</button>
              <button style={styles.btn} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Contact'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Layoff / separation modal (2026-07-17 — replaces the standalone Payroll
// app's layoff form). Stamps the allocation server-side and emails the
// separation notice PDF to payroll (CC the sender). The row is kept with a
// LAID OFF badge — crew history stays visible on the job.
function SeparationModal({ alloc, onDone, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ last_day_worked: today, layoff_date: today, rehireable: 'Yes', reason: '', notes: '', cc: '' });
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.last_day_worked) { setErr('Last day worked is required.'); return; }
    setErr('');
    setSending(true);
    try {
      const r = await api.separateAllocation(alloc.id, { ...form, rehireable: form.rehireable === 'Yes' });
      alert(`Separation notice for ${alloc.employee_name} sent to ${r.sent_to}${r.cc ? ` (cc ${r.cc})` : ''}.`);
      onDone();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onCancel}>
      <div style={{ ...styles.card, width: 440, maxWidth: '94vw' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 4px' }}>Layoff — {alloc.employee_name}</h3>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
          {alloc.craft}{alloc.shift ? ` · ${alloc.shift}` : ''}{alloc.job_number ? ` · Job ${alloc.job_number}` : ''} — a separation notice PDF goes to payroll, CC you.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label style={{ fontSize: 12, color: '#374151' }}>Last day worked *
            <input type="date" style={{ ...styles.input, width: '100%', marginTop: 3 }} value={form.last_day_worked} onChange={e => set('last_day_worked', e.target.value)} />
          </label>
          <label style={{ fontSize: 12, color: '#374151' }}>Layoff / separation date
            <input type="date" style={{ ...styles.input, width: '100%', marginTop: 3 }} value={form.layoff_date} onChange={e => set('layoff_date', e.target.value)} />
          </label>
          <label style={{ fontSize: 12, color: '#374151' }}>Eligible for rehire
            <select style={{ ...styles.input, width: '100%', marginTop: 3 }} value={form.rehireable} onChange={e => set('rehireable', e.target.value)}>
              <option>Yes</option><option>No</option>
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#374151' }}>Reason
            <select style={{ ...styles.input, width: '100%', marginTop: 3 }} value={form.reason} onChange={e => set('reason', e.target.value)}>
              <option value="">—</option>
              <option>Job complete / reduction of force</option>
              <option>End of turnaround</option>
              <option>Voluntary quit</option>
              <option>No call / no show</option>
              <option>Terminated for cause</option>
              <option>Other (see notes)</option>
            </select>
          </label>
        </div>
        <label style={{ display: 'block', fontSize: 12, color: '#374151', marginTop: 10 }}>Notes for payroll (optional)
          <textarea style={{ ...styles.input, width: '100%', minHeight: 44, marginTop: 3 }} value={form.notes} onChange={e => set('notes', e.target.value)} />
        </label>
        <label style={{ display: 'block', fontSize: 12, color: '#374151', marginTop: 10 }}>Also CC (optional — George is always CC'd)
          <input style={{ ...styles.input, width: '100%', marginTop: 3 }} value={form.cc} onChange={e => set('cc', e.target.value)} placeholder="you@selectsafetyusa.com" />
        </label>
        {err && <p style={{ color: '#B91C1C', fontSize: 12, margin: '8px 0 0' }}>{err}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button style={styles.btnSecondary} onClick={onCancel} disabled={sending}>Cancel</button>
          <button style={styles.btnSecondary} disabled={sending}
            title="Opens the exact notice PDF payroll would receive — nothing is sent or stamped"
            onClick={async () => {
              if (!form.last_day_worked) { setErr('Last day worked is required.'); return; }
              setErr('');
              try {
                const res = await api.previewSeparationNotice(alloc.id, { ...form, rehireable: form.rehireable === 'Yes' });
                if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Preview failed'); }
                const url = window.URL.createObjectURL(await res.blob());
                window.open(url, '_blank');
                setTimeout(() => window.URL.revokeObjectURL(url), 60000);
              } catch (e) { setErr(e.message); }
            }}>
            👁 Preview PDF
          </button>
          <button style={{ ...styles.btnDanger }} onClick={submit} disabled={sending}>
            {sending ? 'Sending…' : '✉ Send Separation Notice'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Read-only inline card showing the sales-side fields of the drilled-in
// project. Renders above the Staffing Plan so Ops always sees the AM, $,
// equipment, and notes context while assigning crew. Source endpoint:
// GET /api/operations/projects/:id (operations-scoped to bypass denyOps).
function SalesDetailCard({ project, loading }) {
  if (loading && !project) {
    return (
      <div style={{ ...styles.card, padding: 16 }}>
        <div style={{ fontSize: 13, color: '#6B7280' }}>Loading sales detail…</div>
      </div>
    );
  }
  if (!project) return null;
  const p = project;
  const yn = (b) => b ? 'Yes' : 'No';
  const money = (n) => (n == null || n === '') ? '—' : fmtMoney(n);
  const text = (s) => (s == null || s === '') ? '—' : s;
  const date = (d) => d ? fmtDate(d) : '—';
  return (
    <div style={{ ...styles.card, borderLeft: '4px solid #3B82F6', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
          📋 Sales Entry
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
            · read-only Project Tracker fields · edit on Sales → Projects
          </span>
        </h3>
        <StatusPill status={p.status} />
      </div>

      {/* Top row: the facts Ops needs at a glance while staffing -- AM, $,
          dates, branch. Wraps cleanly on narrow screens. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: 12,
        padding: '10px 12px',
        background: '#F9FAFB',
        borderRadius: 6,
        marginBottom: 10,
      }}>
        <DetailFact label="Account Manager" value={text(p.account_owner_name)} />
        <DetailFact label="Branch" value={text(p.branch_name)} />
        <DetailFact label="Planned Date" value={date(p.date_planned)} />
        <DetailFact label="Duration" value={p.duration_days ? `${p.duration_days} days` : '—'} />
        <DetailFact label="Estimated $" value={money(p.estimated_value)} highlight />
        <DetailFact label="Actual $" value={money(p.actual_value)} />
        <DetailFact label="PO #" value={text(p.po_number)} mono />
        <DetailFact label="PO Amount" value={money(p.po_amount)} />
        <DetailFact label="L&L / MSA" value={`L&L: ${yn(p.lnl)} · MSA: ${yn(p.msa)}`} />
        <DetailFact label="Win %" value={p.win_pct != null ? `${p.win_pct}%` : '—'} />
      </div>

      {/* Equipment + Notes side by side on wide screens, stacked on narrow. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Equipment</div>
          <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', background: '#F9FAFB', padding: 8, borderRadius: 4, color: p.equipment_type ? '#111827' : '#9CA3AF', minHeight: 36 }}>
            {text(p.equipment_type)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Notes</div>
          <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', background: '#F9FAFB', padding: 8, borderRadius: 4, color: p.notes ? '#111827' : '#9CA3AF', minHeight: 36 }}>
            {text(p.notes)}
          </div>
        </div>
      </div>

      {/* Actuals breakdown only when at least one is set -- keeps the card
          tight for jobs that haven't billed yet. */}
      {(p.actual_rentals != null || p.actual_labor != null || p.actual_consumables != null) && (
        <div style={{ marginTop: 10, display: 'flex', gap: 16, fontSize: 12, color: '#6B7280' }}>
          <span>Rentals: <strong style={{ color: '#111827' }}>{money(p.actual_rentals)}</strong></span>
          <span>Labor: <strong style={{ color: '#111827' }}>{money(p.actual_labor)}</strong></span>
          <span>Consumables: <strong style={{ color: '#111827' }}>{money(p.actual_consumables)}</strong></span>
        </div>
      )}
    </div>
  );
}

function DetailFact({ label, value, mono, highlight }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{label}</div>
      <div style={{
        fontSize: 13,
        fontFamily: mono ? 'monospace' : 'inherit',
        color: highlight ? '#E36F2C' : '#111827',
        fontWeight: highlight ? 700 : 500,
      }}>{value}</div>
    </div>
  );
}

function AllocationForm({ allocation, onChange, employees, crafts, projects, onSave, onSaveAndAddAnother, onCancel, requirements = [], allocations = [], lockIdentity = false }) {
  const set = (k, v) => onChange({ ...allocation, [k]: v });
  const [, setSearchParams] = useSearchParams();
  // When the user picks an employee, snapshot their default pay rates onto
  // the allocation so the historical record is preserved (later changes to
  // the Roster don't retroactively change past job pay). Pay rates are NOT
  // editable on this form -- they're set on the Roster, applied here. To
  // change someone's rate, edit the Roster.
  const setEmployee = (employeeId) => {
    const emp = employees.find(e => String(e.id) === String(employeeId));
    const payST = emp?.pay_st_default ?? '';
    let payOT = emp?.pay_ot_default ?? '';
    // Defensive 1.5× derive if the employee has ST but no OT (handles
    // legacy roster rows that pre-date the auto-derive on the Roster form).
    if (payST && !payOT) {
      const n = parseFloat(payST);
      if (isFinite(n)) payOT = (n * 1.5).toFixed(2);
    }
    onChange({
      ...allocation,
      employee_id: employeeId,
      pay_st: payST,
      pay_ot: payOT,
    });
  };
  const VERIFY_OPTS = ['Pending', 'Complete', 'N/A'];

  // Resolve the picked employee for the pay-rate badge below.
  const pickedEmployee = employees.find(e => String(e.id) === String(allocation.employee_id));
  const empPayST = pickedEmployee ? parseFloat(pickedEmployee.pay_st_default) : NaN;
  const empPayOT = pickedEmployee ? parseFloat(pickedEmployee.pay_ot_default) : NaN;
  const empHasPay = isFinite(empPayST) && empPayST > 0;

  // Open-slot context: how many of this craft are still needed on this job?
  // Used to decide whether to show "Save & Add Another" + slot title hint.
  // Only meaningful for new allocations (not edits) where craft + project
  // are both set, and only when there's a matching staffing requirement.
  const isNew = !allocation.id;
  const matchingReq = (isNew && allocation.project_id && allocation.craft)
    ? requirements.find(r => String(r.project_id) === String(allocation.project_id) && r.craft === allocation.craft)
    : null;
  const filledForCraft = allocations.filter(a =>
    String(a.project_id) === String(allocation.project_id) && a.craft === allocation.craft
  ).length;
  // DB column is `needed`, not `qty`.
  const openSlots = matchingReq ? Math.max(0, (matchingReq.needed || 0) - filledForCraft) : 0;
  const showSaveAndAnother = isNew && onSaveAndAddAnother && openSlots > 1;

  return (
    <div style={styles.card}>
      <h3 style={{ fontSize: 14, fontWeight: 700, marginTop: 0 }}>
        {allocation.id ? 'Edit Allocation' : 'New Allocation'}
        {matchingReq && isNew && (
          <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 400, color: '#6B7280' }}>
            · {allocation.craft}: <strong style={{ color: openSlots > 0 ? '#92400E' : '#065F46' }}>
              {filledForCraft} of {matchingReq.needed} filled
            </strong>{openSlots > 0 ? ` · ${openSlots} still open after this one saves` : ''}
          </span>
        )}
      </h3>

      <h4 style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 1, margin: '8px 0' }}>Assignment</h4>
      {lockIdentity && (
        <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', color: '#78350F', padding: 8, borderRadius: 6, marginBottom: 8, fontSize: 12 }}>
          This assignment comes from the estimate crew — who / craft / shift are managed in the Crew section
          (or Estimating → Personnel) and sync here. Dates, per diem, travel, and verifications below stay editable.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        <Field label="Project / Job *">
          <select style={styles.input} disabled={lockIdentity} value={allocation.project_id || ''} onChange={e => set('project_id', e.target.value)}>
            <option value="">— Pick job —</option>
            {projects.filter(listedInAllocations).map(p => <option key={p.id} value={p.id}>{p.job_number || (isStandby(p.status) ? '(no job # — Standby)' : '(no job #)')} — {p.site}</option>)}
          </select>
        </Field>
        <Field label="Employee *">
          <select style={styles.input} disabled={lockIdentity} value={allocation.employee_id || ''} onChange={e => setEmployee(e.target.value)}>
            <option value="">— Pick employee ({employees.filter(e => e.is_active !== false).length} available) —</option>
            {employees.filter(e => e.is_active !== false).map(emp => <option key={emp.id} value={emp.id}>{emp.full_name}</option>)}
          </select>
        </Field>
        <Field label="Craft *">
          <select style={styles.input} disabled={lockIdentity} value={allocation.craft || ''} onChange={e => set('craft', e.target.value)}>
            <option value="">— Pick craft —</option>
            {crafts.filter(c => c.is_active !== false).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Shift">
          <select style={styles.input} disabled={lockIdentity} value={allocation.shift || ''} onChange={e => set('shift', e.target.value)}>
            <option value="Day">Day</option>
            <option value="Night">Night</option>
            <option value="Swing">Swing</option>
          </select>
        </Field>
        <Field label="Bill ST / hr"><MoneyInput value={allocation.bill_st} onChange={e => set('bill_st', e.target.value)} /></Field>
        <Field label="Bill OT / hr"><MoneyInput value={allocation.bill_ot} onChange={e => set('bill_ot', e.target.value)} /></Field>
        <Field label="Start Date (this employee)"><input type="date" min="2000-01-01" max="2100-12-31" style={styles.input} value={toDateInputValue(allocation.start_date)} onChange={e => set('start_date', e.target.value)} title="When THIS employee starts on the job. Defaults from the job's planned start, but commonly differs -- PMs / supers ramp up early, light crew arrives later, etc." /></Field>
        <Field label="End Date (this employee)"><input type="date" min="2000-01-01" max="2100-12-31" style={styles.input} value={toDateInputValue(allocation.end_date)} onChange={e => set('end_date', e.target.value)} title="When THIS employee finishes on the job. Defaults from job end (planned + duration), but commonly differs -- demob crew stays a few days after, rotations leave early, etc." /></Field>
      </div>

      {/* Hint: per-employee dates are the norm during ramp-up / ramp-down,
          not an exception. Job dates seed the defaults but every employee
          has their own window. */}
      <div style={{
        marginTop: 4, fontSize: 11, color: '#6B7280', fontStyle: 'italic',
      }}>
        Each employee has their own start / end as we ramp up and ramp down. Defaults seed from the job's planned date + duration; adjust per person for mob / demob, rotations, partial coverage.
      </div>

      {/* Employee pay rate status -- populated from Roster, not editable
          here. To change, edit the employee on the Roster tab. */}
      {pickedEmployee && (
        <div style={{
          marginTop: 10,
          padding: '8px 12px',
          borderRadius: 6,
          background: empHasPay ? '#D1FAE5' : '#FEF3C7',
          border: `1px solid ${empHasPay ? '#10B981' : '#F59E0B'}`,
          color: empHasPay ? '#065F46' : '#78350F',
          fontSize: 13,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
        }}>
          {empHasPay ? (
            <span>
              ✓ <strong>Pay rate from Roster:</strong>{' '}
              ${empPayST.toFixed(2)} ST / ${(isFinite(empPayOT) ? empPayOT : empPayST * 1.5).toFixed(2)} OT per hour
              <span style={{ marginLeft: 8, color: '#047857', fontSize: 11 }}>(set on the Roster, snapshotted to this allocation on save)</span>
            </span>
          ) : (
            <span>
              ⚠ <strong>{pickedEmployee.full_name} has no pay rate set on the Roster.</strong>{' '}
              Add it before saving — allocations without a pay rate can't be processed by Paylocity.
            </span>
          )}
          <button
            type="button"
            style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px' }}
            onClick={() => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('tab', 'roster'); return n; })}
          >Go to Roster →</button>
        </div>
      )}

      <h4 style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 1, margin: '14px 0 8px' }}>Per Diem / Travel / Hotel</h4>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        <Field label="PD Type">
          <select style={styles.input} value={allocation.pd_type || 'None'} onChange={e => {
            // Changing PD Type also flips dependent verifications
            // (PD Setup, Hotel) between N/A and Pending so the
            // checklist reflects what actually needs doing.
            const next = e.target.value;
            onChange({
              ...allocation,
              pd_type: next,
              ...computeContextualVerifications(next, allocation.travel_type, allocation),
            });
          }}>
            <option>None</option>
            <option>Full Per Diem</option>
            <option>Partial Per Diem</option>
            <option>Hotel Only</option>
          </select>
        </Field>
        <Field label="PD Amount"><MoneyInput value={allocation.pd_amount} onChange={e => set('pd_amount', e.target.value)} /></Field>
        <Field label="Hotel"><MoneyInput value={allocation.hotel_amount} onChange={e => set('hotel_amount', e.target.value)} /></Field>
        <Field label="Meal"><MoneyInput value={allocation.meal_amount} onChange={e => set('meal_amount', e.target.value)} /></Field>
        <Field label="PD Start Date"><input type="date" min="2000-01-01" max="2100-12-31" style={styles.input} value={toDateInputValue(allocation.pd_start_date)} onChange={e => set('pd_start_date', e.target.value)} /></Field>
        <Field label="Travel Type">
          <select style={styles.input} value={allocation.travel_type || 'Local (No Travel)'} onChange={e => {
            // Same idea -- Travel Type drives Travel / Flt Out / Flt
            // Return verifications between N/A and Pending.
            const next = e.target.value;
            onChange({
              ...allocation,
              travel_type: next,
              ...computeContextualVerifications(allocation.pd_type, next, allocation),
            });
          }}>
            <option>Local (No Travel)</option>
            <option>Driving - Personal</option>
            <option>Driving - Company</option>
            <option>Flying</option>
          </select>
        </Field>
        <Field label="Travel Rate (per mi or hr)"><MoneyInput value={allocation.travel_rate} onChange={e => set('travel_rate', e.target.value)} step="0.0001" /></Field>
        <Field label="Bill Miles"><input type="number" step="0.01" style={styles.input} value={allocation.bill_miles ?? ''} onChange={e => set('bill_miles', e.target.value)} /></Field>
        <Field label="Pay Miles"><input type="number" step="0.01" style={styles.input} value={allocation.pay_miles ?? ''} onChange={e => set('pay_miles', e.target.value)} /></Field>
        <Field label="RT Miles"><input type="number" step="0.01" style={styles.input} value={allocation.rt_miles ?? ''} onChange={e => set('rt_miles', e.target.value)} /></Field>
        <Field label="Hotel In"><input type="date" min="2000-01-01" max="2100-12-31" style={styles.input} value={toDateInputValue(allocation.hotel_in_date)} onChange={e => set('hotel_in_date', e.target.value)} /></Field>
        <Field label="Hotel Out"><input type="date" min="2000-01-01" max="2100-12-31" style={styles.input} value={toDateInputValue(allocation.hotel_out_date)} onChange={e => set('hotel_out_date', e.target.value)} /></Field>
      </div>

      <h4 style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 1, margin: '14px 0 8px' }}>
        Verifications
        <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 400, color: '#9CA3AF', textTransform: 'none', letterSpacing: 0 }}>
          · grey N/A fields auto-set from PD Type / Travel Type
        </span>
      </h4>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
        {VERIFY_KEYS.map(([k, label]) => {
          const val = allocation[k] || 'Pending';
          // Auto-derived N/A fields render greyed so the user sees they
          // don't apply (and that flipping PD/Travel Type can re-enable
          // them). Still editable in case there's a special case.
          const isNA = val === 'N/A';
          return (
            <Field key={k} label={label}>
              <select
                style={{
                  ...styles.input,
                  ...(isNA ? { background: '#F3F4F6', color: '#9CA3AF' } : {}),
                }}
                value={val}
                onChange={e => set(k, e.target.value)}
                title={isNA ? `Auto N/A based on PD Type / Travel Type. Override if a special case applies.` : ''}
              >
                {VERIFY_OPTS.map(o => <option key={o}>{o}</option>)}
              </select>
            </Field>
          );
        })}
      </div>

      <div style={{ marginTop: 12 }}>
        <Field label="Notes"><textarea style={{ ...styles.input, minHeight: 50 }} value={allocation.notes || ''} onChange={e => set('notes', e.target.value)} /></Field>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button style={styles.btnSecondary} onClick={onCancel}>Cancel</button>
        {showSaveAndAnother && (
          <button
            style={styles.btnSecondary}
            onClick={onSaveAndAddAnother}
            title={`Save this allocation, then keep the form open with project / craft / dates / per diem pre-filled so you can add the next of ${matchingReq.needed} ${allocation.craft}s.`}
          >Create &amp; Add Another</button>
        )}
        <button style={styles.btn} onClick={onSave}>{allocation.id ? 'Save' : 'Create'}</button>
      </div>
    </div>
  );
}

// ===== Workforce import modal =============================================

// StaffingSection -- top of the job drill-in. Shows the staffing PLAN
// (per-craft requirements) alongside the actual ASSIGNMENTS, so admin
// sees "Project Manager: 1 of 2 filled · 1 OPEN" with a quick-assign
// button on each open slot. Crafts not in the requirements list but
// already assigned (e.g. someone added off-plan) appear in a dimmer
// "extras" group below the planned crafts.
// readOnly (2026-07-30): when the job has a current estimate the PLAN is
// seeded/overwritten by the estimate syncs and crew is filled in the Crew
// editor below — plan mutate controls hide and open slots route there via
// onGoToCrew. Off-plan rows keep "+ Add another" (the beyond-plan path).
// Each name is a button (George 2026-09-30: "I need to be able to edit or
// change the employee here"): it opens an inline swap. onSwapEmployee picks
// the right write path (estimate seat -> Crew editor save; anything else ->
// the assignment itself).
function StaffingSection({ projectId, allocations, requirements, crafts, addingReq, setAddingReq, onSaveReq, onRemoveReq, onAssignToCraft, readOnly = false, onGoToCrew, employees = [], onSwapEmployee }) {
  const [swap, setSwap] = useState(null);   // { alloc, value, busy }
  const doSwap = async () => {
    if (!swap || !swap.value.trim() || !onSwapEmployee) return;
    setSwap(s => ({ ...s, busy: true }));
    const ok = await onSwapEmployee(swap.alloc, swap.value.trim());
    if (ok) setSwap(null); else setSwap(s => (s ? { ...s, busy: false } : s));
  };
  // Map craft name -> { needed, requirementId, filled, names }
  const byCraft = new Map();
  // Seed from requirements (so a craft with 0 filled still shows).
  for (const r of requirements) {
    byCraft.set(r.craft, { craft: r.craft, needed: r.needed, requirementId: r.id, filled: 0, names: [] });
  }
  // Add filled counts from allocations (also pick up off-plan crafts).
  for (const a of allocations) {
    if (!byCraft.has(a.craft)) {
      byCraft.set(a.craft, { craft: a.craft, needed: 0, requirementId: null, filled: 0, names: [] });
    }
    const slot = byCraft.get(a.craft);
    slot.filled++;
    slot.names.push(a.employee_name);
    (slot.people = slot.people || []).push(a);
  }
  // Sort: planned crafts first (by craft name), then off-plan extras.
  const rows = Array.from(byCraft.values()).sort((a, b) => {
    if (!!a.requirementId !== !!b.requirementId) return a.requirementId ? -1 : 1;
    return a.craft.localeCompare(b.craft);
  });

  // Planned seats filled count only planned crafts, capped at what each needs;
  // everyone else is off-plan ("7 of 4 filled" read as a bug on Placid).
  const totalNeeded = rows.reduce((s, r) => s + r.needed, 0);
  const totalFilled = rows.reduce((s, r) => s + (r.requirementId ? Math.min(r.filled, r.needed) : 0), 0);
  const offPlan = rows.reduce((s, r) => s + (r.requirementId ? Math.max(0, r.filled - r.needed) : r.filled), 0);

  return (
    <div style={{ ...styles.card, borderLeft: '4px solid #E36F2C' }}>
      <datalist id="staffing-swap-roster">
        {employees.filter(e => e.is_active !== false && e.full_name).map(e => <option key={e.id} value={e.full_name} />)}
      </datalist>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>
          📋 Staffing Plan
          <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
            {totalNeeded > 0
              ? `· ${totalFilled} of ${totalNeeded} planned slots filled${offPlan ? ` · +${offPlan} off-plan` : ''}`
              : '· No staffing plan defined yet — add craft requirements below'}
          </span>
        </h3>
        {!addingReq && !readOnly && (
          <button
            type="button"
            style={{ ...styles.btnSecondary, fontSize: 12 }}
            onClick={() => setAddingReq({ project_id: projectId, craft: '', needed: 1 })}
          >+ Add Craft Requirement</button>
        )}
        {readOnly && (
          <span style={{ fontSize: 11, color: '#6B7280' }}>
            plan comes from the estimate — fill open slots in the Crew section below
          </span>
        )}
      </div>

      {/* Add-requirement inline form */}
      {addingReq && (
        <div style={{ marginTop: 12, padding: 10, background: '#F9FAFB', borderRadius: 6, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={styles.label}>Craft Needed</label>
            <select style={styles.input} value={addingReq.craft} onChange={e => setAddingReq({ ...addingReq, craft: e.target.value })}>
              <option value="">— Pick craft —</option>
              {crafts.filter(c => c.is_active !== false).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div style={{ width: 100 }}>
            <label style={styles.label}>How Many</label>
            <input type="number" min="1" style={styles.input} value={addingReq.needed} onChange={e => setAddingReq({ ...addingReq, needed: e.target.value })} />
          </div>
          <button style={styles.btn} disabled={!addingReq.craft} onClick={() => onSaveReq(addingReq)}>Save</button>
          <button style={styles.btnSecondary} onClick={() => setAddingReq(null)}>Cancel</button>
        </div>
      )}

      {/* Per-craft summary rows */}
      {rows.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map(slot => {
            const isPlanned = !!slot.requirementId;
            const needed = slot.needed;
            const filled = slot.filled;
            const open = Math.max(0, needed - filled);
            const overFilled = filled > needed && needed > 0;
            // Color logic: green = filled, amber = partial, red = empty,
            // muted = off-plan extras (no requirement, just allocations exist)
            const color = !isPlanned ? '#6B7280'
                        : open === 0 ? '#065F46'
                        : filled === 0 ? '#991B1B'
                        : '#92400E';
            const bg = !isPlanned ? '#F3F4F6'
                     : open === 0 ? '#D1FAE5'
                     : filled === 0 ? '#FEE2E2'
                     : '#FEF3C7';
            return (
              <div key={slot.craft} style={{
                display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 12,
                alignItems: 'center', padding: '8px 12px', background: bg, borderRadius: 6
              }}>
                <div>
                  <div style={{ fontWeight: 700, color, fontSize: 13 }}>{slot.craft}{!isPlanned && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 400 }}>(off-plan)</span>}</div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                    {filled === 0
                      ? <em>No one assigned yet</em>
                      : (slot.people || []).map((a, i) => (
                        <button key={a.id || i} type="button"
                          onClick={() => setSwap({ alloc: a, value: '', busy: false })}
                          title={a.separated_at ? 'Separated — kept as history' : `Change who fills this ${a.origin === 'estimate' && readOnly ? 'estimate seat' : 'assignment'}`}
                          disabled={!!a.separated_at || !onSwapEmployee}
                          style={{ background: 'transparent', border: '1px dashed #9CA3AF', borderRadius: 4, padding: '1px 6px',
                            fontSize: 11, color: '#374151', cursor: a.separated_at ? 'default' : 'pointer' }}>
                          {a.employee_name} ✎
                        </button>
                      ))}
                  </div>
                  {swap && (slot.people || []).includes(swap.alloc) && (
                    <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, color: '#374151' }}>Replace <strong>{swap.alloc.employee_name}</strong> with</span>
                      <input list="staffing-swap-roster" autoFocus style={{ ...styles.input, width: 220, padding: '4px 8px', fontSize: 12 }}
                        placeholder="Type a roster name" value={swap.value}
                        onChange={e => setSwap(s => ({ ...s, value: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') doSwap(); if (e.key === 'Escape') setSwap(null); }} />
                      <button type="button" style={{ ...styles.btn, fontSize: 11, padding: '4px 10px' }}
                        disabled={swap.busy || !swap.value.trim()} onClick={doSwap}>{swap.busy ? 'Saving…' : 'Save'}</button>
                      <button type="button" style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px' }}
                        disabled={swap.busy} onClick={() => setSwap(null)}>Cancel</button>
                    </div>
                  )}
                </div>
                <div style={{ fontWeight: 700, color, fontSize: 13, whiteSpace: 'nowrap' }}>
                  {isPlanned ? `${filled} of ${needed}` : `${filled} assigned`}
                  {overFilled && <span style={{ marginLeft: 4, fontSize: 10, color: '#92400E' }}>+{filled - needed} extra</span>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {isPlanned && open > 0 && (readOnly ? (
                    <button
                      type="button"
                      style={{ ...styles.btn, fontSize: 11, padding: '4px 10px' }}
                      title="Open slots are filled by name in the Crew section below — this scrolls you there"
                      onClick={onGoToCrew}
                    >✎ Fill in Crew · {open} open</button>
                  ) : (
                    <button
                      type="button"
                      style={{ ...styles.btn, fontSize: 11, padding: '4px 10px' }}
                      onClick={() => onAssignToCraft(slot.craft)}
                    >+ Assign · {open} open</button>
                  ))}
                  {isPlanned && open === 0 && !readOnly && (
                    <button
                      type="button"
                      style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px' }}
                      onClick={() => onAssignToCraft(slot.craft)}
                    >+ Add another</button>
                  )}
                  {!isPlanned && (
                    <button
                      type="button"
                      style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px' }}
                      onClick={() => onAssignToCraft(slot.craft)}
                      title={readOnly ? 'Adds a beyond-plan assignment (outside the estimate crew)' : undefined}
                    >+ Add another</button>
                  )}
                </div>
                <div>
                  {/* readOnly hides plan edits on estimate-backed jobs, but the
                      estimate seeder only ever INSERTs/UPDATEs — a craft an
                      estimate revision dropped could otherwise be retired by
                      nobody and kept firing a false shortfall in the readiness
                      digest. An UNSTAFFED row (filled === 0) stays removable
                      even under readOnly: with no one assigned to it, deleting
                      it can't orphan crew. */}
                  {isPlanned && (!readOnly || filled === 0) && (
                    <button
                      type="button"
                      style={{ ...styles.btnDanger, fontSize: 10, padding: '4px 8px' }}
                      onClick={() => onRemoveReq(slot.requirementId, slot.craft)}
                      title={readOnly
                        ? 'No one is assigned to this craft — remove the leftover requirement (e.g. dropped by an estimate revision). Re-syncing the estimate will bring it back if the plan still calls for it.'
                        : 'Delete this craft requirement from the staffing plan. Already-assigned employees on this craft are NOT removed.'}
                    >🗑 Remove Slot</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Ad-hoc job creation modal -- minimum-fields form for jobs that didn't
// originate in Sales. Required: Job # + Site. Everything else is optional
// with sensible defaults set by the parent before opening (status Active,
// today's date, etc.). Posts to /api/operations/projects.
// Picker section header + body. Each time-based block (Active / Past Due
// / Starting Soon / Upcoming) wraps in one of these so the picker reads
// as urgency-grouped sections rather than a single long table.
function PickerSection({ title, count, accent, hint, emptyMsg, children }) {
  const isEmpty = !children;
  return (
    <div style={{
      marginBottom: 16,
      borderLeft: `4px solid ${accent}`,
      paddingLeft: 12,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8,
        marginBottom: hint ? 4 : 8,
      }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>{title}</h3>
        <span style={{ color: '#6B7280', fontSize: 12 }}>· {count}</span>
      </div>
      {hint && (
        <div style={{ fontSize: 11, color: '#6B7280', fontStyle: 'italic', marginBottom: 8 }}>
          {hint}
        </div>
      )}
      {isEmpty ? (
        <div style={{ padding: '12px 0', color: '#9CA3AF', fontSize: 12, fontStyle: 'italic' }}>
          {emptyMsg}
        </div>
      ) : children}
    </div>
  );
}

// Shared table render for both archived and time-grouped views. Receives
// the row set + the handlers it needs as a single ctx object so the call
// sites stay readable.
function renderJobsTable(rows, ctx) {
  const {
    showArchived, closingJobId, setSelectedJobId,
    updateJobPlannedDate, updateJobDuration,
    requiredByProject, requirementsByProject,
    closeJob, reopenJob, quickAdd,
  } = ctx;
  return (
    <table style={{ ...styles.table, fontSize: 13 }}>
      <thead><tr>
        <th style={styles.th}>Job #</th>
        <th style={styles.th}>Site / Client</th>
        <th style={styles.th}>Status</th>
        <th style={styles.th} title="Job's planned start date">Planned Start</th>
        <th style={{ ...styles.th, textAlign: 'center' }} title="Days the job runs from planned start">Duration</th>
        <th style={{ ...styles.th, textAlign: 'center' }}>Days</th>
        <th style={styles.th}>Staffing</th>
        <th style={styles.th} title="Earliest employee start → latest employee end (the actual ramp-up to ramp-down footprint)">Crew Window</th>
        <th style={{ ...styles.th, textAlign: 'right' }}>Pending Verif.</th>
        <th style={styles.th}></th>
      </tr></thead>
      <tbody>
        {rows.map(j => (
          <tr key={j.project_id}
              onClick={() => setSelectedJobId(j.project_id)}
              style={{ cursor: 'pointer' }}>
            <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#E36F2C' }}>
              {j.job_number || '(no job #)'}
            </td>
            <td style={styles.td}>{j.site || '—'}</td>
            <td style={styles.td}><StatusPill status={j.status} /></td>
            <td style={styles.td} onClick={e => e.stopPropagation()}>
              <PlannedDateCell
                value={j.date_planned}
                disabled={closingJobId === j.project_id}
                onSave={(v) => updateJobPlannedDate(j.project_id, v)}
              />
            </td>
            <td style={{ ...styles.td, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
              <DurationCell
                value={j.duration_days}
                disabled={closingJobId === j.project_id}
                onSave={(v) => updateJobDuration(j.project_id, v)}
              />
            </td>
            <td style={{ ...styles.td, textAlign: 'center' }}>
              <DaysUntilChip status={j.status} datePlanned={j.date_planned} />
            </td>
            <td style={styles.td}>
              <StaffingChip
                filled={j.count}
                required={requiredByProject.get(j.project_id) || 0}
                craftCounts={j.craftCounts}
                craftRequirements={requirementsByProject.get(j.project_id)}
              />
            </td>
            <td style={{ ...styles.td, fontSize: 12 }}>
              <CrewWindowCell
                crewStart={j.minStart}
                crewEnd={j.maxEnd}
                jobStart={j.date_planned}
                jobDuration={j.duration_days}
              />
            </td>
            <td style={{ ...styles.td, textAlign: 'right', fontSize: 12 }}>
              {j.pendingVerifications > 0
                ? <span style={{ color: '#92400E', fontWeight: 600 }}>{j.pendingVerifications}</span>
                : <span style={{ color: '#065F46', fontWeight: 600 }}>✓ none</span>}
            </td>
            <td style={{ ...styles.td, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
              {!showArchived && quickAdd && (
                <button
                  style={{ ...styles.btn, fontSize: 11, padding: '4px 10px', marginRight: 4 }}
                  onClick={() => quickAdd(j)}
                  title="Add a person to this job on one screen — no drill-in needed"
                >+ Crew</button>
              )}
              <button
                style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px' }}
                onClick={() => setSelectedJobId(j.project_id)}
              >Open →</button>
              {isStandby(j.status) ? null : !showArchived ? (
                <button
                  style={{ ...styles.btnDanger, fontSize: 11, padding: '4px 10px', marginLeft: 4 }}
                  disabled={closingJobId === j.project_id}
                  onClick={() => closeJob(j.project_id, j.job_number)}
                  title="Mark this job Complete and move it to the archive"
                >
                  {closingJobId === j.project_id ? '…' : '✓ Close Job'}
                </button>
              ) : (
                <button
                  style={{ ...styles.btnSecondary, fontSize: 11, padding: '4px 10px', marginLeft: 4, color: '#065F46', borderColor: '#10B981' }}
                  disabled={closingJobId === j.project_id}
                  onClick={() => reopenJob(j.project_id, j.job_number)}
                  title="Bring this job back out of the archive (marks Active and moves it to the action-items list)"
                >
                  {closingJobId === j.project_id ? '…' : '↻ Reopen'}
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function NewJobModal({ job, branches, onChange, onSave, onCancel }) {
  const set = (k, v) => onChange({ ...job, [k]: v });
  const valid = job.job_number.trim() && job.site.trim();
  return (
    <div style={modalBackdrop} onClick={onCancel}>
      <div style={{ ...modalCard, maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>＋ New Ad-Hoc Job</h2>
          <button style={styles.btnSecondary} onClick={onCancel}>✕</button>
        </div>
        <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', color: '#075985', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 12 }}>
          Use this for jobs that didn't flow through Sales — emergency response, walk-up customers, last-minute requests. Job # and Site are required; the rest can be filled in later.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Job # *">
            <input
              style={styles.input}
              value={job.job_number}
              maxLength={40}
              placeholder="e.g. 901-26-EMRG-VAL"
              onChange={e => set('job_number', e.target.value)}
              autoFocus
            />
          </Field>
          <Field label="Status">
            <select style={styles.input} value={job.status} onChange={e => set('status', e.target.value)}>
              <option value="Active">Active (work happening now)</option>
              <option value="Won">Won (booked, not started)</option>
            </select>
          </Field>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Site / Customer *">
              <input
                style={styles.input}
                value={job.site}
                placeholder="e.g. Valero Three Rivers — Emergency Response"
                onChange={e => set('site', e.target.value)}
              />
            </Field>
          </div>
          <Field label="Branch">
            <select style={styles.input} value={job.branch_id || ''} onChange={e => set('branch_id', e.target.value)}>
              <option value="">— None —</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          <Field label="Planned Start Date">
            <input type="date" min="2000-01-01" max="2100-12-31" style={styles.input} value={job.date_planned || ''} onChange={e => set('date_planned', e.target.value)} />
          </Field>
          <Field label="Duration (days)">
            <input
              type="number"
              min="1"
              style={styles.input}
              value={job.duration_days || ''}
              placeholder="e.g. 14"
              onChange={e => set('duration_days', e.target.value)}
            />
          </Field>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Equipment / Description">
              <textarea
                style={{ ...styles.input, minHeight: 50 }}
                value={job.equipment_type || ''}
                placeholder="What's needed on site -- e.g. CSVM gas detection, breathing air, rescue team..."
                onChange={e => set('equipment_type', e.target.value)}
              />
            </Field>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Notes">
              <textarea
                style={{ ...styles.input, minHeight: 50 }}
                value={job.notes || ''}
                placeholder="Anything Ops should know -- access notes, customer contact, special requirements..."
                onChange={e => set('notes', e.target.value)}
              />
            </Field>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button style={styles.btnSecondary} onClick={onCancel}>Cancel</button>
          <button
            style={{ ...styles.btn, opacity: valid ? 1 : 0.5, cursor: valid ? 'pointer' : 'not-allowed' }}
            disabled={!valid}
            onClick={onSave}
          >Create Job</button>
        </div>
      </div>
    </div>
  );
}

function WorkforceImportModal({ onClose, onCommit }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleUpload = async () => {
    if (!file) return;
    setBusy(true); setError('');
    try { setPreview(await api.uploadWorkforceSpreadsheet(file)); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const handleCommit = async () => {
    if (!preview) return;
    if (!confirm(`Commit will upsert ${preview.summary.employees_count} employees, ${preview.summary.jobs_count} jobs, and ${preview.summary.allocations_count} allocations. Existing rows are matched by name/job# and updated. Continue?`)) return;
    setBusy(true); setError('');
    try {
      const r = await api.commitWorkforceImport({
        employees: preview.employees,
        crafts: preview.crafts,
        jobs: preview.jobs,
        allocations: preview.allocations
      });
      alert(`Committed.\nEmployees: ${r.employees}\nJobs: ${r.jobs}\nCrafts: ${r.crafts}\nAllocations: ${r.allocations} (skipped ${r.allocations_skipped})`);
      onCommit();
    } catch (err) { setError(err.message); setBusy(false); }
  };

  return (
    <div style={modalBackdrop} onClick={onClose}>
      <div style={{ ...modalCard, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ ...styles.h2, margin: 0 }}>⬆ Import Project Startup Spreadsheet</h2>
          <button style={modalClose} onClick={onClose}>✕</button>
        </div>
        {error && <div style={{ ...styles.errorBox, marginBottom: 12 }}>{error}</div>}
        {!preview && (
          <>
            <p style={{ fontSize: 13, color: '#6B7280' }}>
              Upload your <code>SSS_Project_Startup_Master_Final.xlsm</code> (or .xlsx). Parses LISTS + INPUT sheets and upserts employees, crafts, jobs, and allocations. Existing rows matched by name + job# get updated; new rows get created.
            </p>
            <p style={{ fontSize: 12, color: '#92400E', background: '#FEF3C7', border: '1px solid #FBBF24', padding: 10, borderRadius: 6 }}>
              ⚠️ This is a one-time migration tool. Going forward, use the in-CRM forms for day-to-day updates so the CRM stays the source of truth.
            </p>
            <DropZone block onFiles={files => setFile(files[0])} disabled={busy}
              title="Drop the startup master workbook here" style={{ marginBottom: 12, padding: 6 }}>
              <input type="file" accept=".xlsm,.xlsx,.xls" onChange={e => setFile(e.target.files[0])} />
              {file && <span style={{ marginLeft: 10, fontSize: 12, color: '#374151' }}>📎 {file.name}</span>}
            </DropZone>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={styles.btnSecondary} onClick={onClose}>Cancel</button>
              <button style={styles.btn} onClick={handleUpload} disabled={!file || busy}>{busy ? 'Parsing…' : 'Upload + Preview'}</button>
            </div>
          </>
        )}
        {preview && (
          <>
            <div style={{ background: '#D1FAE5', color: '#065F46', padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
              ✅ Parsed:
              <div style={{ marginTop: 6 }}>
                <strong>{preview.summary.employees_count}</strong> employees ·
                {' '}<strong>{preview.summary.crafts_count}</strong> crafts ·
                {' '}<strong>{preview.summary.jobs_count}</strong> jobs ·
                {' '}<strong>{preview.summary.allocations_count}</strong> allocations
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 12, borderTop: '1px solid #E5E7EB' }}>
              <button style={styles.btnSecondary} onClick={onClose} disabled={busy}>Cancel</button>
              <button style={styles.btn} onClick={handleCommit} disabled={busy}>{busy ? 'Committing…' : '✓ Commit'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ===== Shared styles =====================================================

const Field = ({ label, children }) => (
  <div>
    <label style={styles.label}>{label}</label>
    {children}
  </div>
);

// Click-to-toggle chip for boolean / two-state cells in tables (e.g. an
// employee's Paylocity status or active flag). Reads as a colored pill but
// is actually a button -- one click flips the value via the parent's
// onClick (which then PUTs to the server). Greyed out + "…" while saving.
const ToggleChip = ({ active, activeLabel, inactiveLabel, busy, onClick, title }) => (
  <button
    type="button"
    disabled={busy}
    onClick={onClick}
    title={title}
    style={{
      background: active ? '#D1FAE5' : '#F3F4F6',
      color: active ? '#065F46' : '#6B7280',
      border: `1px solid ${active ? '#10B981' : '#D1D5DB'}`,
      padding: '3px 10px',
      borderRadius: 12,
      fontSize: 11,
      fontWeight: 600,
      cursor: busy ? 'wait' : 'pointer',
      opacity: busy ? 0.5 : 1,
      whiteSpace: 'nowrap',
    }}
  >{busy ? '…' : (active ? activeLabel : inactiveLabel)}</button>
);

// Money input: text input with a $ prefix glyph and consistent decimal
// formatting. Renders the value formatted to a fixed number of decimals
// when blurred ($22.00, $34.90, $0.7250), and shows the raw value while
// focused so the user can type freely without toFixed re-formatting on
// every keystroke. Decimals derived from step: "0.01" -> 2, "0.0001" -> 4.
// onBlur snaps the parent value to the formatted version so what you see
// in the field is what gets saved to the DB.
const MoneyInput = ({ value, onChange, step = '0.01', placeholder }) => {
  const decimals = (step.split('.')[1] || '').length;
  const [focused, setFocused] = useState(false);
  const num = parseFloat(value);
  const hasValue = value !== '' && value != null && isFinite(num);
  const displayValue = focused
    ? (value ?? '')
    : (hasValue ? num.toFixed(decimals) : '');

  const handleChange = (e) => {
    // Strip anything but digits + a single decimal point so the user
    // can't accidentally paste a $ sign or commas into a number field.
    const cleaned = e.target.value.replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    const filtered = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : cleaned;
    onChange({ target: { value: filtered } });
  };

  const handleBlur = () => {
    setFocused(false);
    if (hasValue) {
      const formatted = num.toFixed(decimals);
      if (formatted !== String(value)) onChange({ target: { value: formatted } });
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <span style={{
        position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
        color: '#6B7280', fontWeight: 600, pointerEvents: 'none', fontSize: 13,
      }}>$</span>
      <input
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        placeholder={placeholder}
        style={{ ...styles.input, paddingLeft: 22, textAlign: 'right' }}
      />
    </div>
  );
};

const modalBackdrop = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 20, zIndex: 100
};
const modalCard = {
  background: '#fff', borderRadius: 10, padding: 24, width: '100%',
  maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
};
const modalClose = {
  background: 'transparent', border: 'none', fontSize: 24,
  cursor: 'pointer', color: '#6B7280', lineHeight: 1
};
